import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth, useSipPhone, getAuthenticatedMediaUrl } from "@/App";
import { DismissableLayerBranch } from "@radix-ui/react-dismissable-layer";
import { toast } from "sonner";
import jsPDF from "jspdf";
import PhoneLookupPopup from "@/components/PhoneLookupPopup";

// Get base URL for media files (without /api suffix)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft,
  Save,
  Clock,
  User,
  Users,
  UserCircle,
  Shield,
  Camera,
  AlertTriangle,
  Phone,
  Plus,
  Trash2,
  MapPin,
  Link as LinkIcon,
  ExternalLink,
  X,
  Calendar,
  Search,
  Radio,
  Upload,
  Download,
  Image as ImageIcon,
  Film,
  Edit,
  FileText,
  Check,
  Undo2,
  Eye,
  Loader2,
  MessageSquare,
  Link2,
  Play,
  Pause,
  EyeOff,
  Pencil,
  History,
  ChevronDown,
  ChevronUp,
  Copy,
  ChevronLeft,
  ChevronRight,
  FileSpreadsheet,
  Headphones,
  RefreshCw,
  Flag,
  PhoneOutgoing,
  PhoneIncoming
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { openDrivePicker, isDrivePickerConfigured } from "@/utils/drivePicker";

const STATUS_OPTIONS = ["live_call", "log", "report", "investigating", "arrest", "closed"];
const PRIORITY_OPTIONS = ["low", "medium", "high", "critical"];
const APPEARANCE_OPTIONS = ["Alert", "Normal", "Droopy"];
const IDENTIFICATION_BY_OPTIONS = ["CV", "Witness", "Security Footage - CV", "Security Footage - Units"];
const IDENTIFICATION_METHODS = ["CV", "Witness", "Security Footage - CV", "Security Footage - Units"];

// Helper to get proper media URL
const getMediaUrl = (url) => {
  if (!url) return null;
  // If it's base64 data, return as-is
  if (url.startsWith('data:')) return url;
  // If already absolute, return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  // Otherwise, prepend API base
  const baseUrl = API.replace('/api', '');
  if (url.startsWith('/')) return `${baseUrl}${url}`;
  return `${baseUrl}/uploads/${url}`;
};

// Format phone number helper (requirement #6 - display as 718-111-1111)
// Format phone number helper - handles international format like +1-718-111-1111
// Copy a phone number to the clipboard. Strips formatting first so a paste
// into a dialer or another field gets a clean digits-only value. Falls back
// to a hidden <textarea> + execCommand on browsers that don't grant
// navigator.clipboard (older iOS Safari, non-https in dev, etc.).
const copyPhoneNumber = async (raw) => {
  const cleaned = String(raw || "").replace(/\D/g, "");
  if (!cleaned) return false;
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(cleaned);
      return true;
    }
  } catch {}
  try {
    const ta = document.createElement("textarea");
    ta.value = cleaned;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
    return true;
  } catch {
    return false;
  }
};

const formatPhoneNumber = (value) => {
  if (!value) return "";

  // Check if it starts with + (international format)
  const hasPlus = value.startsWith('+');

  // Remove all non-digits
  let digits = value.replace(/\D/g, '');

  // If international format with country code 1 (US), handle it
  if (hasPlus && digits.startsWith('1') && digits.length === 11) {
    return `+1-${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7, 11)}`;
  }

  // If +1 but deleting back below 11 digits, drop the +1
  if (hasPlus && digits.startsWith('1') && digits.length < 11) {
    digits = digits.slice(1); // drop the country code
  }

  // If just 11 digits starting with 1 (without +), format with country code
  if (digits.startsWith('1') && digits.length === 11) {
    return `+1-${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7, 11)}`;
  }

  // Standard 10-digit format: XXX-XXX-XXXX
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

// Phone input wrapper that preserves cursor position during formatting
const PhoneInput = ({ value, onChange, ...props }) => {
  const ref = React.useRef(null);
  const cursorRef = React.useRef(null);

  const handleChange = (e) => {
    const input = e.target;
    const cursorPos = input.selectionStart;
    const digitsBeforeCursor = input.value.slice(0, cursorPos).replace(/\D/g, '').length;
    cursorRef.current = digitsBeforeCursor;
    onChange(e);
  };

  React.useLayoutEffect(() => {
    if (ref.current && cursorRef.current !== null) {
      const formatted = ref.current.value;
      let digitCount = 0;
      let newPos = 0;
      for (let i = 0; i < formatted.length; i++) {
        if (/\d/.test(formatted[i])) digitCount++;
        if (digitCount === cursorRef.current) { newPos = i + 1; break; }
      }
      if (digitCount < cursorRef.current) newPos = formatted.length;
      ref.current.setSelectionRange(newPos, newPos);
      cursorRef.current = null;
    }
  });

  return <Input ref={ref} value={value} onChange={handleChange} {...props} />;
};

export default function IncidentDetail() {
  const { incidentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { handlePhoneClick } = useSipPhone() || {};

  // Prev/next navigation from filtered incident list
  const navList = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("incidents_nav_list") || "[]");
    } catch { return []; }
  }, [incidentId]);
  const navIndex = navList.indexOf(incidentId);
  const prevIncidentId = navIndex > 0 ? navList[navIndex - 1] : null;
  const nextIncidentId = navIndex >= 0 && navIndex < navList.length - 1 ? navList[navIndex + 1] : null;

  const [incident, setIncident] = useState(null);
  const [phoneLookupNumber, setPhoneLookupNumber] = useState(null);

  // Related incidents linking
  const [showLinkIncident, setShowLinkIncident] = useState(false);
  const [linkIncSearch, setLinkIncSearch] = useState("");
  const [linkIncResults, setLinkIncResults] = useState([]);
  const [linkIncSearching, setLinkIncSearching] = useState(false);
  const [linkIncNote, setLinkIncNote] = useState("");
  const [incidentTypes, setIncidentTypes] = useState([]);
  const [units, setUnits] = useState([]);
  const [investigators, setInvestigators] = useState([]);
  const [dispatchers, setDispatchers] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [linkedProfileDetails, setLinkedProfileDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recentCalls, setRecentCalls] = useState([]);
  const [loadingRecentCalls, setLoadingRecentCalls] = useState(false);
  const [showRecentCallsFor, setShowRecentCallsFor] = useState(null); // "callback" | "victim" | "witness"

  const fetchRecentCalls = async () => {
    setLoadingRecentCalls(true);
    try {
      const res = await axios.get(`${API}/incoming-calls/recent`, { withCredentials: true });
      setRecentCalls(res.data || []);
    } catch { setRecentCalls([]); }
    setLoadingRecentCalls(false);
  };

  // Search states for units
  const [unitRespondedSearch, setUnitRespondedSearch] = useState("");
  const [unitInvestigatingSearch, setUnitInvestigatingSearch] = useState("");
  const [dispatcherSearch, setDispatcherSearch] = useState("");
  // Refs so we can refocus the search input after each add — keeps the mobile
  // keyboard open for rapid multi-add, matching the LiveCalls unit-assignment UX.
  const unitRespondedInputRef = useRef(null);
  const unitInvestigatingInputRef = useRef(null);
  const dispatcherInputRef = useRef(null);

  // Dialog states
  const [showVictimDialog, setShowVictimDialog] = useState(false);
  const [showWitnessDialog, setShowWitnessDialog] = useState(false);
  const [showPerpDescDialog, setShowPerpDescDialog] = useState(false);
  const [showArrestDialog, setShowArrestDialog] = useState(false);
  const [showIdentifiedDialog, setShowIdentifiedDialog] = useState(false);
  const [showLinkProfile, setShowLinkProfile] = useState(false);
  const [showLinkProfileDialog, setShowLinkProfileDialog] = useState(false);
  const [linkProfilePerp, setLinkProfilePerp] = useState(null);
  const [profileSearchQuery, setProfileSearchQuery] = useState("");

  // Media lightbox state
  const [lightboxMedia, setLightboxMedia] = useState(null);
  const [lightboxMediaIndex, setLightboxMediaIndex] = useState(0);
  const [hiddenMediaIds, setHiddenMediaIds] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);

  // Form state for complex fields
  const [newVictim, setNewVictim] = useState({});
  const [newWitness, setNewWitness] = useState({});
  const [newPerpDesc, setNewPerpDesc] = useState({});
  const [newArrest, setNewArrest] = useState({ identification_by: [] });
  const [newNote, setNewNote] = useState("");
  
  // Edit states for inline editing
  const [editingVictimId, setEditingVictimId] = useState(null);
  const [editingWitnessId, setEditingWitnessId] = useState(null);
  const [editingPerpId, setEditingPerpId] = useState(null);
  const [editingArrestId, setEditingArrestId] = useState(null);
  const [editingNoteIndex, setEditingNoteIndex] = useState(null);
  const [editNoteValue, setEditNoteValue] = useState("");
  const [editingCallbackId, setEditingCallbackId] = useState(null);
  const [sourceLiveCall, setSourceLiveCall] = useState(null);

  // Additional locations inline add/edit
  const [addLocOpen, setAddLocOpen] = useState(false);
  const [editingLocIdx, setEditingLocIdx] = useState(null);
  const [addLocData, setAddLocData] = useState({ title: "", location: "", cross_streets: "", location_notes: "", direction_of_flight: "" });

  // Linked radio recordings state
  const [linkedRecordings, setLinkedRecordings] = useState([]);
  const [loadingRecordings, setLoadingRecordings] = useState(false);
  const [showLinkedRecordings, setShowLinkedRecordings] = useState(false);
  const [playingRecording, setPlayingRecording] = useState(null);
  const [linkedAudioRef] = useState(() => new Audio());
  const linkedBlobUrlRef = useRef(null);
  const [transcribingLinkedFile, setTranscribingLinkedFile] = useState(null);
  const cancelTranscribeAllRef = useRef(false);

  // Media upload state
  const [uploading, setUploading] = useState(false);
  
  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportData, setExportData] = useState("");
  const [exportFilename, setExportFilename] = useState("");
  const [generatingPdf, setGeneratingPdf] = useState(false);

  // Undo state - stores previous incident state
  const [undoStack, setUndoStack] = useState([]);
  const [canUndo, setCanUndo] = useState(false);

  // Audit log state
  const [auditLog, setAuditLog] = useState([]);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [auditExpanded, setAuditExpanded] = useState(false);

  useEffect(() => {
    fetchData();
    // Mark incident as read when opened (this gets just the incident)
    markIncidentAsRead();
    // Refresh audit log if expanded, otherwise clear stale data
    if (auditExpanded) {
      fetchAuditLog();
    } else {
      setAuditLog([]);
    }
  }, [incidentId]);

  const markIncidentAsRead = async () => {
    try {
      // Mark incident as read
      await axios.post(`${API}/incidents/${incidentId}/read`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Failed to mark as read:", error);
    }
  };
  
  // Also mark linked live call as read when incident data is loaded
  useEffect(() => {
    if (incident?.source_call_id) {
      axios.post(`${API}/live-calls/${incident.source_call_id}/read`, {}, { withCredentials: true })
        .catch(e => console.log("Could not mark live call as read:", e.message));
    }
  }, [incident?.source_call_id]);

  // Sync hidden media IDs from incident
  useEffect(() => {
    if (incident?.hidden_media_file_ids) {
      setHiddenMediaIds(incident.hidden_media_file_ids);
    }
  }, [incident?.hidden_media_file_ids]);

  const fetchData = async () => {
    try {
      const [incidentRes, typesRes, unitsRes, investigatorsRes, dispatchersRes, profilesRes] = await Promise.all([
        axios.get(`${API}/incidents/${incidentId}`, { withCredentials: true }),
        axios.get(`${API}/incident-types`, { withCredentials: true }),
        axios.get(`${API}/units`, { withCredentials: true }),
        axios.get(`${API}/units/investigators`, { withCredentials: true }),
        axios.get(`${API}/units/dispatchers`, { withCredentials: true }),
        axios.get(`${API}/perpetrator-profiles`, { withCredentials: true })
      ]);
      
      // Ensure notes array is initialized
      const incidentData = {
        ...incidentRes.data,
        notes: incidentRes.data.notes || []
      };
      
      setIncident(incidentData);
      setIncidentTypes([...typesRes.data].sort((a, b) => a.name.localeCompare(b.name)));
      setUnits(unitsRes.data);
      setInvestigators(investigatorsRes.data);
      setDispatchers(dispatchersRes.data);
      setProfiles(profilesRes.data?.profiles || profilesRes.data || []);

      // Fetch source live call if this incident came from one. Clear any
      // previously-loaded source data first so navigating from an incident
      // that came from a live call to one that didn't doesn't leak the
      // prior call's units / updates / etc into the new view.
      setSourceLiveCall(null);
      if (incidentData.from_live_call_id) {
        try {
          const liveCallRes = await axios.get(`${API}/live-calls/${incidentData.from_live_call_id}`, { withCredentials: true });
          setSourceLiveCall(liveCallRes.data);
        } catch (e) {
          console.log("Could not fetch source live call:", e);
        }
      }

      // Fetch full details for profiles linked to this incident (need ready_for_arrest arrays).
      // Reset to {} first so the previous incident's profile detail map
      // doesn't bleed through when the new one has no linked profiles.
      setLinkedProfileDetails({});
      const linkedPids = new Set();
      (incidentData.perp_descriptions || []).forEach(p => { if (p.linked_profile_id) linkedPids.add(p.linked_profile_id); });
      (incidentData.linked_profiles || []).forEach(pid => linkedPids.add(pid));
      if (linkedPids.size > 0) {
        const detailResults = await Promise.allSettled(
          [...linkedPids].map(pid => axios.get(`${API}/perpetrator-profiles/${pid}`, { withCredentials: true }))
        );
        const details = {};
        detailResults.forEach(r => { if (r.status === "fulfilled" && r.value?.data) details[r.value.data.profile_id] = r.value.data; });
        setLinkedProfileDetails(details);
      }

      // Refresh to get updated read receipts after marking as read
      setTimeout(async () => {
        try {
          const refreshRes = await axios.get(`${API}/incidents/${incidentId}`, { withCredentials: true });
          setIncident({
            ...refreshRes.data,
            notes: refreshRes.data.notes || []
          });
        } catch (e) {}
      }, 500);
    } catch (error) {
      console.error("Failed to fetch incident:", error);
      toast.error("Failed to load incident");
      navigate("/incidents");
    } finally {
      setLoading(false);
    }
  };

  // Fetch audit log
  const fetchAuditLog = async () => {
    setLoadingAudit(true);
    try {
      const res = await axios.get(`${API}/incidents/${incidentId}/audit`, { withCredentials: true });
      setAuditLog(res.data || []);
    } catch (error) {
      console.error("Failed to fetch audit log:", error);
    } finally {
      setLoadingAudit(false);
    }
  };

  // Fetch linked radio recordings for the source live call
  const fetchLinkedRecordings = async (call) => {
    if (!user?.recording_admin_access && user?.role !== 'admin') return;
    if (!call?.created_at) return;

    setLoadingRecordings(true);
    try {
      const createdAt = new Date(call.created_at);
      const startTime = new Date(createdAt.getTime() - 2 * 60 * 1000);

      const call98Update = call.updates?.find(u => u.call_mode === "call_98");
      const endTime = call98Update
        ? new Date(new Date(call98Update.timestamp).getTime() + 2 * 60 * 1000)
        : new Date();

      // Format for API in NY time (backend expects ET)
      const formatNYDate = (d) => {
        const parts = d.toLocaleDateString("en-US", { timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit" }).split("/");
        return `${parts[2]}-${parts[0]}-${parts[1]}`;
      };
      const formatNYTime = (d) => {
        return d.toLocaleTimeString("en-US", { timeZone: "America/New_York", hour: "2-digit", minute: "2-digit", hour12: false });
      };
      const dateFrom = formatNYDate(startTime);
      const dateTo = formatNYDate(endTime);
      const timeFrom = formatNYTime(startTime);
      const timeTo = formatNYTime(endTime);

      const params = new URLSearchParams({
        channel: "chsp1",
        date_from: dateFrom,
        date_to: dateTo,
        time_from: timeFrom,
        time_to: timeTo
      });

      const response = await axios.get(`${API}/radio/recordings?${params}`, { withCredentials: true });
      setLinkedRecordings(response.data.recordings || []);
    } catch (error) {
      console.error("Error fetching linked recordings:", error);
      setLinkedRecordings([]);
    } finally {
      setLoadingRecordings(false);
    }
  };

  // Play linked recording inline
  const transcribeLinkedRecording = async (filename) => {
    setTranscribingLinkedFile(filename);
    try {
      const decodedFilename = decodeURIComponent(filename);
      const response = await axios.post(`${API}/radio/recordings/transcribe`, null, { params: { filename: decodedFilename }, withCredentials: true });
      setLinkedRecordings(prev => prev.map(r => r.filename === filename ? { ...r, transcription: response.data.transcription, transcription_status: "completed" } : r));
      toast.success("Transcription complete");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Transcription failed");
    } finally {
      setTranscribingLinkedFile(null);
    }
  };

  const playLinkedRecording = async (filename) => {
    if (playingRecording === filename) {
      linkedAudioRef.pause();
      setPlayingRecording(null);
      return;
    }

    try {
      // Revoke previous blob URL
      if (linkedBlobUrlRef.current) {
        URL.revokeObjectURL(linkedBlobUrlRef.current);
        linkedBlobUrlRef.current = null;
      }

      // Use axios (includes auth token) instead of fetch
      const response = await axios.get(`${API}/radio/stream/${filename}`, {
        responseType: 'blob',
        withCredentials: true
      });

      const blobUrl = URL.createObjectURL(response.data);
      linkedBlobUrlRef.current = blobUrl;
      linkedAudioRef.src = blobUrl;
      linkedAudioRef.onended = () => { setPlayingRecording(null); };
      linkedAudioRef.onerror = () => { toast.error("Failed to play recording"); setPlayingRecording(null); };
      await linkedAudioRef.play();
      setPlayingRecording(filename);
    } catch (err) {
      console.error("Error playing recording:", err);
      toast.error("Failed to play recording");
      setPlayingRecording(null);
    }
  };

  // Fetch linked recordings when sourceLiveCall is loaded
  useEffect(() => {
    if (sourceLiveCall && (user?.recording_admin_access || user?.role === 'admin')) {
      fetchLinkedRecordings(sourceLiveCall);
    }
  }, [sourceLiveCall, user?.recording_admin_access]);

  // Export audit log as CSV
  const exportAuditCSV = async () => {
    try {
      const response = await axios.get(`${API}/incidents/${incidentId}/audit/export/csv`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit_${incidentId}_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Audit log exported as CSV");
    } catch (error) {
      toast.error("Failed to export CSV");
    }
  };

  // Export audit log as PDF
  const exportAuditPDF = async () => {
    try {
      const response = await axios.get(`${API}/incidents/${incidentId}/audit/export/pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit_${incidentId}_${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Audit log exported as PDF");
    } catch (error) {
      toast.error("Failed to export PDF");
    }
  };

  const handleSave = async (updatedIncident = null) => {
    setSaving(true);
    try {
      const dataToSave = updatedIncident || incident;
      
      // Save current state to undo stack before saving
      setUndoStack(prev => [...prev.slice(-9), JSON.parse(JSON.stringify(incident))]);
      setCanUndo(true);
      
      console.log("Saving incident:", incidentId, dataToSave);
      const response = await axios.put(`${API}/incidents/${incidentId}`, dataToSave, { withCredentials: true });
      setIncident(response.data);
      toast.success("Incident saved");
    } catch (error) {
      console.error("Save error:", error);
      console.error("Error response:", error.response?.data);
      // Handle error message properly - could be string or object
      let errorMsg = "Unknown error";
      if (error.response?.data?.detail) {
        errorMsg = typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : JSON.stringify(error.response.data.detail);
      } else if (error.message) {
        errorMsg = error.message;
      }
      toast.error("Failed to save: " + errorMsg);
    } finally {
      setSaving(false);
    }
  };

  // Undo last save
  const handleUndo = async () => {
    if (undoStack.length === 0) {
      toast.error("Nothing to undo");
      return;
    }
    
    const previousState = undoStack[undoStack.length - 1];
    const newStack = undoStack.slice(0, -1);
    setUndoStack(newStack);
    setCanUndo(newStack.length > 0);
    
    setSaving(true);
    try {
      const response = await axios.put(`${API}/incidents/${incidentId}`, previousState, { withCredentials: true });
      setIncident(response.data);
      toast.success("Undo successful");
    } catch (error) {
      // Restore the stack if undo failed
      setUndoStack(undoStack);
      setCanUndo(true);
      toast.error("Undo failed: " + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setIncident({ ...incident, [field]: value });
  };

  // Related incidents
  const searchIncidentsForLink = async (query) => {
    if (!query || query.length < 2) { setLinkIncResults([]); return; }
    setLinkIncSearching(true);
    try {
      const res = await axios.get(`${API}/incidents`, { params: { search: query, limit: 20 }, withCredentials: true });
      const results = Array.isArray(res.data) ? res.data : [];
      setLinkIncResults(results.filter(i => i.incident_id !== incidentId));
    } catch { setLinkIncResults([]); }
    finally { setLinkIncSearching(false); }
  };

  const linkRelatedIncident = async (targetId) => {
    try {
      const res = await axios.post(`${API}/incidents/${incidentId}/link-incident`, {
        target_incident_id: targetId,
        note: linkIncNote,
      }, { withCredentials: true });
      setIncident(res.data);
      toast.success("Incident linked");
      setShowLinkIncident(false);
      setLinkIncSearch("");
      setLinkIncResults([]);
      setLinkIncNote("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to link incident");
    }
  };

  const unlinkRelatedIncident = async (targetId) => {
    try {
      const res = await axios.post(`${API}/incidents/${incidentId}/unlink-incident`, {
        target_incident_id: targetId,
      }, { withCredentials: true });
      setIncident(res.data);
      toast.success("Incident unlinked");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to unlink incident");
    }
  };

  // Multi-select toggle helpers
  const toggleUnit = (unitName, field, inputRef = null) => {
    const current = incident[field] || [];
    if (current.includes(unitName)) {
      updateField(field, current.filter(u => u !== unitName));
    } else {
      updateField(field, [...current, unitName]);
      // Clear search so user can immediately type the next unit
      if (field === "units_responded") setUnitRespondedSearch("");
      else if (field === "units_investigating") setUnitInvestigatingSearch("");
      else if (field === "dispatcher_units") setDispatcherSearch("");
    }
    // Refocus the search input so the keyboard stays open for the next add.
    if (inputRef) setTimeout(() => inputRef.current?.focus(), 100);
  };

  // Victim handlers
  const addVictim = () => {
    const victims = [...(incident.victims || []), { ...newVictim, victim_id: `vic_${Date.now()}` }];
    updateField("victims", victims);
    setNewVictim({});
    setShowVictimDialog(false);
  };

  const removeVictim = (victimId) => {
    const victims = incident.victims.filter(v => v.victim_id !== victimId);
    updateField("victims", victims);
  };

  // Witness handlers
  const addWitness = () => {
    const witnesses = [...(incident.witnesses || []), { ...newWitness, witness_id: `wit_${Date.now()}` }];
    updateField("witnesses", witnesses);
    setNewWitness({});
    setShowWitnessDialog(false);
  };

  const removeWitness = (witnessId) => {
    const witnesses = incident.witnesses.filter(w => w.witness_id !== witnessId);
    updateField("witnesses", witnesses);
  };

  // Perp description handlers
  const addPerpDesc = () => {
    const newPerp = { ...newPerpDesc, perp_id: `perp_${Date.now()}` };
    const perpDescriptions = [...(incident.perp_descriptions || []), newPerp];
    updateField("perp_descriptions", perpDescriptions);
    setNewPerpDesc({});
    setShowPerpDescDialog(false);
  };

  const removePerpDesc = async (perpId) => {
    const perp = incident.perp_descriptions?.find(p => p.perp_id === perpId);
    const perpDescriptions = (incident.perp_descriptions || []).filter(p => p.perp_id !== perpId);
    // Also remove any linked arrest records
    const arrests = (incident.arrests || []).filter(a => a.linked_perp_id !== perpId);
    
    // If perp had a linked profile, unlink it
    let linkedProfiles = incident.linked_profiles || [];
    if (perp?.linked_profile_id) {
      // Remove from linked_profiles array if present
      linkedProfiles = linkedProfiles.filter(id => id !== perp.linked_profile_id);
      
      // Also unlink from the profile side
      try {
        await axios.delete(
          `${API}/perpetrator-profiles/${perp.linked_profile_id}/link/${incident.incident_id}`,
          { withCredentials: true }
        );
        
        // If perp was arrested, remove arrest from profile
        if (perp.was_arrested) {
          const arrestRecord = incident.arrests?.find(a => a.linked_perp_id === perpId);
          if (arrestRecord) {
            await axios.delete(
              `${API}/perpetrator-profiles/${perp.linked_profile_id}/arrest/${arrestRecord.arrest_id}`,
              { withCredentials: true }
            );
          }
        }
        
        // If perp was identified for arrest, remove from profile
        if (perp.identified_for_arrest) {
          await axios.delete(
            `${API}/perpetrator-profiles/${perp.linked_profile_id}/ready-for-arrest/${incident.incident_id}`,
            { withCredentials: true }
          );
        }
      } catch (error) {
        console.error("Failed to unlink from profile:", error);
      }
    }
    
    const updated = { ...incident, perp_descriptions: perpDescriptions, arrests, linked_profiles: linkedProfiles };
    setIncident(updated);
    handleSave(updated);
  };

  // Toggle perp arrest - creates/removes linked arrest record AND updates incident is_arrest
  // Also syncs to perp profile if linked
  const togglePerpArrest = async (perpId, isArrested) => {
    const updatedPerps = (incident.perp_descriptions || []).map(p => 
      p.perp_id === perpId ? { ...p, was_arrested: isArrested } : p
    );
    
    let updatedArrests = [...(incident.arrests || [])];
    const perp = incident.perp_descriptions?.find(p => p.perp_id === perpId);
    
    if (isArrested) {
      // Only create arrest record if one doesn't already exist for this perp.
      // We hold the chosen record in `arrestRecord` so the profile-sync block
      // below can read its arrest_date in either case (re-toggling an already
      // existing arrest used to throw ReferenceError on newArrestRecord).
      const existingArrest = updatedArrests.find(a => a.linked_perp_id === perpId);
      let arrestRecord = existingArrest;
      if (!existingArrest) {
        arrestRecord = {
          arrest_id: `arr_${Date.now()}`,
          linked_perp_id: perpId,
          linked_profile_id: perp?.linked_profile_id || null,
          perp_description_summary: `${perp?.sex || ''} ${perp?.race || ''} ${perp?.age || ''}`.trim() || 'Unknown',
          was_live: false,
          identification_by: [],
          created_from_perp: true,
          arrest_date: new Date().toISOString()
        };
        updatedArrests.push(arrestRecord);
      }

      // If perp is linked to a profile, sync arrest to profile
      if (perp?.linked_profile_id) {
        try {
          await axios.post(
            `${API}/perpetrator-profiles/${perp.linked_profile_id}/arrest`,
            {
              incident_id: incident.incident_id,
              incident_type: incident.incident_type,
              secondary_incident_type: incident.secondary_incident_type || "",
              headline: incident.headline,
              address: incident.address || incident.location,
              arrest_date: arrestRecord?.arrest_date || new Date().toISOString()
            },
            { withCredentials: true }
          );
          toast.success(`Arrest synced to profile: ${perp.linked_profile_name || 'Unknown'}`);
        } catch (error) {
          console.error("Failed to sync arrest to profile:", error);
        }
      }
    } else {
      // Remove linked arrest record
      const removedArrest = updatedArrests.find(a => a.linked_perp_id === perpId);
      updatedArrests = updatedArrests.filter(a => a.linked_perp_id !== perpId);
      
      // If perp is linked to a profile, remove arrest from profile
      // Use incident_id for deletion as it's consistent between frontend and backend
      if (perp?.linked_profile_id) {
        try {
          await axios.delete(
            `${API}/perpetrator-profiles/${perp.linked_profile_id}/arrest/${incident.incident_id}`,
            { withCredentials: true }
          );
          toast.success("Arrest removed from profile");
        } catch (error) {
          console.error("Failed to remove arrest from profile:", error);
        }
      }
    }
    
    // Check if ANY perp is arrested to set incident-level is_arrest
    const anyPerpArrested = updatedPerps.some(p => p.was_arrested);
    
    const updated = { 
      ...incident, 
      perp_descriptions: updatedPerps, 
      arrests: updatedArrests,
      is_arrest: anyPerpArrested || incident.is_arrest // Keep true if manually set, or if any perp arrested
    };
    setIncident(updated);
    handleSave(updated);
  };

  // Arrest handlers
  const addArrest = async () => {
    const newArrestRecord = { 
      ...newArrest, 
      arrest_id: `arr_${Date.now()}`,
      arrest_date: new Date().toISOString()
    };
    const arrests = [...(incident.arrests || []), newArrestRecord];
    
    // If linked to a perp, also mark them as arrested
    let updatedPerps = incident.perp_descriptions || [];
    let profileIdToSync = newArrest.linked_profile_id;
    
    if (newArrest.linked_perp_id) {
      const perp = updatedPerps.find(p => p.perp_id === newArrest.linked_perp_id);
      updatedPerps = updatedPerps.map(p => 
        p.perp_id === newArrest.linked_perp_id ? { ...p, was_arrested: true } : p
      );
      // Get profile from perp if not already set
      if (!profileIdToSync && perp?.linked_profile_id) {
        profileIdToSync = perp.linked_profile_id;
      }
    }
    
    // Sync arrest to profile if linked
    if (profileIdToSync) {
      try {
        await axios.post(
          `${API}/perpetrator-profiles/${profileIdToSync}/arrest`,
          {
            incident_id: incident.incident_id,
            incident_type: incident.incident_type,
            secondary_incident_type: incident.secondary_incident_type || "",
            headline: incident.headline,
            address: incident.address,
            date_time: incident.date_time,
            arrest_date: newArrestRecord.arrest_date,
            linked_ready_for_arrest_id: newArrest.linked_ready_for_arrest_id || null
          },
          { withCredentials: true }
        );
        toast.success("Arrest synced to profile");
      } catch (error) {
        console.error("Failed to sync arrest to profile:", error);
      }
    }
    
    // Auto-check incident-level is_arrest when adding arrest record
    const updated = { ...incident, arrests, perp_descriptions: updatedPerps, is_arrest: true };
    setIncident(updated);
    handleSave(updated);
    setNewArrest({ identification_by: [] });
    setShowArrestDialog(false);
  };

  const removeArrest = async (arrestId) => {
    const arrestToRemove = incident.arrests?.find(a => a.arrest_id === arrestId);
    const arrests = (incident.arrests || []).filter(a => a.arrest_id !== arrestId);
    
    // If this arrest was linked to a perp, unmark them as arrested
    let updatedPerps = incident.perp_descriptions || [];
    let profileIdToSync = arrestToRemove?.linked_profile_id;
    
    if (arrestToRemove?.linked_perp_id) {
      const perp = updatedPerps.find(p => p.perp_id === arrestToRemove.linked_perp_id);
      updatedPerps = updatedPerps.map(p => 
        p.perp_id === arrestToRemove.linked_perp_id ? { ...p, was_arrested: false } : p
      );
      // Get profile ID from perp if not on arrest record
      if (!profileIdToSync && perp?.linked_profile_id) {
        profileIdToSync = perp.linked_profile_id;
      }
    }
    
    // Remove arrest from profile if linked
    if (profileIdToSync && arrestId) {
      try {
        await axios.delete(
          `${API}/perpetrator-profiles/${profileIdToSync}/arrest/${arrestId}`,
          { withCredentials: true }
        );
        toast.success("Arrest removed from profile");
      } catch (error) {
        console.error("Failed to remove arrest from profile:", error);
      }
    }
    
    const updated = { ...incident, arrests, perp_descriptions: updatedPerps };
    setIncident(updated);
    handleSave(updated);
  };

  const toggleArrestIdentification = (method) => {
    const current = newArrest.identification_by || [];
    if (current.includes(method)) {
      setNewArrest({ ...newArrest, identification_by: current.filter(m => m !== method) });
    } else {
      setNewArrest({ ...newArrest, identification_by: [...current, method] });
    }
  };

  // Identified for Arrest handlers
  const IDENTIFICATION_METHODS = ['CV', 'Witness', 'Security Footage'];
  
  const toggleIdentifiedForArrest = async (perpId, isIdentified, methods = [], reportFiled = false) => {
    // First find the perp to get the profile ID (use current data)
    const perp = incident.perp_descriptions?.find(p => p.perp_id === perpId);
    
    const updatedPerps = (incident.perp_descriptions || []).map(p => 
      p.perp_id === perpId ? { 
        ...p, 
        identified_for_arrest: isIdentified,
        identification_methods: isIdentified ? methods : [],
        report_filed: isIdentified ? reportFiled : false
      } : p
    );
    
    // Sync to profile if linked - do this BEFORE updating state
    if (perp?.linked_profile_id && isIdentified) {
      try {
        const response = await axios.post(
          `${API}/perpetrator-profiles/${perp.linked_profile_id}/ready-for-arrest`,
          {
            incident_id: incident.incident_id,
            incident_type: incident.incident_type,
            secondary_incident_type: incident.secondary_incident_type || "",
            headline: incident.headline,
            address: incident.address,
            date_time: incident.date_time,
            identification_methods: methods,
            report_filed: reportFiled,
            perp_description: `${perp.sex || ''} ${perp.race || ''} ${perp.age || ''}`.trim()
          },
          { withCredentials: true }
        );
        console.log("Ready for arrest sync response:", response.data);
        toast.success(`Ready for arrest synced to profile: ${perp.linked_profile_name || 'Unknown'}`);
      } catch (error) {
        console.error("Failed to sync ready for arrest to profile:", error);
        toast.error("Failed to sync to profile");
      }
    } else if (perp?.linked_profile_id && !isIdentified) {
      // Remove identification from profile
      try {
        await axios.delete(
          `${API}/perpetrator-profiles/${perp.linked_profile_id}/ready-for-arrest/${incident.incident_id}`,
          { withCredentials: true }
        );
        toast.success("Ready for arrest removed from profile");
      } catch (error) {
        console.error("Failed to remove ready for arrest from profile:", error);
      }
    }
    
    const updated = { ...incident, perp_descriptions: updatedPerps };
    setIncident(updated);
    handleSave(updated);
  };
  
  // Update report filed status for a perp - syncs to profile
  const updatePerpReportFiled = async (perpId, reportFiled) => {
    // Get the CURRENT perp from current incident state
    const perp = incident.perp_descriptions?.find(p => p.perp_id === perpId);
    
    if (!perp) {
      console.error("Perp not found:", perpId);
      return;
    }
    
    const updatedPerps = (incident.perp_descriptions || []).map(p => 
      p.perp_id === perpId ? { ...p, report_filed: reportFiled } : p
    );
    
    // If linked to profile and identified for arrest, update the ready_for_arrest record
    // Check both identified_for_arrest and ready_for_identification flags
    const isIdentified = perp.identified_for_arrest || perp.ready_for_identification;
    
    if (perp.linked_profile_id && isIdentified) {
      try {
        const response = await axios.post(
          `${API}/perpetrator-profiles/${perp.linked_profile_id}/ready-for-arrest`,
          {
            incident_id: incident.incident_id,
            incident_type: incident.incident_type,
            secondary_incident_type: incident.secondary_incident_type || "",
            headline: incident.headline,
            address: incident.address,
            date_time: incident.date_time,
            identification_methods: perp.identification_methods || [],
            report_filed: reportFiled,
            perp_description: `${perp.sex || ''} ${perp.race || ''} ${perp.age || ''}`.trim()
          },
          { withCredentials: true }
        );
        console.log("Report filed sync response:", response.data);
        toast.success(`Report filed status updated on profile`);
      } catch (error) {
        console.error("Failed to update profile:", error);
        toast.error("Failed to sync to profile");
      }
    } else {
      console.log("Not syncing report_filed - perp not linked or not identified:", {
        linked_profile_id: perp.linked_profile_id,
        identified_for_arrest: perp.identified_for_arrest,
        ready_for_identification: perp.ready_for_identification
      });
      if (!perp.linked_profile_id) {
        toast.info("Link a profile to sync report filed status");
      }
    }
    
    const updated = { ...incident, perp_descriptions: updatedPerps };
    setIncident(updated);
    handleSave(updated);
  };

  // Unlink profile from perp - also removes from linked_profiles and profile side
  const unlinkProfileFromPerp = async (perpId) => {
    const perp = incident.perp_descriptions?.find(p => p.perp_id === perpId);
    
    if (!perp?.linked_profile_id) {
      toast.error("No profile linked to this perp");
      return;
    }
    
    const profileId = perp.linked_profile_id;
    
    const updatedPerps = (incident.perp_descriptions || []).map(p => 
      p.perp_id === perpId ? { 
        ...p, 
        linked_profile_id: null,
        linked_profile_name: null
      } : p
    );
    
    // Also remove from linked_profiles array
    const linkedProfiles = (incident.linked_profiles || []).filter(id => id !== profileId);
    
    // Unlink from profile side too
    try {
      await axios.delete(
        `${API}/perpetrator-profiles/${profileId}/link/${incident.incident_id}`,
        { withCredentials: true }
      );
      
      // If perp was arrested, remove arrest from profile
      if (perp.was_arrested) {
        const arrestRecord = incident.arrests?.find(a => a.linked_perp_id === perpId || a.linked_profile_id === profileId);
        if (arrestRecord) {
          await axios.delete(
            `${API}/perpetrator-profiles/${profileId}/arrest/${arrestRecord.arrest_id}`,
            { withCredentials: true }
          );
        }
      }
      
      // If perp was identified for arrest, remove from profile
      if (perp.identified_for_arrest) {
        await axios.delete(
          `${API}/perpetrator-profiles/${profileId}/ready-for-arrest/${incident.incident_id}`,
          { withCredentials: true }
        );
      }
    } catch (error) {
      console.error("Failed to unlink from profile:", error);
    }
    
    // Also update any arrests that were linked to this profile
    const updatedArrests = (incident.arrests || []).map(a => 
      a.linked_profile_id === profileId ? { ...a, linked_profile_id: null, linked_profile_name: null } : a
    );
    
    const updated = { ...incident, perp_descriptions: updatedPerps, linked_profiles: linkedProfiles, arrests: updatedArrests };
    setIncident(updated);
    await handleSave(updated);
    toast.success("Profile unlinked");
  };

  const updatePerpIdentificationMethods = (perpId, methods) => {
    const updatedPerps = (incident.perp_descriptions || []).map(p => 
      p.perp_id === perpId ? { ...p, identification_methods: methods } : p
    );
    const updated = { ...incident, perp_descriptions: updatedPerps };
    setIncident(updated);
  };

  // Note handler
  const addNote = () => {
    if (!newNote.trim()) return;
    const noteObj = {
      note_id: `note_${Date.now()}`,
      text: newNote.trim(),
      created_at: new Date().toISOString(),
      created_by: user?.name || "Unknown",
    };
    const notes = [...(incident.notes || []), noteObj];
    updateField("notes", notes);
    setNewNote("");
  };

  // Edit handlers for inline editing
  const updateVictim = (victimId, updates) => {
    const victims = (incident.victims || []).map(v => 
      v.victim_id === victimId ? { ...v, ...updates } : v
    );
    
    // If updating has_injuries, also update injuries_reported
    let updatedIncident = { ...incident, victims };
    if ('has_injuries' in updates) {
      // Check if ANY victim now has injuries
      const anyVictimHasInjuries = victims.some(v => v.has_injuries === true);
      updatedIncident.injuries_reported = anyVictimHasInjuries;
    }
    
    setIncident(updatedIncident);
  };

  const saveVictimEdit = async (victimId) => {
    await handleSave();
    setEditingVictimId(null);
    toast.success("Victim updated");
  };

  const updateWitness = (witnessId, updates) => {
    const witnesses = (incident.witnesses || []).map(w => 
      w.witness_id === witnessId ? { ...w, ...updates } : w
    );
    const updated = { ...incident, witnesses };
    setIncident(updated);
  };

  const saveWitnessEdit = async (witnessId) => {
    await handleSave();
    setEditingWitnessId(null);
    toast.success("Witness updated");
  };

  const updatePerpDesc = (perpId, updates) => {
    const perpDescriptions = (incident.perp_descriptions || []).map(p => 
      p.perp_id === perpId ? { ...p, ...updates } : p
    );
    const updated = { ...incident, perp_descriptions: perpDescriptions };
    setIncident(updated);
  };

  // Link an existing profile to a perp description
  const linkProfileToPerp = async (perpId, profile) => {
    if (!perpId) {
      toast.error("No perpetrator ID provided");
      return;
    }
    if (!profile || !profile.profile_id) {
      toast.error("No profile selected");
      return;
    }
    
    console.log("Linking perp", perpId, "to profile", profile.profile_id);
    
    // Find the perp to check if it has an arrest
    const perp = (incident.perp_descriptions || []).find(p => p.perp_id === perpId);
    const existingArrest = (incident.arrests || []).find(a => a.linked_perp_id === perpId);
    const existingReadyForArrest = perp?.ready_for_identification || perp?.identified_for_arrest || perp?.ready_for_arrest;
    
    const perpDescriptions = (incident.perp_descriptions || []).map(p => 
      p.perp_id === perpId ? { 
        ...p, 
        linked_profile_id: profile.profile_id,
        linked_profile_name: profile.name
      } : p
    );
    
    // Also add to linked_profiles array if not already there
    const linkedProfiles = incident.linked_profiles || [];
    if (!linkedProfiles.includes(profile.profile_id)) {
      linkedProfiles.push(profile.profile_id);
    }
    
    const updated = { ...incident, perp_descriptions: perpDescriptions, linked_profiles: linkedProfiles };
    setIncident(updated);
    await handleSave(updated);
    
    // Also link the incident to the profile
    try {
      await axios.post(
        `${API}/perpetrator-profiles/${profile.profile_id}/link/${incident.incident_id}`,
        {},
        { withCredentials: true }
      );
      
      // If perp has ready_for_arrest checked, sync it to profile
      if (existingReadyForArrest) {
        try {
          await axios.post(
            `${API}/perpetrator-profiles/${profile.profile_id}/ready-for-arrest`,
            {
              incident_id: incident.incident_id,
              incident_type: incident.incident_type,
              secondary_incident_type: incident.secondary_incident_type || "",
              headline: incident.headline,
              address: incident.address,
              date_time: incident.date_time,
              identification_methods: perp?.identification_methods || [],
              report_filed: perp?.report_filed || false,
              perp_description: perp ? `${perp.sex || ''} ${perp.race || ''} ${perp.age || ''}`.trim() : ''
            },
            { withCredentials: true }
          );
          console.log("Synced ready_for_arrest to profile");
        } catch (e) {
          console.error("Failed to sync ready_for_arrest:", e);
        }
      }
      
      // If perp has an arrest already (or was_arrested flag is set), sync it to profile
      if (existingArrest || perp?.was_arrested) {
        try {
          await axios.post(
            `${API}/perpetrator-profiles/${profile.profile_id}/arrest`,
            {
              incident_id: incident.incident_id,
              incident_type: incident.incident_type,
              secondary_incident_type: incident.secondary_incident_type || "",
              headline: incident.headline,
              address: incident.address,
              date_time: incident.date_time,
              arrest_date: existingArrest?.arrest_date || new Date().toISOString()
            },
            { withCredentials: true }
          );
          // Also update the arrest record's linked_profile_id on the incident
          if (existingArrest) {
            const updatedArrests = (incident.arrests || []).map(a =>
              a.linked_perp_id === perpId ? { ...a, linked_profile_id: profile.profile_id } : a
            );
            const updatedWithArrests = { ...incident, perp_descriptions: perpDescriptions, linked_profiles: linkedProfiles, arrests: updatedArrests };
            setIncident(updatedWithArrests);
            handleSave(updatedWithArrests);
          }
          toast.success(`Arrest synced to profile: ${profile.name}`);
        } catch (e) {
          console.error("Failed to sync arrest:", e);
        }
      }
    } catch (error) {
      console.error("Failed to link profile to incident:", error);
    }
    
    setShowLinkProfileDialog(false);
    setLinkProfilePerp(null);
    setProfileSearchQuery("");
    toast.success(`Linked to profile: ${profile.name}`);
  };

  const savePerpEdit = async (perpId) => {
    await handleSave();
    setEditingPerpId(null);
    toast.success("Perpetrator updated");
  };

  const updateArrest = (arrestId, updates) => {
    const arrests = (incident.arrests || []).map(a => 
      a.arrest_id === arrestId ? { ...a, ...updates } : a
    );
    const updated = { ...incident, arrests };
    setIncident(updated);
  };

  const saveArrestEdit = async (arrestId) => {
    const arrest = incident.arrests?.find(a => a.arrest_id === arrestId);
    const linkedPerp = incident.perp_descriptions?.find(p => p.perp_id === arrest?.linked_perp_id);
    const profileId = linkedPerp?.linked_profile_id || arrest?.linked_profile_id;

    // Always sync arrest data to profile if there's a linked profile
    if (profileId) {
      try {
        await axios.post(
          `${API}/perpetrator-profiles/${profileId}/arrest`,
          {
            incident_id: incident.incident_id,
            incident_type: incident.incident_type,
            secondary_incident_type: incident.secondary_incident_type || "",
            headline: incident.headline,
            address: incident.address,
            date_time: incident.date_time,
            arrest_date: arrest.arrest_date || new Date().toISOString(),
            linked_ready_for_arrest_id: arrest.linked_ready_for_arrest_id,
            linked_ready_for_arrest_ids: arrest.linked_ready_for_arrest_ids,
            // Include all arrest fields for sync
            was_live: arrest.was_live,
            perp_stopped_by_pd_only: arrest.perp_stopped_by_pd_only,
            outstanding_perpetrators: arrest.outstanding_perpetrators,
            identification_by: arrest.identification_by
          },
          { withCredentials: true }
        );
      } catch (error) {
        console.error("Failed to sync arrest to profile:", error);
      }
    }

    await handleSave();
    setEditingArrestId(null);
    toast.success("Arrest updated");
  };

  const startEditNote = (index) => {
    setEditingNoteIndex(index);
    const note = incident.notes[index];
    const noteText = typeof note === 'string' ? note : (note?.text || note?.note || String(note));
    setEditNoteValue(noteText);
  };

  const saveNoteEdit = async () => {
    if (!editNoteValue.trim()) return;
    const notes = [...incident.notes];
    const existing = notes[editingNoteIndex];
    if (typeof existing === 'string') {
      // Migrate old string note to object
      notes[editingNoteIndex] = {
        note_id: `note_${Date.now()}`,
        text: editNoteValue.trim(),
        created_at: null,
        created_by: null,
        edited_at: new Date().toISOString(),
        edited_by: user?.name || "Unknown",
      };
    } else {
      notes[editingNoteIndex] = {
        ...existing,
        text: editNoteValue.trim(),
        edited_at: new Date().toISOString(),
        edited_by: user?.name || "Unknown",
      };
    }
    updateField("notes", notes);
    await handleSave({ ...incident, notes });
    setEditingNoteIndex(null);
    setEditNoteValue("");
    toast.success("Note updated");
  };

  const removeNote = (index) => {
    const notes = incident.notes.filter((_, i) => i !== index);
    updateField("notes", notes);
  };

  // Profile link handler
  const linkProfile = async (profileId) => {
    try {
      await axios.post(`${API}/perpetrator-profiles/${profileId}/link/${incidentId}`, {}, { withCredentials: true });
      const linkedProfiles = [...(incident.linked_profiles || []), profileId];
      updateField("linked_profiles", linkedProfiles);
      setShowLinkProfile(false);
      toast.success("Profile linked");
    } catch (error) {
      toast.error("Failed to link profile");
    }
  };

  const unlinkProfile = async (profileId) => {
    try {
      // First, always try to remove any arrest record for this incident from the profile
      // This handles both perp-linked and direct-linked profiles
      try {
        await axios.delete(
          `${API}/perpetrator-profiles/${profileId}/arrest/${incident.incident_id}`,
          { withCredentials: true }
        );
      } catch (e) { 
        // Ignore if no arrest exists
        console.log("No arrest to remove or already removed");
      }
      
      // Remove any ready_for_arrest records for this incident
      try {
        await axios.delete(
          `${API}/perpetrator-profiles/${profileId}/ready-for-arrest/${incident.incident_id}`,
          { withCredentials: true }
        );
      } catch (e) { 
        console.log("No ready for arrest to remove or already removed");
      }
      
      // Unlink from profile side
      await axios.delete(`${API}/perpetrator-profiles/${profileId}/link/${incidentId}`, { withCredentials: true });
      
      // Remove from linked_profiles
      const linkedProfiles = (incident.linked_profiles || []).filter(id => id !== profileId);
      
      // Also unlink from any perps that have this profile
      const updatedPerps = (incident.perp_descriptions || []).map(p => 
        p.linked_profile_id === profileId ? { ...p, linked_profile_id: null, linked_profile_name: null } : p
      );
      
      // Also update any arrests that were linked to this profile
      const updatedArrests = (incident.arrests || []).map(a => 
        a.linked_profile_id === profileId ? { ...a, linked_profile_id: null, linked_profile_name: null, linked_ready_for_arrest_id: null } : a
      );
      
      const updated = { ...incident, linked_profiles: linkedProfiles, perp_descriptions: updatedPerps, arrests: updatedArrests };
      setIncident(updated);
      handleSave(updated);
      toast.success("Profile unlinked");
    } catch (error) {
      toast.error("Failed to unlink profile");
    }
  };

  // Helper function to capture video frame as thumbnail
  const captureVideoThumbnail = (file) => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'auto';
      video.muted = true;
      video.playsInline = true;
      video.crossOrigin = 'anonymous';

      let hasResolved = false;
      const cleanup = () => {
        if (!hasResolved) {
          hasResolved = true;
          URL.revokeObjectURL(video.src);
        }
      };

      const timeout = setTimeout(() => {
        cleanup();
        resolve(null);
      }, 10000);

      video.onloadedmetadata = () => {
        video.currentTime = Math.min(1, video.duration * 0.1);
      };

      video.onseeked = () => {
        clearTimeout(timeout);
        try {
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth || 640;
          canvas.height = video.videoHeight || 360;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

          canvas.toBlob((blob) => {
            cleanup();
            resolve(blob);
          }, 'image/jpeg', 0.85);
        } catch (err) {
          cleanup();
          resolve(null);
        }
      };

      video.onerror = () => {
        clearTimeout(timeout);
        cleanup();
        resolve(null);
      };

      video.src = URL.createObjectURL(file);
      video.load();
    });
  };

  // Media upload handler
  const handleMediaUpload = async (e, showInMediaLibrary = false) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    const uploadedUrls = [];

    try {
      for (const file of files) {
        const isVideoFile = file.type.startsWith('video/');
        const formData = new FormData();
        formData.append("file", file);

        // Include incident_id and show_in_media_library in query params
        const params = new URLSearchParams();
        params.append("incident_id", incident.incident_id);
        params.append("show_in_media_library", showInMediaLibrary.toString());

        const response = await axios.post(`${API}/upload?${params}`, formData, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" }
        });
        uploadedUrls.push({ url: response.data.url, file_id: response.data.file_id });

        // If it's a video, capture and upload thumbnail
        if (isVideoFile && response.data.file_id) {
          console.log('[IncidentDetail] Video upload - attempting thumbnail capture for:', response.data.file_id);
          try {
            const thumbnailBlob = await captureVideoThumbnail(file);
            console.log('[IncidentDetail] Thumbnail blob:', thumbnailBlob ? `${thumbnailBlob.size} bytes` : 'null');
            if (thumbnailBlob) {
              const thumbFormData = new FormData();
              thumbFormData.append('file', thumbnailBlob, 'thumbnail.jpg');
              const thumbResponse = await axios.post(
                `${API}/upload/${response.data.file_id}/thumbnail`,
                thumbFormData,
                { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
              );
              console.log('[IncidentDetail] Thumbnail uploaded:', thumbResponse.data);
            } else {
              console.warn('[IncidentDetail] Thumbnail capture returned null');
            }
          } catch (thumbError) {
            console.error("[IncidentDetail] Thumbnail upload failed:", thumbError);
          }
        } else if (isVideoFile) {
          console.warn('[IncidentDetail] Video file but no file_id in response:', response.data);
        }
      }

      const updatedMediaFiles = [...(incident.media_files || []), ...uploadedUrls.map(u => u.url)];
      const updated = { ...incident, media_files: updatedMediaFiles };
      setIncident(updated);
      await handleSave(updated);
      toast.success(`${uploadedUrls.length} file(s) uploaded to R2`);
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to upload media");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  // Open Google Drive Picker → POST picked files' metadata to backend.
  // Backend stores entries as dicts inside `media_files`.
  const handleAttachFromDrive = async () => {
    if (!isDrivePickerConfigured()) {
      toast.error("Google Drive isn't configured yet — ask an admin to set the API key.");
      return;
    }
    try {
      await openDrivePicker({
        onPick: async (docs) => {
          if (!docs || docs.length === 0) return;
          const files = docs.map(d => ({
            file_id: d.id,
            name: d.name,
            mime_type: d.mimeType,
            thumbnail_url: d.thumbnailUrl || d.iconUrl,
            size: d.sizeBytes,
          }));
          try {
            const res = await axios.post(
              `${API}/incidents/${incident.incident_id}/drive-attachments`,
              { files },
              { withCredentials: true }
            );
            setIncident(res.data);
            toast.success(`Attached ${files.length} file(s) from Drive — make sure they're shared with your team.`);
          } catch (err) {
            console.error("Drive attach failed:", err);
            toast.error(err.response?.data?.detail || "Failed to attach Drive files");
          }
        },
      });
    } catch (err) {
      console.error("Drive picker error:", err);
      toast.error("Couldn't open Drive Picker");
    }
  };

  // Detach a Drive file from this incident (does NOT delete from Drive).
  const handleDetachDriveFile = async (fileId) => {
    try {
      const res = await axios.delete(
        `${API}/incidents/${incident.incident_id}/drive-attachments/${fileId}`,
        { withCredentials: true }
      );
      setIncident(res.data);
      toast.success("Drive attachment removed from this incident");
    } catch (err) {
      console.error("Drive detach failed:", err);
      toast.error("Failed to remove Drive attachment");
    }
  };

  // Toggle hide from media library for individual media
  const toggleHideMedia = async (fileId, hide) => {
    try {
      await axios.post(`${API}/uploads/hide-media-from-library`, {
        incident_id: incidentId,
        file_id: fileId,
        hide: hide
      }, { withCredentials: true });

      // Update local state
      if (hide) {
        setHiddenMediaIds(prev => [...prev, fileId]);
      } else {
        setHiddenMediaIds(prev => prev.filter(id => id !== fileId));
      }
      toast.success(hide ? "Hidden from Media Page" : "Visible on Media Page");
    } catch (error) {
      toast.error("Failed to update media visibility");
    }
  };

  // Download media directly to device
  const handleMediaDownload = async (url) => {
    if (isDownloading) return;
    setIsDownloading(true);

    try {
      const fileId = getFileIdFromUrl(url);

      // Build download URL - if already an API URL, use it; otherwise route through backend
      let downloadUrl;
      if (url && url.includes('/api/uploads/')) {
        downloadUrl = url.includes('?')
          ? `${url}&download=true`
          : `${url}?download=true`;
      } else {
        downloadUrl = `/api/uploads/${fileId}?download=true`;
      }

      const response = await fetch(downloadUrl, { credentials: 'include' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('text/html')) {
        throw new Error('File not found');
      }

      const blob = await response.blob();

      let ext = 'jpg';
      if (contentType.includes('png')) ext = 'png';
      else if (contentType.includes('gif')) ext = 'gif';
      else if (contentType.includes('mp4') || contentType.includes('video')) ext = 'mp4';
      else if (fileId?.includes('.')) ext = fileId.split('.').pop();

      const timestamp = Date.now();
      const filename = `CHSP_${timestamp}.${ext}`;

      const blobUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.style.display = 'none';
      anchor.href = blobUrl;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      setTimeout(() => {
        document.body.removeChild(anchor);
        URL.revokeObjectURL(blobUrl);
      }, 100);

      toast.success('Downloaded!');
    } catch (err) {
      console.error('Download failed:', err);
      toast.error('Download failed');
    } finally {
      setIsDownloading(false);
    }
  };

  // Open media in lightbox
  const openLightbox = (url, index) => {
    setLightboxMedia(url);
    setLightboxMediaIndex(index);
  };

  // Remove media file
  const removeMediaFile = async (url) => {
    const updatedMediaFiles = (incident.media_files || []).filter(f => f !== url);
    const updated = { ...incident, media_files: updatedMediaFiles };
    setIncident(updated);
    await handleSave(updated);
    toast.success("Media removed");
  };

  // Check if file is image or video (supports both file paths and base64 data URLs)
  const isImage = (url) => {
    if (!url) return false;
    if (url.startsWith('data:image/')) return true;
    return /\.(jpg|jpeg|png|gif|webp)$/i.test(url);
  };
  const isVideo = (url) => {
    if (!url) return false;
    if (url.startsWith('data:video/')) return true;
    return /\.(mp4|mov|avi|webm)$/i.test(url);
  };
  
  // Get file ID from URL
  const getFileIdFromUrl = (url) => {
    if (!url) return null;
    const parts = url.split('/');
    return parts[parts.length - 1];
  };
  
  // Get the proper URL for media display (handles both base64 and file paths)
  const getMediaUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('data:')) return url; // base64 data URL, use directly
    return `${BACKEND_URL}${url}`; // file path, prepend backend URL
  };

  // Format live call notes for export
  const formatLiveCallNotes = (notes) => {
    if (!notes) return 'N/A';
    
    const lines = notes.split('\n');
    const formatted = [];
    
    for (const line of lines) {
      if (line.startsWith('[') && line.includes(']')) {
        // This is an update line - format it nicely
        const hasUnits84 = line.includes('[UNITS 84]');
        const hasUnits98 = line.includes('[UNITS 98]');
        const has1085 = line.includes('[10-85]');
        const has1013 = line.includes('[10-13]');
        const hasNoFurther = line.includes('[NO FURTHER]');
        const hasReactivated = line.includes('[REACTIVATED]');
        
        // Extract timestamp
        const tsMatch = line.match(/^\[([^\]]+)\]/);
        const timestamp = tsMatch ? tsMatch[1] : '';
        
        // Clean up the rest
        let rest = line
          .replace(/^\[[^\]]+\]\s*/, '')
          .replace(/\s*\|\s*\[UNITS 84\]/g, '')
          .replace(/\s*\|\s*\[UNITS 98\]/g, '')
          .replace(/\s*\|\s*\[10-85\]/g, '')
          .replace(/\s*\|\s*\[10-13\]/g, '')
          .replace(/\s*\|\s*\[NO FURTHER\]/g, '')
          .replace(/\s*\|\s*\[REACTIVATED\]/g, '')
          .replace(/\[UNITS 84\]/g, '')
          .replace(/\[UNITS 98\]/g, '')
          .replace(/\[10-85\]/g, '')
          .replace(/\[10-13\]/g, '')
          .replace(/\[NO FURTHER\]/g, '')
          .replace(/\[REACTIVATED\]/g, '')
          .replace(/\s*\|\s*$/g, '')
          .replace(/^\s*\|\s*/g, '')
          .trim();
        
        // Build flags string
        const flags = [];
        if (hasUnits84) flags.push('UNITS 84');
        if (hasUnits98) flags.push('UNITS 98');
        if (has1085) flags.push('10-85');
        if (has1013) flags.push('10-13');
        if (hasNoFurther) flags.push('NO FURTHER');
        if (hasReactivated) flags.push('REACTIVATED');
        
        let updateLine = `  [${timestamp}]`;
        if (rest) updateLine += ` ${rest}`;
        if (flags.length > 0) updateLine += `\n           FLAGS: ${flags.join(', ')}`;
        
        formatted.push(updateLine);
      } else if (line.startsWith('Updates:')) {
        formatted.push('\nUPDATES:');
        formatted.push('-'.repeat(40));
      } else if (line.includes(':')) {
        // Key-value line
        const colonIdx = line.indexOf(':');
        const key = line.substring(0, colonIdx).trim();
        const value = line.substring(colonIdx + 1).trim();
        formatted.push(`${key.toUpperCase().padEnd(20)}: ${value}`);
      } else if (line.trim()) {
        formatted.push(line);
      }
    }
    
    return formatted.join('\n');
  };

  // Export incident as PDF
  const exportIncidentAsPdf = async () => {
    setGeneratingPdf(true);
    try {
      console.log("Starting PDF generation...");
      const doc = new jsPDF();
      const i = incident;
      let y = 20;
      const margin = 15;
      const pageWidth = doc.internal.pageSize.getWidth();
      const maxWidth = pageWidth - margin * 2;
      
      // Helper to format date in NY timezone
      const formatNYTime = (dateStr) => {
        if (!dateStr) return 'N/A';
        try {
          return new Date(dateStr).toLocaleString('en-US', { 
            timeZone: 'America/New_York',
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', hour12: true
          });
        } catch (e) { return String(dateStr); }
      };

      // Helper to check page overflow
      const checkPage = (needed = 10) => {
        if (y > 280 - needed) { doc.addPage(); y = 20; }
      };

      // Helper to add a line of text
      const addLine = (text, bold = false, indent = 0) => {
        checkPage();
        if (bold) doc.setFont('helvetica', 'bold');
        else doc.setFont('helvetica', 'normal');
        const lines = doc.splitTextToSize(String(text || ''), maxWidth - indent);
        doc.text(lines, margin + indent, y);
        y += lines.length * 5 + 2;
      };

      // Helper to add field
      const addField = (label, value) => {
        if (!value && value !== 0 && value !== false) return;
        checkPage();
        doc.setFont('helvetica', 'bold');
        doc.text(label + ':  ', margin, y);  // Added extra space after colon
        doc.setFont('helvetica', 'normal');
        const labelWidth = doc.getTextWidth(label + ':  ');  // Match spacing
        const valLines = doc.splitTextToSize(String(value), maxWidth - labelWidth - 5);
        doc.text(valLines, margin + labelWidth, y);
        y += valLines.length * 5 + 2;
      };

      // Helper to add section header
      const addSection = (title) => {
        checkPage(20);
        y += 8; // More space before section
        doc.setFillColor(30, 41, 59);
        doc.rect(margin, y - 5, maxWidth, 8, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.text(title, margin + 2, y);
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(10);
        y += 10; // More space after header to prevent text overlap
      };

      // Helper for yes/no
      const yesNo = (val) => val ? 'YES' : 'No';

      // Get the user who is generating the report
      const generatedBy = user?.name || user?.email || 'Unknown User';

      // ===== TITLE =====
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('INCIDENT REPORT', pageWidth / 2, y, { align: 'center' });
      y += 8;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated: ${formatNYTime(new Date().toISOString())} by ${generatedBy}`, pageWidth / 2, y, { align: 'center' });
      y += 12;

      // ===== INCIDENT INFO =====
      addSection('INCIDENT INFORMATION');
      addField('Incident ID', i.incident_id);
      addField('Headline', i.headline);
      addField('Status', i.status?.toUpperCase());
      addField('Priority', i.priority?.toUpperCase());
      if (i.confirmation_status) addField('Confirmation', i.confirmation_status.toUpperCase());
      addField('Date/Time', formatNYTime(i.date_time) + ' (NY)');
      addField('Address', i.address);
      if (i.cross_streets) addField('Cross Streets', i.cross_streets);
      if (i.location_notes) addField('Location Notes', i.location_notes);
      if (i.direction_of_flight) addField('Direction of Flight', i.direction_of_flight);
      if (i.callback_numbers?.length > 0) {
        addField('Callback Numbers', i.callback_numbers.map(cb => {
          let str = cb.number;
          if (cb.label) str += ` (${cb.label})`;
          if (cb.caller_type) str += ` [${cb.caller_type === 'cv' ? 'CV' : '3rd Party'}]`;
          return str;
        }).join(', '));
      }
      addField('Incident Type', i.incident_type);
      addField('Secondary Type', i.secondary_incident_type || 'None');
      addField('Report Type', i.report_type);

      // ===== UNITS =====
      addSection('UNITS & PERSONNEL');
      addField('Units Responded', [...(i.units_responded || [])].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })).join(', ') || 'None');
      addField('Units Investigating', [...(i.units_investigating || [])].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })).join(', ') || 'None');
      addField('Dispatcher', (i.dispatcher_units || []).join(', ') || 'None');
      addField('Assigned Detective', i.assigned_detective);

      // ===== FLAGS =====
      addSection('FLAGS & INDICATORS');
      addField('10-85 Transmitted', yesNo(i.transmitted_10_85));
      addField('10-13 Transmitted', yesNo(i.transmitted_10_13));
      addField('PR Flagged', yesNo(i.is_pr) + (i.pr_notes ? ` - ${i.pr_notes}` : ''));
      addField('Arrest Made', yesNo(i.is_arrest));
      addField('Hotline Call', yesNo(i.is_hotline_call));
      addField('Hate Crime', yesNo(i.is_hate_crime) + (i.hate_crime_nature ? ` - ${i.hate_crime_nature}` : ''));
      addField('HCTF Notified', yesNo(i.hctf_notified));
      addField('Weapon Displayed/Used', yesNo(i.weapon_displayed) + (i.weapon_notes ? ` - ${i.weapon_notes}` : ''));
      addField('EMS Required', yesNo(i.ems_required));
      addField('Injuries Reported', yesNo(i.injuries_reported));
      addField('Footage Requested', yesNo(i.footage_requested));
      addField('Footage Obtained', yesNo(i.footage_obtained) + (i.footage_locations ? ` - ${i.footage_locations}` : ''));
      
      // Police Follow-up (separate from regular follow-up)
      addField('Police Follow-up Required', yesNo(i.police_followup_required) + (i.police_followup_notes ? ` - ${i.police_followup_notes}` : ''));
      
      // Police Report Filed
      addField('Police Report Filed', yesNo(i.police_report_filed) + (i.police_report_notes ? ` - ${i.police_report_notes}` : ''));
      
      // Police Action/Response
      if (i.police_action_notes) {
        addField('Police Action/Response', i.police_action_notes);
      }
      
      // Regular Follow-up (separate from police follow-up)
      addField('Needs Follow-up', yesNo(i.needs_followup) + (i.followup_notes ? ` - ${i.followup_notes}` : ''));

      // ===== VICTIMS =====
      if (i.victims?.length > 0) {
        addSection(`VICTIMS (${i.victims.length})`);
        i.victims.forEach((v, idx) => {
          checkPage(30);
          addLine(`Victim ${idx + 1}: ${v.name || 'Unknown'}`, true);
          if (v.email) addField('  Email', v.email);
          if (v.phone1) addField('  Phone 1', v.phone1);
          if (v.phone2) addField('  Phone 2', v.phone2);
          if (v.address) addField('  Address', v.address);
          if (v.statement) addField('  Statement', v.statement);
          if (v.property_stolen) addField('  Property Stolen', v.property_stolen);
          if (v.has_injuries) {
            addField('  Injuries', 'YES' + (v.injuries_description ? ` - ${v.injuries_description}` : ''));
            if (v.medical_treatment) addField('  Medical Treatment', v.medical_treatment);
          }
          addField('  On Police Report', yesNo(v.on_police_report));
          addField('  Can Recognize Perp', yesNo(v.can_recognize_perp));
          addField('  Interviewed on Scene', yesNo(v.interviewed_on_scene));
          if (v.is_minor_or_elderly) {
            addField('  Minor/Elderly', 'YES');
            if (v.guardian_name) addField('  Guardian Name', v.guardian_name);
            if (v.guardian_email) addField('  Guardian Email', v.guardian_email);
            if (v.guardian_phone1) addField('  Guardian Phone', v.guardian_phone1);
            if (v.guardian_address) addField('  Guardian Address', v.guardian_address);
          }
          if (v.direction_of_flight_prior) addField('  Dir of Flight Prior', v.direction_of_flight_prior);
          if (v.direction_of_flight_post) addField('  Dir of Flight Post', v.direction_of_flight_post);
          y += 3;
        });
      }

      // ===== WITNESSES =====
      if (i.witnesses?.length > 0) {
        addSection(`WITNESSES (${i.witnesses.length})`);
        i.witnesses.forEach((w, idx) => {
          checkPage(20);
          addLine(`Witness ${idx + 1}: ${w.name || 'Unknown'}`, true);
           if (w.contact_info) addField('  Contact', w.contact_info);
          if (w.location) addField('  Location', w.location);
          if (w.statement) addField('  Statement', w.statement);
          addField('  On Police Report', yesNo(w.on_police_report));
          addField('  Already Interviewed', yesNo(w.already_interviewed));
          addField('  Interviewed on Scene', yesNo(w.interviewed_on_scene));
          y += 3;
        });
      }

      // ===== PERPETRATORS =====
      if (i.perp_descriptions?.length > 0) {
        addSection(`PERPETRATOR DESCRIPTIONS (${i.perp_descriptions.length})`);
        i.perp_descriptions.forEach((p, idx) => {
          checkPage(40);
          addLine(`Perpetrator ${idx + 1}${p.was_arrested ? ' [ARRESTED]' : ''}`, true);
          // Name and Address (if known)
          if (p.name) addField('  Name', p.name);
          if (p.address) addField('  Address', p.address);
          // Physical
          if (p.sex) addField('  Sex', p.sex);
          if (p.race) addField('  Race', p.race);
          if (p.age) addField('  Age', p.age);
          if (p.height) addField('  Height', p.height);
          if (p.build) addField('  Build', p.build);
          if (p.complexion) addField('  Complexion', p.complexion);
          if (p.appearance) addField('  Appearance', p.appearance);
          // Clothing
          if (p.hat_color_style) addField('  Hat', p.hat_color_style);
          if (p.hair_color_cut) addField('  Hair', p.hair_color_cut);
          if (p.facial_hair) addField('  Facial Hair', p.facial_hair);
          if (p.glasses) addField('  Glasses', p.glasses);
          if (p.shirt) addField('  Shirt', p.shirt);
          if (p.jacket_coat) addField('  Jacket/Coat', p.jacket_coat);
          if (p.pants) addField('  Pants', p.pants);
          if (p.shoes) addField('  Shoes', p.shoes);
          if (p.visible_marks) addField('  Marks/Tattoos', p.visible_marks);
          // Movement
          if (p.direction_of_flight) addField('  Dir of Flight', p.direction_of_flight);
          if (p.direction_of_approach) addField('  Dir of Approach', p.direction_of_approach);
          // Vehicle
          if (p.vehicle_description) addField('  Vehicle', p.vehicle_description);
          if (p.license_plate) addField('  License Plate', p.license_plate);
          // Flags
          if (p.has_weapon) addField('  Has Weapon', 'YES');
          if (p.victim_can_recognize) addField('  Victim Can Recognize', 'YES');
          if (p.identifiable_via_camera) addField('  Identifiable via Camera', 'YES');
          if (p.has_warrant) addField('  Has Warrant', 'YES');
          if (p.ready_for_identification || p.identified_for_arrest) addField('  Ready for ID', 'YES');
          if (p.identification_methods?.length > 0) addField('  ID Methods', p.identification_methods.join(', '));
          if (p.report_filed) addField('  Report Filed', 'YES');
          // Linked Profile
          if (p.linked_profile_id || p.linked_profile_name) {
            addField('  Linked Profile', p.linked_profile_name || p.linked_profile_id);
          }
          if (p.notes) addField('  Notes', p.notes);
          y += 3;
        });
      }

      // ===== LINKED PROFILES =====
      if (i.linked_profiles?.length > 0) {
        addSection(`LINKED PERPETRATOR PROFILES (${i.linked_profiles.length})`);
        i.linked_profiles.forEach((profileId, idx) => {
          checkPage(10);
          // Try to find profile name from perp descriptions
          const linkedPerp = i.perp_descriptions?.find(p => p.linked_profile_id === profileId);
          const profileName = linkedPerp?.linked_profile_name || profileId;
          addLine(`${idx + 1}. ${profileName}`, false, 5);
        });
      }

      // ===== READY FOR ARREST =====
      // Check perp descriptions for identified_for_arrest or ready_for_identification flag
      const readyPerps = (i.perp_descriptions || []).filter(p => p.identified_for_arrest || p.ready_for_identification);
      if (readyPerps.length > 0 || i.police_report_filed) {
        addSection('READY FOR ARREST');
        if (i.police_report_filed) {
          addField('Police Report Filed', 'YES');
          if (i.police_report_notes) addField('Report Notes', i.police_report_notes);
        }
        readyPerps.forEach((p, idx) => {
          checkPage(15);
          const perpInfo = [p.name, p.sex, p.race, p.age ? `age ${p.age}` : ''].filter(Boolean).join(', ') || 'Unknown';
          addLine(`Perp ${idx + 1}: ${perpInfo}`, false, 5);
          if (p.linked_profile_name) addField('    Linked Profile', p.linked_profile_name);
          if (p.identification_methods?.length > 0) addField('    ID Methods', p.identification_methods.join(', '));
          if (p.report_filed) addField('    Report Filed', 'YES');
        });
      }

      // ===== ARRESTS =====
      if (i.arrests?.length > 0) {
        addSection(`ARRESTS (${i.arrests.length})`);
        i.arrests.forEach((a, idx) => {
          checkPage(20);
          const linkedPerp = i.perp_descriptions?.find(p => p.perp_id === a.linked_perp_id);
          addLine(`Arrest ${idx + 1}`, true);
          
          // Show arrestee name if available
          if (a.name) {
            addField('  Name', a.name);
          }
          
          // Show linked perp info
          if (linkedPerp) {
            const perpDesc = [linkedPerp.name, linkedPerp.sex, linkedPerp.race, linkedPerp.age ? `age ${linkedPerp.age}` : ''].filter(Boolean).join(', ');
            if (perpDesc) addField('  Linked Perp', perpDesc);
            if (linkedPerp.linked_profile_name) {
              addField('  Linked Profile', linkedPerp.linked_profile_name);
            }
          }
          
          // Show if connected to ready for arrest
          if (a.linked_ready_for_arrest_id || (linkedPerp && linkedPerp.ready_for_identification)) {
            addField('  From Ready for Arrest', 'YES');
          }
          
          addField('  Was Live Arrest', yesNo(a.was_live));
          if (a.was_live) addField('  PD Only', yesNo(a.perp_stopped_by_pd_only));
          addField('  Outstanding Perps', yesNo(a.outstanding_perpetrators));
          if (a.identification_by?.length > 0) {
            addField('  Identified By', a.identification_by.join(', '));
          }
          if (a.charges) {
            const chargesStr = Array.isArray(a.charges) ? a.charges.join(', ') : a.charges;
            if (chargesStr) addField('  Charges', chargesStr);
          }
          if (a.notes) addField('  Notes', a.notes);
          y += 3;
        });
      }

      // ===== NOTES =====
      if (i.notes?.length > 0) {
        addSection(`NOTES (${i.notes.length})`);
        i.notes.forEach((note, idx) => {
          checkPage(10);
          // Handle both string notes and object notes
          if (typeof note === 'string') {
            addLine(`${idx + 1}. ${note}`, false, 5);
          } else if (typeof note === 'object' && note !== null) {
            const noteText = note.text || note.note || note.content || '';
            const noteBy = note.created_by_name || note.user || '';
            const noteAt = note.created_at ? formatNYTime(note.created_at) : '';
            let displayNote = noteText;
            if (noteBy || noteAt) {
              displayNote += ` (${[noteBy, noteAt].filter(Boolean).join(' - ')})`;
            }
            addLine(`${idx + 1}. ${displayNote}`, false, 5);
          }
        });
      }

      // ===== LIVE CALL INFO =====
      if (i.live_call_description || i.live_call_notes) {
        addSection('LIVE CALL INFORMATION');
        if (i.live_call_description) addField('Description', i.live_call_description);
        if (i.live_call_notes) {
          addLine('Live Call Summary:', true);
          const summaryLines = (i.live_call_notes || '').split('\n');
          summaryLines.forEach(line => {
            if (line.trim()) addLine(line, false, 5);
          });
        }
      }

      // ===== CALL FINAL =====
      if (i.call_final) {
        addSection('CALL FINAL');
        addLine(i.call_final);
      }

      // ===== READ RECEIPTS =====
      if (i.read_receipts && i.read_receipts.length > 0) {
        addSection('READ RECEIPTS');
        i.read_receipts.forEach((receipt, idx) => {
          addField(`Reader ${idx + 1}`, `${receipt.user_name} - ${formatNYTime(receipt.read_at)}`);
        });
      }

      // ===== AUDIT =====
      addSection('AUDIT INFORMATION');
      addField('Created', formatNYTime(i.created_at) + ' (NY)');
      addField('Created By', i.created_by_name || 'System');
      addField('Incident ID', i.incident_id);
      if (i.from_live_call_id) addField('From Live Call', i.from_live_call_id);
      addField('Report Generated By', generatedBy);
      addField('Report Generated At', formatNYTime(new Date().toISOString()) + ' (NY)');

      console.log("PDF generation complete, adding watermarks...");
      
      // Add watermark to all pages
      const totalPages = doc.internal.getNumberOfPages();
      const generatedAt = formatNYTime(new Date().toISOString());
      
      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        doc.setPage(pageNum);
        const pageHeight = doc.internal.pageSize.getHeight();
        
        // Add footer with generation info - includes username
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(100, 100, 100); // Medium gray - more visible
        doc.text(`CHSP IMS - Generated by: ${generatedBy}`, margin, pageHeight - 15);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.text(`${generatedAt} | User: ${generatedBy}`, margin, pageHeight - 10);
        doc.text(`Page ${pageNum} of ${totalPages}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
        
        // Add multiple diagonal watermarks across the page - MORE FREQUENT
        doc.setFontSize(40);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(200, 200, 200);
        doc.saveGraphicsState();
        doc.setGState(new doc.GState({ opacity: 0.12 }));
        
        // Watermark grid - 3 rows x 2 columns for more coverage
        const watermarkText = `CHSP - ${generatedBy}`;
        const positions = [
          { x: pageWidth * 0.25, y: pageHeight * 0.25 },
          { x: pageWidth * 0.75, y: pageHeight * 0.25 },
          { x: pageWidth * 0.25, y: pageHeight * 0.5 },
          { x: pageWidth * 0.75, y: pageHeight * 0.5 },
          { x: pageWidth * 0.25, y: pageHeight * 0.75 },
          { x: pageWidth * 0.75, y: pageHeight * 0.75 },
        ];
        
        positions.forEach(pos => {
          doc.text(watermarkText, pos.x, pos.y, { 
            align: 'center', 
            angle: 45 
          });
        });
        doc.restoreGraphicsState();
        
        // Add header watermark bar on EVERY page with username
        doc.setFillColor(30, 58, 138); // Dark blue
        doc.rect(0, 0, pageWidth, 8, 'F');
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(255, 255, 255);
        doc.text(`CHSP INCIDENT MANAGEMENT SYSTEM | ${generatedBy} | Page ${pageNum}/${totalPages}`, pageWidth / 2, 5.5, { align: 'center' });
        
        // Reset text color for any remaining content
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');
      }

      console.log("PDF generation complete, creating output...");
      
      // Generate PDF filename
      const filename = `incident_${i.incident_id}_${format(new Date(), 'yyyyMMdd')}.pdf`;
      
      // Log the document pages to verify content was added
      console.log("Total pages in PDF:", totalPages);
      
      // Try direct save first (most reliable)
      try {
        doc.save(filename);
        toast.success("PDF downloaded as " + filename);
        return;
      } catch (saveErr) {
        console.log("Direct save failed:", saveErr);
      }
      
      // Fallback: use data URI to open in new tab
      try {
        const pdfDataUri = doc.output('datauristring');
        console.log("Data URI length:", pdfDataUri.length);
        
        const newWindow = window.open('', '_blank');
        if (newWindow) {
          newWindow.document.write(`
            <html>
              <head><title>${filename.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')}</title></head>
              <body style="margin:0;padding:0;">
                <embed width="100%" height="100%" src="${pdfDataUri}" type="application/pdf">
              </body>
            </html>
          `);
          newWindow.document.close();
          toast.success("PDF opened in new tab");
        } else {
          toast.error("Popup blocked - please allow popups");
        }
      } catch (uriErr) {
        console.error("Data URI failed:", uriErr);
        toast.error("PDF generation failed");
      }
      
    } catch (err) {
      console.error("PDF generation error:", err);
      toast.error("Failed to generate PDF: " + (err.message || 'Unknown error'));
    } finally {
      setGeneratingPdf(false);
    }
  };

  // Legacy export for text preview
  const exportIncident = () => {
    exportIncidentAsPdf();
  };

  const getStatusClass = (status) => `status-badge status-${status}`;
  const getPriorityClass = (priority) => `priority-${priority}`;

  // Format datetime for input
  const formatDateTimeForInput = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      return format(date, "yyyy-MM-dd'T'HH:mm");
    } catch {
      return "";
    }
  };

  const hasIncidentsAccess = user?.role === "admin" || user?.incidents_access;

  if (!hasIncidentsAccess) {
    return (
      <div className="flex items-center justify-center h-full min-h-[60vh]">
        <Card>
          <CardContent className="pt-6 text-center">
            <Shield className="h-12 w-12 mx-auto mb-4 text-slate-500" />
            <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
            <p className="text-slate-400 text-sm">
              You do not have permission to view incidents.
              Contact an administrator to request access.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!incident) return null;

  return (
    <div className="space-y-4" data-testid="incident-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
          <Button variant="ghost" size="sm" onClick={() => {
            const back = sessionStorage.getItem("incidents_return_url");
            navigate(back || "/incidents");
          }} data-testid="back-btn" className="flex-shrink-0">
            <ArrowLeft className="w-4 h-4 sm:mr-1" />
            <span className="hidden sm:inline">Back</span>
          </Button>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
              <span className={getStatusClass(incident.status)}>
                {incident.status?.replace("_", " ")}
              </span>
              <Badge className={getPriorityClass(incident.priority)}>
                {incident.priority}
              </Badge>
              {incident.is_pr && <Badge variant="outline" className="text-purple-400 border-purple-500">PR</Badge>}
              {(incident.secondary_call_type || incident.secondary_incident_type) && (
                <Badge variant="outline" className="text-slate-400 border-slate-600">{incident.secondary_call_type || incident.secondary_incident_type}</Badge>
              )}
            </div>
            <h1 className="text-lg sm:text-2xl font-bold mt-1 break-words">{incident.headline}</h1>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {navList.length > 1 && (
            <div className="flex items-center gap-0.5 mr-1 border-r border-slate-300 dark:border-slate-700 pr-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                disabled={!prevIncidentId}
                onClick={() => prevIncidentId && navigate(`/incidents/${prevIncidentId}`)}
                title="Previous incident"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-xs text-slate-500 min-w-[3rem] text-center">{navIndex + 1}/{navList.length}</span>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                disabled={!nextIncidentId}
                onClick={() => nextIncidentId && navigate(`/incidents/${nextIncidentId}`)}
                title="Next incident"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
          <Button variant="outline" size="sm" onClick={exportIncidentAsPdf} disabled={generatingPdf} className="flex" data-testid="export-btn">
            {generatingPdf ? (
              <>
                <div className="w-4 h-4 animate-spin rounded-full border-2 border-slate-400 border-t-transparent" />
              </>
            ) : (
              <>
                <FileText className="w-4 h-4" />
              </>
            )}
          </Button>
          <Button 
            variant="outline"
            size="sm"
            onClick={handleUndo} 
            disabled={!canUndo || saving} 
            title="Undo last save"
          >
            <Undo2 className="w-4 h-4" />
          </Button>
          <Button size="sm" onClick={() => editingArrestId ? saveArrestEdit(editingArrestId) : handleSave()} disabled={saving} className="btn-primary" data-testid="save-btn">
            <Save className="w-4 h-4 sm:mr-1" />
            <span className="hidden sm:inline">{saving ? "Saving..." : "Save"}</span>
          </Button>
        </div>
      </div>

      {/* Audit Trail */}
      <Card className="bg-slate-950 border-slate-800">
        <CardContent className="p-3 text-sm">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
              <div className="flex items-center gap-2 text-slate-400">
                <Clock className="w-4 h-4 flex-shrink-0" />
                <span className="font-mono text-xs sm:text-sm">{format(new Date(incident.created_at), "MM/dd/yyyy HH:mm")}</span>
              </div>
              <div className="flex items-center gap-2 text-slate-400">
                <User className="w-4 h-4 flex-shrink-0" />
                <span className="text-xs sm:text-sm truncate">{incident.created_by_name || "System"}</span>
              </div>
            </div>
            <span className="text-[10px] sm:text-xs font-mono text-slate-500">ID: {incident.incident_id}</span>
          </div>
        </CardContent>
      </Card>

      {/* Main Content - Full Width Tabs */}
      <Tabs defaultValue="summary" className="w-full">
        <TabsList className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-12 gap-1 w-full bg-slate-900 h-auto p-1">
          <TabsTrigger value="summary" className="text-[10px] sm:text-xs px-1.5 py-1.5">Summary</TabsTrigger>
          <TabsTrigger value="callinfo" className="text-[10px] sm:text-xs px-1.5 py-1.5">Call Info</TabsTrigger>
          <TabsTrigger value="cvcb" className="text-[10px] sm:text-xs px-1.5 py-1.5">CV/CB</TabsTrigger>
          <TabsTrigger value="perps" className="text-[10px] sm:text-xs px-1.5 py-1.5">Perps</TabsTrigger>
          <TabsTrigger value="profiles" className="text-[10px] sm:text-xs px-1.5 py-1.5">Profiles</TabsTrigger>
          <TabsTrigger value="arrests" className="text-[10px] sm:text-xs px-1.5 py-1.5">Arrests</TabsTrigger>
          <TabsTrigger value="ready" className="text-[10px] sm:text-xs px-1.5 py-1.5">Ready</TabsTrigger>
          <TabsTrigger value="units" className="text-[10px] sm:text-xs px-1.5 py-1.5">Units</TabsTrigger>
          <TabsTrigger value="updates" className="text-[10px] sm:text-xs px-1.5 py-1.5">Updates</TabsTrigger>
          <TabsTrigger value="media" className="text-[10px] sm:text-xs px-1.5 py-1.5">Media</TabsTrigger>
          <TabsTrigger value="notes" className="text-[10px] sm:text-xs px-1.5 py-1.5">Notes</TabsTrigger>
          <TabsTrigger value="livecall" className="text-[10px] sm:text-xs px-1.5 py-1.5">Live Call</TabsTrigger>
        </TabsList>

        {/* Summary Tab — read-only at-a-glance overview */}
        <TabsContent value="summary" className="mt-4">
          {(() => {
            // Pull updates from BOTH the source live call (when this incident
            // came from one) AND the incident's own assignment history, so an
            // incident created directly still surfaces post-creation activity.
            const liveUpdates = sourceLiveCall?.updates || [];
            const assignmentHistory = (incident.unit_assignment_history || []).map(h => ({
              update_id: h.id || `hist_${h.timestamp || ""}_${h.unit || ""}`,
              timestamp: h.timestamp,
              updated_by: h.by,
              update_text: h.unit ? `${h.unit} ${h.action || "updated"}` : (h.action || "Updated"),
            }));
            const updates = [...liveUpdates, ...assignmentHistory];
            // The auto-log scheduler appends a "System / Auto-logged after 1 hour…"
            // entry as the very last update, so the "last update" preview always
            // pointed at that synthetic event. Show the most recent real update
            // by filtering it out when picking the latest.
            const isAutoLogUpdate = (u) => {
              if (!u) return false;
              const note = (u.general_notes || u.update_text || "").toLowerCase();
              const by = (u.updated_by || "").toLowerCase();
              if (note.startsWith("auto-logged")) return true;
              if (by === "system" && note.includes("auto-log")) return true;
              return false;
            };
            const sortedDesc = [...updates].sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
            const latestUpdate = sortedDesc.find(u => !isAutoLogUpdate(u)) || sortedDesc[0] || null;
            const perps = incident.perp_descriptions || [];
            const arrestedCount = perps.filter(p => p.is_arrested).length;
            const holdingCount = perps.filter(p => p.is_holding).length;
            const detainedCount = perps.filter(p => p.is_detained).length;
            const readyCount = perps.filter(p => p.ready_for_arrest).length;
            // ARREST / ARREST VIA — backend enriches both flags on the
            // single-incident endpoint (mirrors /incidents list logic and
            // the Profiles-page directionality), so the hero just trusts
            // them. is_arrest_via is set when this incident's
            // ready_for_arrest was used in another incident's arrest.
            const hasArrest = incident.is_arrest === true;
            const hasArrestVia = incident.is_arrest_via === true;
            const mediaCount = (incident.media_files || []).length;
            const callbackNumbers = incident.callback_numbers || [];
            const hasPrimaryStandalone = incident.callback_phone && !callbackNumbers.some(c => c.number === incident.callback_phone);
            const totalCallbacks = callbackNumbers.length + (hasPrimaryStandalone ? 1 : 0);
            // Units list — union of live-call assignments, incident.units_responded
            // (the field that's actually populated when units are added on a
            // direct-created incident) and the legacy incident.assigned_units.
            const units = [...new Set([
              ...((sourceLiveCall?.assigned_units) || []),
              ...((incident.units_responded) || []),
              ...((incident.assigned_units) || []),
            ])];
            const addressForMap = incident.address || "";
            return (
              <div className="space-y-4">
                {/* Hero / Overview */}
                <Card className="bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 border-slate-700">
                  <CardContent className="p-4">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <Badge className={`${getPriorityClass(incident.priority)} text-xs px-2 py-0.5`}>{incident.priority?.toUpperCase()}</Badge>
                      <span className={`${getStatusClass(incident.status)} text-xs`}>{incident.status?.replace("_", " ")}</span>
                      {incident.incident_type && (
                        <Badge variant="outline" className="text-xs border-blue-500 text-blue-300">{incident.incident_type}</Badge>
                      )}
                      {(incident.secondary_call_type || incident.secondary_incident_type) && (
                        <Badge variant="outline" className="text-xs border-slate-600 text-slate-400">+ {incident.secondary_call_type || incident.secondary_incident_type}</Badge>
                      )}
                      {incident.confirmation_status === "confirmed" && (
                        <Badge className="text-xs bg-emerald-700 text-white">CONFIRMED</Badge>
                      )}
                      {incident.confirmation_status === "unconfirmed" && (
                        <Badge className="text-xs bg-amber-700 text-white">UNCONFIRMED</Badge>
                      )}
                      {incident.report_type === "reported_live" && (
                        <Badge className="text-xs bg-rose-700 text-white">REPORTED LIVE</Badge>
                      )}
                      {incident.report_type === "reported_in_past" && (
                        <Badge variant="outline" className="text-xs border-slate-600 text-slate-400">REPORTED IN PAST</Badge>
                      )}
                      {incident.is_pr && <Badge variant="outline" className="text-xs text-purple-400 border-purple-500">PR</Badge>}
                      {hasArrest && (
                        <Badge className="text-xs bg-emerald-700 text-white">ARREST</Badge>
                      )}
                      {hasArrestVia && (
                        <Badge className="text-xs bg-emerald-800 text-white" title="This incident's Ready-for-Arrest was used in another incident's arrest">ARREST VIA</Badge>
                      )}
                    </div>
                    <h2 className="text-xl sm:text-2xl font-bold text-slate-100 break-words">{incident.headline || "Untitled incident"}</h2>
                    <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                      <div>
                        <p className="text-slate-500 uppercase tracking-wide">Created</p>
                        <p className="text-slate-200 font-mono mt-0.5">{format(new Date(incident.created_at), "MM/dd/yyyy h:mm a")}</p>
                        <p className="text-slate-500 text-[10px] mt-0.5">by {incident.created_by_name || "System"}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 uppercase tracking-wide">Incident time</p>
                        <p className="text-slate-200 font-mono mt-0.5">{incident.date_time ? format(new Date(incident.date_time), "MM/dd/yyyy h:mm a") : "—"}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 uppercase tracking-wide">ID</p>
                        <p className="text-slate-200 font-mono mt-0.5 truncate">{incident.incident_id}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 uppercase tracking-wide">Updates</p>
                        <p className="text-slate-200 font-mono mt-0.5">{updates.length}{latestUpdate ? ` · last ${format(new Date(latestUpdate.timestamp), "h:mm a")}` : ""}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Two-column body */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Location */}
                  <Card className="bg-slate-900 border-slate-800">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium flex items-center gap-2 text-slate-300">
                        <MapPin className="w-4 h-4 text-amber-500" /> Location
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 pt-0">
                      <div className="p-2.5 bg-slate-800/60 rounded border border-slate-700/50">
                        <Badge variant="outline" className="text-[10px] text-amber-400 border-amber-600 mb-1">Original</Badge>
                        {addressForMap ? (
                          <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressForMap + ", Brooklyn, NY")}`} target="_blank" rel="noopener noreferrer" className="block text-sm text-slate-100 font-medium underline-offset-2 hover:underline break-words">📍 {addressForMap}</a>
                        ) : (
                          <p className="text-sm text-slate-500 italic">No address</p>
                        )}
                        {incident.location_notes && <p className="text-xs text-slate-400 italic mt-1">{incident.location_notes}</p>}
                        {incident.cross_streets && <p className="text-xs text-emerald-400 mt-1">Cross: {incident.cross_streets}</p>}
                      </div>
                      {(incident.additional_locations || []).map((loc, idx) => (
                        <div key={idx} className="p-2.5 bg-slate-800/60 rounded border border-blue-800/40">
                          <Badge variant="outline" className="text-[10px] text-blue-400 border-blue-600 mb-1">{loc.title || `Additional #${idx + 1}`}</Badge>
                          {loc.location ? (
                            <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc.location + ", Brooklyn, NY")}`} target="_blank" rel="noopener noreferrer" className="block text-sm text-slate-100 font-medium underline-offset-2 hover:underline break-words">📍 {loc.location}</a>
                          ) : null}
                          {loc.cross_streets && <p className="text-xs text-emerald-400 mt-1">Cross: {loc.cross_streets}</p>}
                          {loc.location_notes && <p className="text-xs text-slate-400 italic mt-1">{loc.location_notes}</p>}
                        </div>
                      ))}
                      {(incident.perp_descriptions || [])
                        .map((perp, idx) => ({ perp, perpNum: idx + 1 }))
                        .filter(({ perp }) => perp.current_location || perp.direction_of_flight)
                        .map(({ perp, perpNum }) => (
                          <div key={`perpLoc_${perp.perp_id || perpNum}`} className="p-2.5 bg-slate-800/60 rounded border border-purple-800/40">
                            <div className="flex items-center gap-1.5 flex-wrap mb-1">
                              <Badge variant="outline" className="text-[10px] text-purple-400 border-purple-600">
                                Perp #{perpNum}{perp.linked_profile_name ? ` — ${perp.linked_profile_name}` : ''}
                              </Badge>
                              {(perp.was_arrested || perp.is_arrested) && <Badge className="text-[10px] bg-emerald-700 text-white border-0">ARRESTED</Badge>}
                              {perp.is_holding && <Badge className="text-[10px] bg-amber-700 text-white border-0">HOLDING</Badge>}
                              {perp.is_detained && <Badge className="text-[10px] bg-orange-700 text-white border-0">DETAINED</Badge>}
                              {perp.is_visual && <Badge className="text-[10px] bg-cyan-600 text-white border-0">VISUAL</Badge>}
                              {perp.is_positive_id && <Badge className="text-[10px] bg-emerald-600 text-white border-0">POSITIVE ID</Badge>}
                              {perp.has_weapon && <Badge variant="destructive" className="text-[10px]">ARMED</Badge>}
                            </div>
                            {perp.current_location && (
                              <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(perp.current_location + ", Brooklyn, NY")}`} target="_blank" rel="noopener noreferrer" className="block text-sm text-slate-100 font-medium underline-offset-2 hover:underline break-words">📍 {perp.current_location}</a>
                            )}
                            {perp.direction_of_flight && (
                              <p className="text-xs text-amber-400 mt-1">→ {perp.direction_of_flight}</p>
                            )}
                          </div>
                        ))}
                    </CardContent>
                  </Card>

                  {/* People */}
                  <Card className="bg-slate-900 border-slate-800">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium flex items-center gap-2 text-slate-300">
                        <Users className="w-4 h-4 text-purple-400" /> People
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0 space-y-2">
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="p-2 bg-slate-800/60 rounded border border-slate-700/50">
                          <p className="text-2xl font-bold text-purple-300">{perps.length}</p>
                          <p className="text-[10px] uppercase text-slate-500 mt-0.5">Perps</p>
                        </div>
                        <div className="p-2 bg-slate-800/60 rounded border border-slate-700/50">
                          <p className="text-2xl font-bold text-emerald-300">{(incident.victims || []).length}</p>
                          <p className="text-[10px] uppercase text-slate-500 mt-0.5">Victims</p>
                        </div>
                        <div className="p-2 bg-slate-800/60 rounded border border-slate-700/50">
                          <p className="text-2xl font-bold text-sky-300">{(incident.witnesses || []).length}</p>
                          <p className="text-[10px] uppercase text-slate-500 mt-0.5">Witnesses</p>
                        </div>
                      </div>
                      {(() => {
                        // Counters for callbacks: total + how many were CV or 3rd party.
                        // Pull caller_type from the additional callbacks AND from the
                        // primary callback (cv_or_third_party) when it's standalone.
                        const types = [];
                        callbackNumbers.forEach(cb => { if (cb.caller_type) types.push(cb.caller_type); });
                        if (hasPrimaryStandalone && incident.cv_or_third_party) types.push(incident.cv_or_third_party);
                        const cvCount = types.filter(t => t === "cv").length;
                        const thirdCount = types.filter(t => t === "3rd_party").length;
                        return (
                          <div className="grid grid-cols-3 gap-2 text-center">
                            <div className="p-2 bg-slate-800/60 rounded border border-slate-700/50">
                              <p className="text-2xl font-bold text-emerald-300">{totalCallbacks}</p>
                              <p className="text-[10px] uppercase text-slate-500 mt-0.5">Callbacks</p>
                            </div>
                            <div className="p-2 bg-slate-800/60 rounded border border-slate-700/50">
                              <p className="text-2xl font-bold text-amber-300">{cvCount}</p>
                              <p className="text-[10px] uppercase text-slate-500 mt-0.5">CV</p>
                            </div>
                            <div className="p-2 bg-slate-800/60 rounded border border-slate-700/50">
                              <p className="text-2xl font-bold text-slate-300">{thirdCount}</p>
                              <p className="text-[10px] uppercase text-slate-500 mt-0.5">3rd Party</p>
                            </div>
                          </div>
                        );
                      })()}
                      {perps.length > 0 && (
                        <div className="space-y-1.5">
                          {perps.map((p, idx) => {
                            const bits = [p.sex, p.race, p.age].filter(Boolean).join(" / ");
                            return (
                              <div key={p.perp_id || idx} className="p-2 bg-slate-800/60 rounded border border-purple-800/40 flex items-center gap-2 flex-wrap">
                                <Badge variant="outline" className="text-[10px] text-purple-300 border-purple-600">Perp #{idx + 1}</Badge>
                                {bits && <span className="text-xs text-slate-300">{bits}</span>}
                                {p.linked_profile_name && <span className="text-xs text-blue-300 font-medium">{p.linked_profile_name}</span>}
                                {p.is_arrested && <Badge className="text-[9px] bg-emerald-700">ARRESTED</Badge>}
                                {p.is_holding && <Badge className="text-[9px] bg-amber-700">HOLDING</Badge>}
                                {p.is_detained && <Badge className="text-[9px] bg-orange-700">DETAINED</Badge>}
                                {p.ready_for_arrest && <Badge className="text-[9px] bg-blue-700">READY</Badge>}
                                {p.has_weapon && <Badge className="text-[9px] bg-red-700">ARMED</Badge>}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {(arrestedCount + holdingCount + detainedCount + readyCount) > 0 && (
                        <div className="flex flex-wrap gap-1 pt-1 border-t border-slate-800">
                          {arrestedCount > 0 && <Badge className="text-[10px] bg-emerald-700">{arrestedCount} arrested</Badge>}
                          {holdingCount > 0 && <Badge className="text-[10px] bg-amber-700">{holdingCount} holding</Badge>}
                          {detainedCount > 0 && <Badge className="text-[10px] bg-orange-700">{detainedCount} detained</Badge>}
                          {readyCount > 0 && <Badge className="text-[10px] bg-blue-700">{readyCount} ready</Badge>}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Job Narrative */}
                  <Card className="bg-slate-900 border-slate-800 lg:col-span-2">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium flex items-center gap-2 text-slate-300">
                        <FileText className="w-4 h-4 text-blue-400" /> Job Narrative
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      {incident.call_note || sourceLiveCall?.call_note ? (
                        <p className="text-sm text-slate-200 whitespace-pre-wrap">{incident.call_note || sourceLiveCall?.call_note}</p>
                      ) : (
                        <p className="text-sm text-slate-500 italic">No job narrative.</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Status & Flags — surfaces any selected field from the Updates tab */}
                  {(() => {
                    const flagBadges = [
                      incident.is_pr && { label: "PR", cls: "bg-purple-700" },
                      incident.is_arrest && { label: "Arrest", cls: "bg-emerald-700" },
                      incident.transmitted_10_85 && { label: "10-85 Transmitted", cls: "bg-cyan-700" },
                      incident.transmitted_10_13 && { label: "10-13 Transmitted", cls: "bg-rose-700" },
                      incident.pd_activated && { label: "PD Activated", cls: "bg-blue-700" },
                      incident.police_report_filed && { label: "Police Report Filed", cls: "bg-green-700" },
                      incident.is_hate_crime && { label: "Hate Crime", cls: "bg-red-700" },
                      incident.hctf_notified && { label: "HCTF Notified", cls: "bg-red-700" },
                      incident.weapon_displayed && { label: "Weapon Displayed", cls: "bg-red-700" },
                      incident.ems_required && { label: "EMS Required", cls: "bg-amber-700" },
                      incident.injuries_reported && { label: "Injuries Reported", cls: "bg-amber-700" },
                      incident.footage_requested && { label: "Footage Requested", cls: "bg-violet-700" },
                      incident.footage_obtained && { label: "Footage Obtained", cls: "bg-violet-700" },
                      incident.needs_followup && { label: "Needs Follow-up", cls: "bg-orange-700" },
                      incident.show_in_call_history && { label: "Visible in Call History", cls: "bg-slate-700" },
                      incident.hide_from_media && { label: "Hidden from Media Page", cls: "bg-slate-700" },
                    ].filter(Boolean);

                    const noteSections = [
                      incident.is_hate_crime && incident.hate_crime_nature && { title: "Hate Crime", body: incident.hate_crime_nature },
                      incident.weapon_displayed && incident.weapon_notes && { title: "Weapon", body: incident.weapon_notes },
                      incident.ems_required && incident.ems_notes && { title: "EMS", body: incident.ems_notes },
                      incident.injuries_reported && incident.injuries_notes && { title: "Injuries", body: incident.injuries_notes },
                      incident.police_report_filed && incident.police_report_notes && { title: "Police Report", body: incident.police_report_notes },
                      incident.police_action_notes && { title: "Police Response / Action", body: incident.police_action_notes },
                      incident.police_followup_required && incident.police_followup_notes && { title: "Police Follow-up", body: incident.police_followup_notes },
                      incident.needs_followup && incident.followup_notes && { title: "Follow-up", body: incident.followup_notes },
                      (incident.footage_requested || incident.footage_obtained) && incident.footage_locations && { title: "Footage Locations", body: incident.footage_locations },
                      incident.call_final && { title: "Call Final", body: incident.call_final },
                    ].filter(Boolean);

                    const detective = (incident.assigned_detective || "").trim();
                    const hasAnything = flagBadges.length > 0 || noteSections.length > 0 || !!detective;
                    if (!hasAnything) return null;

                    return (
                      <Card className="bg-slate-900 border-slate-800 lg:col-span-2">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm font-medium flex items-center gap-2 text-slate-300">
                            <Flag className="w-4 h-4 text-amber-400" /> Status & Flags
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-0 space-y-3">
                          {flagBadges.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                              {flagBadges.map((b, idx) => (
                                <Badge key={idx} className={`${b.cls} text-[10px] text-white`}>{b.label}</Badge>
                              ))}
                            </div>
                          )}
                          {detective && (
                            <div className="text-xs text-slate-300">
                              <span className="text-slate-500 uppercase tracking-wide text-[10px] mr-2">Assigned Detective</span>
                              <span className="text-slate-100 font-medium">{detective}</span>
                            </div>
                          )}
                          {noteSections.length > 0 && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {noteSections.map((n, idx) => (
                                <div key={idx} className="p-2 bg-slate-800/60 rounded border border-slate-700/50">
                                  <p className="text-[10px] uppercase tracking-wide text-slate-500">{n.title}</p>
                                  <p className="text-xs text-slate-200 mt-1 whitespace-pre-wrap break-words">{n.body}</p>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })()}

                  {/* Units + Counts */}
                  <Card className="bg-slate-900 border-slate-800">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium flex items-center gap-2 text-slate-300">
                        <Shield className="w-4 h-4 text-cyan-400" /> Units & Activity
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0 space-y-2">
                      <div>
                        <p className="text-[10px] uppercase text-slate-500 mb-1">Assigned units ({units.length})</p>
                        {units.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {units.map((u, idx) => (
                              <Badge key={idx} variant="outline" className="text-[10px] border-cyan-700 text-cyan-300">{u}</Badge>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-slate-500 italic">No units</p>
                        )}
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-center pt-2 border-t border-slate-800">
                        <div>
                          <p className="text-lg font-bold text-slate-200">{updates.length}</p>
                          <p className="text-[10px] uppercase text-slate-500">Updates</p>
                        </div>
                        <div>
                          <p className="text-lg font-bold text-slate-200">{mediaCount}</p>
                          <p className="text-[10px] uppercase text-slate-500">Media</p>
                        </div>
                        <div>
                          <p className="text-lg font-bold text-slate-200">{(incident.related_incidents || []).length}</p>
                          <p className="text-[10px] uppercase text-slate-500">Related</p>
                        </div>
                      </div>
                      {latestUpdate && (
                        <div className="pt-2 border-t border-slate-800">
                          <p className="text-[10px] uppercase text-slate-500">Latest update</p>
                          <p className="text-xs text-slate-300 mt-1">
                            <span className="font-mono text-slate-500">{format(new Date(latestUpdate.timestamp), "h:mm a")}</span>
                            {latestUpdate.updated_by ? <span className="text-slate-500"> · {latestUpdate.updated_by}</span> : null}
                          </p>
                          {latestUpdate.update_text && <p className="text-xs text-slate-200 mt-1 break-words">{latestUpdate.update_text}</p>}
                          {latestUpdate.updated_location && <p className="text-xs text-amber-300 mt-1">📍 {latestUpdate.updated_location}</p>}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Linked Profiles */}
                  {(() => {
                    const profileIds = new Set();
                    perps.forEach(p => { if (p.linked_profile_id) profileIds.add(p.linked_profile_id); });
                    (incident.linked_profiles || []).forEach(pid => profileIds.add(pid));
                    const profilesList = [...profileIds].map(pid => {
                      const profile = linkedProfileDetails?.[pid] || (Array.isArray(profiles) ? profiles.find(p => p.profile_id === pid) : null);
                      const perp = perps.find(p => p.linked_profile_id === pid);
                      return { pid, profile, perp };
                    }).filter(p => p.profile || p.perp);
                    if (profilesList.length === 0) return null;
                    return (
                      <Card className="bg-slate-900 border-slate-800 lg:col-span-2">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm font-medium flex items-center gap-2 text-slate-300">
                            <UserCircle className="w-4 h-4 text-fuchsia-400" /> Linked Profiles ({profilesList.length})
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {profilesList.map(({ pid, profile, perp }) => {
                              const name = profile?.name || perp?.linked_profile_name || pid;
                              const aliases = profile?.aliases || profile?.aka || [];
                              const arrestCount = Array.isArray(profile?.arrests) ? profile.arrests.length : 0;
                              const rfaCount = Array.isArray(profile?.ready_for_arrest) ? profile.ready_for_arrest.length : 0;
                              return (
                                <button
                                  key={pid}
                                  onClick={() => navigate(`/perpetrators/${pid}`)}
                                  className="text-left p-2.5 bg-slate-800/60 rounded border border-fuchsia-800/40 hover:bg-slate-800 transition-colors"
                                >
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <UserCircle className="w-4 h-4 text-fuchsia-400 shrink-0" />
                                    <span className="text-sm font-medium text-fuchsia-200">{name}</span>
                                    {perp && (
                                      <Badge variant="outline" className="text-[9px] text-purple-300 border-purple-600">
                                        Perp #{perps.indexOf(perp) + 1}
                                      </Badge>
                                    )}
                                  </div>
                                  {Array.isArray(aliases) && aliases.length > 0 && (
                                    <p className="text-[11px] text-slate-400 mt-1 italic">aka {aliases.join(", ")}</p>
                                  )}
                                  {profile?.description && (
                                    <p className="text-[11px] text-slate-300 mt-1 line-clamp-2 break-words">{profile.description}</p>
                                  )}
                                  {(arrestCount > 0 || rfaCount > 0) && (
                                    <div className="flex flex-wrap gap-1 mt-1.5">
                                      {arrestCount > 0 && <Badge className="text-[9px] bg-emerald-700">{arrestCount} prior arrest{arrestCount === 1 ? "" : "s"}</Badge>}
                                      {rfaCount > 0 && <Badge className="text-[9px] bg-blue-700">{rfaCount} ready for arrest</Badge>}
                                    </div>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })()}

                  {/* Arrests on this incident */}
                  {(incident.arrests || []).length > 0 && (
                    <Card className="bg-slate-900 border-slate-800 lg:col-span-2">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium flex items-center gap-2 text-slate-300">
                          <Shield className="w-4 h-4 text-emerald-400" /> Arrests ({(incident.arrests || []).length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="space-y-2">
                          {(incident.arrests || []).map((arrest, idx) => {
                            const linkedPerp = arrest.linked_perp_id ? perps.find(p => p.perp_id === arrest.linked_perp_id) : null;
                            const linkedProfile = arrest.linked_profile_id
                              ? (linkedProfileDetails?.[arrest.linked_profile_id] || (Array.isArray(profiles) ? profiles.find(p => p.profile_id === arrest.linked_profile_id) : null))
                              : null;
                            const profileName = linkedProfile?.name || arrest.linked_profile_name;
                            const perpBits = linkedPerp
                              ? [linkedPerp.sex && `Sex: ${linkedPerp.sex}`, linkedPerp.race && `Race: ${linkedPerp.race}`, linkedPerp.age && `Age: ${linkedPerp.age}`, linkedPerp.height && `Height: ${linkedPerp.height}`].filter(Boolean).join(" · ")
                              : "";
                            // Resolve linked Ready-for-Arrest records to actual incident info
                            // so we can show "ARREST VIA <other incident>".
                            const linkedRfaIds = arrest.linked_ready_for_arrest_ids || (arrest.linked_ready_for_arrest_id ? [arrest.linked_ready_for_arrest_id] : []);
                            const linkedRfaRecords = (linkedProfile?.ready_for_arrest || []).filter(r => linkedRfaIds.includes(r.id || r.incident_id));
                            const idMethods = arrest.identification_by || [];
                            return (
                              <div key={arrest.arrest_id || idx} className="p-2.5 bg-slate-800/60 rounded border border-emerald-800/40 space-y-1.5">
                                {/* Header row */}
                                <div className="flex items-center gap-2 flex-wrap">
                                  <Badge className="text-[10px] bg-emerald-700">ARREST</Badge>
                                  {linkedPerp && (
                                    <Badge variant="outline" className="text-[10px] text-purple-300 border-purple-600">Perp #{perps.indexOf(linkedPerp) + 1}</Badge>
                                  )}
                                  {!linkedPerp && arrest.linked_perp_id && (
                                    <Badge className="text-[10px] bg-purple-900/60 text-purple-200">Perp: {arrest.perp_description_summary || 'Linked'}</Badge>
                                  )}
                                  {profileName && (
                                    <button
                                      onClick={() => arrest.linked_profile_id && navigate(`/perpetrators/${arrest.linked_profile_id}`)}
                                      className="text-sm font-medium text-fuchsia-200 hover:underline"
                                    >
                                      <UserCircle className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5" />
                                      {profileName}
                                    </button>
                                  )}
                                  {arrest.arrest_date && (
                                    <span className="text-xs text-emerald-300 font-mono ml-auto">
                                      <Calendar className="w-3 h-3 inline -mt-0.5 mr-1" />
                                      {format(new Date(arrest.arrest_date), "MM/dd/yyyy h:mm a")}
                                    </span>
                                  )}
                                </div>

                                {/* Perp descriptors */}
                                {perpBits && (
                                  <p className="text-xs text-slate-300">{perpBits}</p>
                                )}

                                {/* Status flags */}
                                {(arrest.was_live || arrest.perp_stopped_by_pd_only || arrest.outstanding_perpetrators) && (
                                  <div className="flex flex-wrap gap-1">
                                    {arrest.was_live && <Badge className="text-[9px] bg-rose-700">Live Arrest</Badge>}
                                    {arrest.perp_stopped_by_pd_only && <Badge className="text-[9px] bg-blue-700">PD Only</Badge>}
                                    {arrest.outstanding_perpetrators && <Badge className="text-[9px] bg-amber-700">Outstanding Perps</Badge>}
                                  </div>
                                )}

                                {/* Identification methods */}
                                {idMethods.length > 0 && (
                                  <p className="text-xs text-slate-400">
                                    <span className="text-slate-500">ID by:</span>{" "}
                                    {idMethods.map((m, i) => (
                                      <span key={i} className="inline-block bg-slate-700/60 text-slate-200 rounded px-1.5 py-0.5 text-[10px] mr-1">{m}</span>
                                    ))}
                                  </p>
                                )}

                                {/* ARREST VIA — Ready-for-Arrest records this arrest used */}
                                {(linkedRfaRecords.length > 0 || linkedRfaIds.length > 0) && (
                                  <div className="mt-1.5 p-2 bg-emerald-950/40 border border-emerald-700/50 rounded">
                                    <p className="text-[10px] uppercase text-emerald-300 tracking-wide font-semibold">
                                      <Link2 className="w-3 h-3 inline -mt-0.5 mr-1" />
                                      Arrest Via — used Ready-for-Arrest record{linkedRfaRecords.length === 1 ? "" : "s"}
                                    </p>
                                    {linkedRfaRecords.length === 0 ? (
                                      <p className="text-[11px] text-emerald-200/80 mt-1">{linkedRfaIds.length} linked record{linkedRfaIds.length === 1 ? "" : "s"}</p>
                                    ) : (
                                      <div className="space-y-1.5 mt-1.5">
                                        {linkedRfaRecords.map((rfa, ridx) => (
                                          <button
                                            key={rfa.id || rfa.incident_id || ridx}
                                            onClick={() => rfa.incident_id && navigate(`/incidents/${rfa.incident_id}`)}
                                            className="w-full text-left p-2 bg-slate-900/60 rounded border border-emerald-800/40 hover:bg-slate-900 transition-colors"
                                          >
                                            <div className="flex items-center gap-2 flex-wrap">
                                              {rfa.incident_type && <Badge className="text-[9px] bg-slate-600">{rfa.incident_type}</Badge>}
                                              <span className="text-xs font-medium text-slate-100">{rfa.headline || "Ready for Arrest"}</span>
                                              {rfa.report_filed && <Badge className="text-[9px] bg-blue-600">REPORT FILED</Badge>}
                                              {rfa.incident_id && (
                                                <span className="font-mono text-[10px] text-pink-300 ml-auto">{rfa.incident_id}</span>
                                              )}
                                            </div>
                                            {(rfa.address || rfa.date_time) && (
                                              <p className="text-[11px] text-slate-400 mt-0.5">
                                                {rfa.address || ""}
                                                {rfa.address && rfa.date_time ? " · " : ""}
                                                {rfa.date_time ? format(new Date(rfa.date_time), "MM/dd/yyyy") : ""}
                                              </p>
                                            )}
                                            {Array.isArray(rfa.identification_methods) && rfa.identification_methods.length > 0 && (
                                              <p className="text-[10px] text-slate-500 mt-0.5">ID: {rfa.identification_methods.join(", ")}</p>
                                            )}
                                          </button>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Source-incident metadata (if backend recorded it) */}
                                {arrest.source_incident_id && arrest.source_incident_id !== incident.incident_id && (
                                  <p className="text-[11px] text-slate-400 mt-1">
                                    <span className="text-slate-500">Source incident:</span>{" "}
                                    <button onClick={() => navigate(`/incidents/${arrest.source_incident_id}`)} className="font-mono text-pink-300 hover:underline">{arrest.source_incident_id}</button>
                                    {arrest.source_incident_type ? ` · ${arrest.source_incident_type}` : ""}
                                    {arrest.source_address ? ` · ${arrest.source_address}` : ""}
                                  </p>
                                )}

                                {arrest.charges && (
                                  <p className="text-xs text-slate-300"><span className="text-slate-500">Charges:</span> {arrest.charges}</p>
                                )}
                                {arrest.precinct && (
                                  <p className="text-[11px] text-slate-400"><span className="text-slate-500">Precinct:</span> {arrest.precinct}</p>
                                )}
                                {arrest.notes && (
                                  <p className="text-xs text-slate-200 whitespace-pre-wrap break-words"><span className="text-slate-500 text-[10px] uppercase block">Notes</span>{arrest.notes}</p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Related Incidents */}
                  {(incident.related_incidents || []).length > 0 && (
                    <Card className="bg-slate-900 border-slate-800 lg:col-span-2">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium flex items-center gap-2 text-slate-300">
                          <Link2 className="w-4 h-4 text-pink-400" /> Related Incidents ({(incident.related_incidents || []).length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="flex flex-wrap gap-1.5">
                          {(incident.related_incidents || []).map((rel) => (
                            <button key={rel.incident_id} onClick={() => navigate(`/incidents/${rel.incident_id}`)} className="px-2 py-1 bg-slate-800/60 rounded border border-pink-800/40 text-xs hover:bg-slate-800 transition-colors text-left">
                              <span className="font-mono text-pink-300">{rel.incident_id}</span>
                              {rel.headline && <span className="text-slate-300 ml-1.5">— {rel.headline}</span>}
                            </button>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            );
          })()}
        </TabsContent>

        {/* Call Info Tab */}
        <TabsContent value="callinfo" className="mt-4">
          <Card className="form-section">
            <CardHeader className="pb-2">
              <CardTitle className="form-section-title">Incident Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Headline</Label>
                <Input
                  value={incident.headline}
                  onChange={(e) => updateField("headline", e.target.value)}
                  className="input-dark"
                  data-testid="edit-headline"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label>Address</Label>
                  <Input
                    value={incident.address}
                    onChange={(e) => updateField("address", e.target.value)}
                    className="input-dark"
                  />
                </div>
                <div>
                  <Label>Cross Streets <span className="text-xs text-slate-500 font-normal">(from Google Maps)</span></Label>
                  <Input
                    value={incident.cross_streets || ""}
                    onChange={(e) => updateField("cross_streets", e.target.value)}
                    className="input-dark bg-slate-800/50"
                    placeholder="Auto from address..."
                  />
                </div>
                <div>
                  <Label>Location Notes</Label>
                  <Input
                    value={incident.location_notes || ""}
                    onChange={(e) => updateField("location_notes", e.target.value)}
                    className="input-dark"
                    placeholder="Apt #, floor, landmarks..."
                  />
                </div>
              </div>
              {/* Additional Locations */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-slate-400 uppercase">Additional Locations ({(incident.additional_locations || []).length})</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => {
                      setAddLocData({ title: "", location: "", cross_streets: "", location_notes: "", direction_of_flight: "" });
                      setEditingLocIdx(null);
                      setAddLocOpen(true);
                    }}
                  >
                    <Plus className="w-3 h-3 mr-1" />Add Location
                  </Button>
                </div>
                {(incident.additional_locations || []).map((loc, idx) => (
                  <div key={idx} className="p-2 bg-blue-950/30 border border-blue-800/40 rounded">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        {loc.title && <p className="text-[10px] text-blue-300 font-semibold uppercase mb-0.5">{loc.title}</p>}
                        <div className="flex items-center gap-1">
                          <MapPin className="w-3 h-3 text-blue-400 shrink-0" />
                          <span className="text-sm text-slate-200 break-words">{loc.location}</span>
                        </div>
                        {loc.cross_streets && <p className="text-xs text-emerald-400 ml-4">✕ {loc.cross_streets}</p>}
                        {loc.location_notes && <p className="text-xs text-slate-400 ml-4 italic">{loc.location_notes}</p>}
                        {loc.direction_of_flight && <p className="text-xs text-amber-400 ml-4">→ {loc.direction_of_flight}</p>}
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-blue-400"
                          onClick={() => {
                            setAddLocData({
                              title: loc.title || "",
                              location: loc.location || "",
                              cross_streets: loc.cross_streets || "",
                              location_notes: loc.location_notes || "",
                              direction_of_flight: loc.direction_of_flight || "",
                            });
                            setEditingLocIdx(idx);
                            setAddLocOpen(true);
                          }}
                        >
                          <Edit className="w-3 h-3" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-red-400"
                          onClick={() => {
                            const next = (incident.additional_locations || []).filter((_, i) => i !== idx);
                            const updated = { ...incident, additional_locations: next };
                            setIncident(updated);
                            handleSave(updated);
                          }}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {addLocOpen && (
                  <div className="p-3 bg-slate-800/60 border border-blue-800/40 rounded space-y-2">
                    <p className="text-xs text-blue-300 font-semibold uppercase">{editingLocIdx !== null ? "Edit Location" : "New Location"}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Title</Label>
                        <Input value={addLocData.title} onChange={(e) => setAddLocData({ ...addLocData, title: e.target.value })} className="input-dark h-8" placeholder="e.g. Suspect's home" />
                      </div>
                      <div>
                        <Label className="text-xs">Address</Label>
                        <Input value={addLocData.location} onChange={(e) => setAddLocData({ ...addLocData, location: e.target.value })} className="input-dark h-8" placeholder="Address" />
                      </div>
                      <div>
                        <Label className="text-xs">Cross Streets</Label>
                        <Input value={addLocData.cross_streets} onChange={(e) => setAddLocData({ ...addLocData, cross_streets: e.target.value })} className="input-dark h-8" placeholder="Cross streets" />
                      </div>
                      <div>
                        <Label className="text-xs">Direction of Flight</Label>
                        <Input value={addLocData.direction_of_flight} onChange={(e) => setAddLocData({ ...addLocData, direction_of_flight: e.target.value })} className="input-dark h-8" placeholder="e.g. Northbound" />
                      </div>
                      <div className="sm:col-span-2">
                        <Label className="text-xs">Notes</Label>
                        <Input value={addLocData.location_notes} onChange={(e) => setAddLocData({ ...addLocData, location_notes: e.target.value })} className="input-dark h-8" placeholder="Apt #, floor, landmarks..." />
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                      <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setAddLocOpen(false); setEditingLocIdx(null); }}>
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white"
                        disabled={!addLocData.location.trim()}
                        onClick={() => {
                          const list = [...(incident.additional_locations || [])];
                          const entry = { ...addLocData };
                          if (editingLocIdx !== null) {
                            list[editingLocIdx] = { ...list[editingLocIdx], ...entry };
                          } else {
                            list.push(entry);
                          }
                          const updated = { ...incident, additional_locations: list };
                          setIncident(updated);
                          handleSave(updated);
                          setAddLocOpen(false);
                          setEditingLocIdx(null);
                        }}
                      >
                        {editingLocIdx !== null ? "Save" : "Add"}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Date & Time</Label>
                  <Input
                    type="datetime-local"
                    value={formatDateTimeForInput(incident.date_time)}
                    onChange={(e) => updateField("date_time", new Date(e.target.value).toISOString())}
                    className="input-dark"
                    data-testid="edit-datetime"
                  />
                </div>
                <div>
                  <Label>Incident Type</Label>
                  <Select value={incident.incident_type} onValueChange={(v) => updateField("incident_type", v)}>
                    <SelectTrigger className="input-dark">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {incidentTypes.map((type) => (
                        <SelectItem key={type.type_id} value={type.name}>{type.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Secondary Type</Label>
                  <Select value={incident.secondary_incident_type || "none"} onValueChange={(v) => updateField("secondary_incident_type", v === "none" ? "" : v)}>
                    <SelectTrigger className="input-dark">
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {incidentTypes.map((type) => (
                        <SelectItem key={type.type_id} value={type.name}>{type.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Report Type</Label>
                  <Select value={incident.report_type} onValueChange={(v) => updateField("report_type", v)}>
                    <SelectTrigger className="input-dark">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="reported_live">Reported Live</SelectItem>
                      <SelectItem value="reported_in_past">Reported In Past</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <Label>Status</Label>
                  <Select value={incident.status} onValueChange={(v) => updateField("status", v)}>
                    <SelectTrigger className="input-dark" data-testid="status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((s) => (
                        <SelectItem key={s} value={s}>{s.replace("_", " ").toUpperCase()}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {/* Send to Dispatch button when status is live_call */}
                  {incident.status === "live_call" && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2 w-full bg-blue-900/30 border-blue-500 text-blue-400 hover:bg-blue-900/50"
                      onClick={async () => {
                        try {
                          const response = await axios.post(`${API}/incidents/${incidentId}/to-live-call`, {}, { withCredentials: true });
                          toast.success("Sent back to Dispatch", {
                            action: {
                              label: "View",
                              onClick: () => navigate("/live-calls")
                            }
                          });
                          fetchData();
                        } catch (error) {
                          toast.error("Failed to send to live calls: " + (error.response?.data?.detail || error.message));
                        }
                      }}
                    >
                      <Radio className="w-4 h-4 mr-2" />
                      Send to Dispatch
                    </Button>
                  )}
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select value={incident.priority} onValueChange={(v) => updateField("priority", v)}>
                    <SelectTrigger className="input-dark">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRIORITY_OPTIONS.map((p) => (
                        <SelectItem key={p} value={p}>{p.toUpperCase()}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Confirmation Status</Label>
                  <Select value={incident.confirmation_status || "no_confirm"} onValueChange={(v) => updateField("confirmation_status", v === "no_confirm" ? "" : v)}>
                    <SelectTrigger className="input-dark">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no_confirm">No Confirm</SelectItem>
                      <SelectItem value="unconfirmed">Unconfirmed</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Related Incidents */}
          <Card className="form-section mt-4">
            <CardHeader className="pb-2">
              <CardTitle className="form-section-title flex items-center justify-between">
                <span className="flex items-center gap-2"><Link2 className="w-4 h-4" />Related Incidents ({(incident.related_incidents || []).length})</span>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setShowLinkIncident(!showLinkIncident); setLinkIncSearch(""); setLinkIncResults([]); setLinkIncNote(""); }}>
                  {showLinkIncident ? <X className="w-3 h-3 mr-1" /> : <Plus className="w-3 h-3 mr-1" />}
                  {showLinkIncident ? "Cancel" : "Link Incident"}
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Link search */}
              {showLinkIncident && (
                <div className="mb-3 p-3 bg-slate-800/50 rounded space-y-2">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-slate-500" />
                    <Input
                      placeholder="Search by ID, headline, address..."
                      value={linkIncSearch}
                      onChange={(e) => { setLinkIncSearch(e.target.value); searchIncidentsForLink(e.target.value); }}
                      className="input-dark pl-8 h-8 text-sm"
                    />
                  </div>
                  <Input
                    placeholder="Note (optional) - e.g. 'same perp', 'related location'..."
                    value={linkIncNote}
                    onChange={(e) => setLinkIncNote(e.target.value)}
                    className="input-dark h-8 text-sm"
                  />
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {linkIncSearching && <div className="flex justify-center py-3"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>}
                    {!linkIncSearching && linkIncSearch.length >= 2 && linkIncResults.length === 0 && (
                      <p className="text-xs text-slate-500 text-center py-3">No incidents found</p>
                    )}
                    {linkIncResults.map((inc) => {
                      const alreadyLinked = (incident.related_incidents || []).some(r => r.incident_id === inc.incident_id);
                      return (
                        <div
                          key={inc.incident_id}
                          className={`p-2 rounded text-sm cursor-pointer transition-colors ${alreadyLinked ? "bg-slate-700/50 opacity-50" : "bg-slate-800 hover:bg-slate-700"}`}
                          onClick={() => !alreadyLinked && linkRelatedIncident(inc.incident_id)}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium truncate">{inc.headline || inc.incident_type || "Incident"}</span>
                                <Badge variant="outline" className="text-[9px] px-1 py-0">{inc.status?.replace("_", " ") || "open"}</Badge>
                              </div>
                              <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                                <span className="font-mono">{inc.incident_id}</span>
                                {inc.date_time && <span>&bull; {new Date(inc.date_time).toLocaleDateString('en-US', { timeZone: 'America/New_York' })}</span>}
                              </div>
                            </div>
                            {alreadyLinked ? <span className="text-xs text-slate-500">Linked</span> : <Plus className="h-4 w-4 text-blue-400 shrink-0" />}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Linked list */}
              {(incident.related_incidents || []).length === 0 && !showLinkIncident && (
                <p className="text-slate-500 text-sm text-center py-4">No related incidents linked.</p>
              )}
              {(incident.related_incidents || []).length > 0 && (
                <div className="space-y-1">
                  {(incident.related_incidents || []).map((rel) => {
                    // Compute arrest-via relationship between this incident and the related one.
                    // Direction A — "ARRESTED VIA": this incident's ready was used in an arrest on the related incident.
                    // Direction B — "ARREST VIA":   this incident's arrest was made via the related incident's ready.
                    let arrestedViaRel = false;
                    let arrestViaRel = false;
                    (incident.perp_descriptions || []).forEach(perp => {
                      if (!perp.linked_profile_id) return;
                      const profile = linkedProfileDetails?.[perp.linked_profile_id];
                      if (!profile?.ready_for_arrest) return;
                      profile.ready_for_arrest.forEach(r => {
                        if (r.incident_id === incident.incident_id && r.arrest_linked?.incident_id === rel.incident_id) {
                          arrestedViaRel = true;
                        }
                        if (r.incident_id === rel.incident_id && r.arrest_linked?.incident_id === incident.incident_id) {
                          arrestViaRel = true;
                        }
                      });
                    });
                    return (
                    <div key={rel.incident_id} className="flex items-center justify-between p-2 bg-slate-800/50 rounded hover:bg-slate-800 transition-colors">
                      <div className="min-w-0 flex-1 cursor-pointer" onClick={() => navigate(`/incidents/${rel.incident_id}`)}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-mono text-blue-400 hover:underline">{rel.incident_id}</span>
                          {rel.headline && <span className="text-sm text-slate-300 break-words">{rel.headline}</span>}
                          {arrestedViaRel && (
                            <Badge className="bg-emerald-600 text-white text-[10px]" title="This incident's ready-for-arrest was used in the arrest on this related incident">
                              ARRESTED VIA
                            </Badge>
                          )}
                          {arrestViaRel && (
                            <Badge className="bg-emerald-700 text-white text-[10px]" title="This incident's arrest was made via this related incident's ready-for-arrest">
                              ARREST VIA
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-[10px] text-slate-500 mt-0.5">
                          {rel.note && <span className="text-slate-400">{rel.note}</span>}
                          {rel.linked_by && <span>Linked by {rel.linked_by}</span>}
                        </div>
                      </div>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400 shrink-0" onClick={(e) => e.stopPropagation()}>
                            <X className="w-3 h-3" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-slate-900 border-slate-700">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="text-red-400">Unlink Incident?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Unlink <span className="text-white font-medium">{rel.incident_id}</span>{rel.headline ? <> — {rel.headline}</> : ""}? This will remove the link from both incidents.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="bg-slate-700 border-slate-600">Cancel</AlertDialogCancel>
                            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => unlinkRelatedIncident(rel.incident_id)}>Unlink</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* CV/CB Tab - Callbacks, Victims, Witnesses */}
            <TabsContent value="cvcb" className="mt-4">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
              {/* Callback Numbers */}
              <Card className="form-section">
                <CardContent className="p-4">
                  <p className="text-xs text-slate-500 uppercase mb-2">Callback Numbers ({incident.callback_numbers?.length || 0})</p>
                  {incident.callback_numbers?.length > 0 && (
                    <div className="space-y-1 mb-3">
                      {incident.callback_numbers.map((cb, idx) => (
                        <div key={cb.callback_id || idx} className="p-2 bg-slate-900 rounded text-sm">
                          {editingCallbackId === (cb.callback_id || idx) ? (
                            <div className="space-y-2">
                              <div className="flex gap-2 flex-wrap items-center">
                                <Input id={`edit-cb-number-${idx}`} defaultValue={cb.number} placeholder="Phone number..." className="input-dark h-8 flex-1 min-w-[140px]" type="tel" inputMode="numeric" />
                                <select id={`edit-cb-type-${idx}`} defaultValue={cb.caller_type || cb.type || ''} className="h-8 px-2 text-sm bg-slate-800 border border-slate-700 rounded">
                                  <option value="">Type</option><option value="cv">CV</option><option value="3rd_party">3rd Party</option>
                                </select>
                                <select id={`edit-cb-visual-${idx}`} defaultValue={cb.has_visual ? 'true' : 'false'} className="h-8 px-2 text-sm bg-slate-800 border border-slate-700 rounded">
                                  <option value="false">No Visual</option><option value="true">Has Visual</option>
                                </select>
                              </div>
                              <div className="flex gap-2">
                                <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700" onClick={() => { const numberInput = document.getElementById(`edit-cb-number-${idx}`); const typeInput = document.getElementById(`edit-cb-type-${idx}`); const visualInput = document.getElementById(`edit-cb-visual-${idx}`); const callback_numbers = [...incident.callback_numbers]; callback_numbers[idx] = { ...callback_numbers[idx], number: formatPhoneNumber(numberInput.value), caller_type: typeInput.value, has_visual: visualInput.value === 'true' }; const updated = { ...incident, callback_numbers }; setIncident(updated); handleSave(updated); setEditingCallbackId(null); }}>Save</Button>
                                <Button size="sm" variant="ghost" className="h-7" onClick={() => setEditingCallbackId(null)}>Cancel</Button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2 flex-wrap">
                                {cb.contact_status && <Flag className="w-3 h-3 text-yellow-400" />}
                                <button className="text-blue-400 hover:underline font-medium bg-transparent border-none cursor-pointer p-0" onClick={() => setPhoneLookupNumber(cb.number)}>{formatPhoneNumber(cb.number)}</button>
                                {handlePhoneClick && <button className="p-0.5 rounded hover:bg-slate-700 text-green-400 hover:text-green-300 shrink-0" title="Call" onClick={(e) => { e.stopPropagation(); handlePhoneClick(cb.number); }}><PhoneOutgoing className="h-3 w-3" /></button>}
                                <button className="p-0.5 rounded hover:bg-slate-700 text-slate-400 hover:text-slate-200 shrink-0" title="Copy number" onClick={async (e) => { e.stopPropagation(); const ok = await copyPhoneNumber(cb.number); if (ok) toast.success("Phone number copied"); else toast.error("Couldn't copy"); }}><Copy className="h-3 w-3" /></button>
                                {cb.contact_name && <span className="text-[10px] text-yellow-400/80">{cb.contact_name}</span>}
                                {(cb.caller_type || cb.type) && <Badge variant="outline" className="text-[10px]">{(cb.caller_type || cb.type) === 'cv' ? 'CV' : (cb.caller_type || cb.type) === '3rd_party' ? '3rd Party' : (cb.caller_type || cb.type)}</Badge>}
                                {cb.has_visual && <Badge className="text-[10px] bg-emerald-500/20 text-emerald-400">VISUAL</Badge>}
                                {!cb.has_visual && <span className="text-[10px] text-slate-500">No Visual</span>}
                              </div>
                              <div className="flex items-center gap-1">
                                <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-slate-400 hover:text-blue-400" onClick={() => setEditingCallbackId(cb.callback_id || idx)}><Pencil className="w-3 h-3" /></Button>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-400"><X className="w-4 h-4" /></Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent className="bg-slate-900 border-slate-700">
                                    <AlertDialogHeader>
                                      <AlertDialogTitle className="text-red-400">Remove Callback?</AlertDialogTitle>
                                      <AlertDialogDescription>Remove <span className="text-white font-medium">{cb.number}</span> from callbacks?</AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel className="bg-slate-700 border-slate-600">Cancel</AlertDialogCancel>
                                      <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => { const callback_numbers = incident.callback_numbers.filter((_, i) => i !== idx); const updated = { ...incident, callback_numbers }; setIncident(updated); handleSave(updated); }}>Remove</AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2 flex-wrap items-center">
                    <Input id="new-callback-number" placeholder="Phone number..." className="input-dark h-8 flex-1 min-w-[140px]" type="tel" inputMode="numeric" />
                    <Popover open={showRecentCallsFor === "callback"} onOpenChange={(open) => { setShowRecentCallsFor(open ? "callback" : null); if (open) fetchRecentCalls(); }}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="h-8 px-2" title="Recent incoming calls"><PhoneIncoming className="w-3.5 h-3.5" /></Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-72 p-2 bg-slate-900 border-slate-700 z-[9999]">
                        <p className="text-xs text-slate-400 mb-2">Recent Calls (30 min)</p>
                        {loadingRecentCalls ? <div className="text-center py-2"><RefreshCw className="w-4 h-4 animate-spin mx-auto" /></div>
                        : recentCalls.length === 0 ? <p className="text-xs text-slate-500 text-center py-2">No recent calls</p>
                        : <div className="space-y-1 max-h-48 overflow-y-auto">{recentCalls.map((call, i) => (
                          <Button key={i} variant="ghost" size="sm" className="w-full justify-start text-xs h-7 px-2" onClick={() => {
                            const el = document.getElementById('new-callback-number');
                            if (el) { el.value = formatPhoneNumber(call.caller_number); }
                            setShowRecentCallsFor(null);
                          }}><Phone className="w-3 h-3 mr-1.5 shrink-0" />{call.caller_number}</Button>
                        ))}</div>}
                      </PopoverContent>
                    </Popover>
                    <select id="new-callback-type" className="h-8 px-2 text-sm bg-slate-800 border border-slate-700 rounded"><option value="">Type</option><option value="cv">CV</option><option value="3rd_party">3rd Party</option></select>
                    <select id="new-callback-visual" className="h-8 px-2 text-sm bg-slate-800 border border-slate-700 rounded"><option value="false">No Visual</option><option value="true">Has Visual</option></select>
                    <Button size="sm" variant="outline" onClick={() => { const numberInput = document.getElementById('new-callback-number'); const typeInput = document.getElementById('new-callback-type'); const visualInput = document.getElementById('new-callback-visual'); if (numberInput.value) { const callback_numbers = [...(incident.callback_numbers || []), { callback_id: `cb_${Date.now()}`, number: formatPhoneNumber(numberInput.value), caller_type: typeInput.value, has_visual: visualInput.value === 'true' }]; const updated = { ...incident, callback_numbers }; setIncident(updated); handleSave(updated); numberInput.value = ''; typeInput.value = ''; visualInput.value = 'false'; } }}><Plus className="w-3 h-3 mr-1" />Add</Button>
                  </div>
                </CardContent>
              </Card>

              {/* Victims */}
              {incident && (
              <Card className="form-section">
                <CardContent className="p-4">
                  <div className="max-h-[80vh] overflow-y-auto">
                    {incident.victims?.map((victim) => (
                      <div key={victim.victim_id} className="p-3 bg-slate-950 rounded-sm mb-2"
                        ref={editingVictimId === victim.victim_id ? (el) => setTimeout(() => el?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 50) : undefined}
                      >
                        {editingVictimId === victim.victim_id ? (
                          // Edit mode
                          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs">Name</Label>
                                <Input value={victim.name || ""} onChange={(e) => updateVictim(victim.victim_id, { name: e.target.value })} className="input-dark h-8" />
                              </div>
                              <div>
                                <Label className="text-xs">Email</Label>
                                <Input value={victim.email || ""} onChange={(e) => updateVictim(victim.victim_id, { email: e.target.value })} className="input-dark h-8" />
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs">Phone 1</Label>
                                <PhoneInput value={victim.phone1 || ""} onChange={(e) => updateVictim(victim.victim_id, { phone1: formatPhoneNumber(e.target.value) })} className="input-dark h-8" />
                              </div>
                              <div>
                                <Label className="text-xs">Phone 2</Label>
                                <PhoneInput value={victim.phone2 || ""} onChange={(e) => updateVictim(victim.victim_id, { phone2: formatPhoneNumber(e.target.value) })} className="input-dark h-8" />
                              </div>
                            </div>
                            <div>
                              <Label className="text-xs">Address</Label>
                              <Input value={victim.address || ""} onChange={(e) => updateVictim(victim.victim_id, { address: e.target.value })} className="input-dark h-8" />
                            </div>
                            <div>
                              <Label className="text-xs">Statement</Label>
                              <Textarea value={victim.statement || ""} onChange={(e) => updateVictim(victim.victim_id, { statement: e.target.value })} className="input-dark" rows={2} />
                            </div>
                            <div>
                              <Label className="text-xs">Property Stolen</Label>
                              <Textarea value={victim.property_stolen || ""} onChange={(e) => updateVictim(victim.victim_id, { property_stolen: e.target.value })} className="input-dark" rows={2} />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <div className="flex items-center gap-2">
                                <Checkbox checked={victim.has_injuries || false} onCheckedChange={(c) => updateVictim(victim.victim_id, { has_injuries: c })} />
                                <Label className="text-xs">Injuries?</Label>
                              </div>
                              <div className="flex items-center gap-2">
                                <Checkbox checked={victim.on_police_report || false} onCheckedChange={(c) => updateVictim(victim.victim_id, { on_police_report: c })} />
                                <Label className="text-xs">On Police Report?</Label>
                              </div>
                              <div className="flex items-center gap-2">
                                <Checkbox checked={victim.can_recognize_perp || false} onCheckedChange={(c) => updateVictim(victim.victim_id, { can_recognize_perp: c })} />
                                <Label className="text-xs">Can Recognize Perp?</Label>
                              </div>
                              <div className="flex items-center gap-2">
                                <Checkbox checked={victim.interviewed_on_scene || false} onCheckedChange={(c) => updateVictim(victim.victim_id, { interviewed_on_scene: c })} />
                                <Label className="text-xs">Interviewed on Scene?</Label>
                              </div>
                              <div className="flex items-center gap-2">
                                <Checkbox checked={victim.is_minor_or_elderly || false} onCheckedChange={(c) => updateVictim(victim.victim_id, { is_minor_or_elderly: c })} />
                                <Label className="text-xs">Minor or Elderly?</Label>
                              </div>
                            </div>
                            {victim.has_injuries && (
                              <div>
                                <Label className="text-xs">Injuries Description</Label>
                                <Textarea value={victim.injuries_description || ""} onChange={(e) => updateVictim(victim.victim_id, { injuries_description: e.target.value })} className="input-dark" rows={2} />
                              </div>
                            )}
                            {victim.is_minor_or_elderly && (
                              <div className="p-2 bg-slate-900 rounded space-y-2">
                                <p className="text-xs font-medium text-amber-400">Guardian Info</p>
                                <Input placeholder="Guardian Name" value={victim.guardian_name || ""} onChange={(e) => updateVictim(victim.victim_id, { guardian_name: e.target.value })} className="input-dark h-8" />
                                <PhoneInput placeholder="Guardian Phone" value={victim.guardian_phone1 || ""} onChange={(e) => updateVictim(victim.victim_id, { guardian_phone1: formatPhoneNumber(e.target.value) })} className="input-dark h-8" />
                              </div>
                            )}
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs">Dir of Flight Prior</Label>
                                <Input value={victim.direction_of_flight_prior || ""} onChange={(e) => updateVictim(victim.victim_id, { direction_of_flight_prior: e.target.value })} className="input-dark h-8" />
                              </div>
                              <div>
                                <Label className="text-xs">Dir of Flight Post</Label>
                                <Input value={victim.direction_of_flight_post || ""} onChange={(e) => updateVictim(victim.victim_id, { direction_of_flight_post: e.target.value })} className="input-dark h-8" />
                              </div>
                            </div>
                            <div>
                              <Label className="text-xs">Response / Follow-up</Label>
                              <Textarea value={victim.response || ""} onChange={(e) => updateVictim(victim.victim_id, { response: e.target.value })} className="input-dark" rows={2} placeholder="Notes from callback or follow-up..." />
                            </div>
                            <div className="flex gap-2 sticky bottom-0 bg-slate-950 pt-2 pb-1 z-10">
                              <Button size="sm" onClick={() => saveVictimEdit(victim.victim_id)} className="btn-primary">
                                <Check className="w-3 h-3 mr-1" />Save
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setEditingVictimId(null)}>Cancel</Button>
                            </div>
                          </div>
                        ) : (
                          // View mode
                          <>
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <p className="font-medium">{victim.name}</p>
                                {victim.email && <p className="text-xs text-slate-400">{victim.email}</p>}
                              </div>
                              <div className="flex gap-1">
                                <Button variant="ghost" size="sm" onClick={() => setEditingVictimId(victim.victim_id)}>
                                  <Edit className="w-4 h-4 text-blue-400" />
                                </Button>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button variant="ghost" size="sm"><Trash2 className="w-4 h-4 text-red-500" /></Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent className="bg-slate-900 border-slate-700">
                                    <AlertDialogHeader>
                                      <AlertDialogTitle className="text-red-400">Remove Victim?</AlertDialogTitle>
                                      <AlertDialogDescription>Remove <span className="text-white font-medium">{victim.name || "this victim"}</span>?</AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel className="bg-slate-700 border-slate-600">Cancel</AlertDialogCancel>
                                      <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => removeVictim(victim.victim_id)}>Remove</AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-sm text-slate-400">
                              {victim.phone1 && (
                                <div className="flex items-center gap-1">
                                  <button className="text-blue-400 hover:underline bg-transparent border-none cursor-pointer p-0 flex items-center gap-1" onClick={() => setPhoneLookupNumber(victim.phone1)}><Phone className="w-3 h-3" />{victim.phone1}</button>
                                  <button className="p-0.5 rounded hover:bg-slate-700 text-slate-400 hover:text-slate-200 shrink-0" title="Copy number" onClick={async (e) => { e.stopPropagation(); const ok = await copyPhoneNumber(victim.phone1); if (ok) toast.success("Phone number copied"); else toast.error("Couldn't copy"); }}><Copy className="h-3 w-3" /></button>
                                </div>
                              )}
                              {victim.phone2 && (
                                <div className="flex items-center gap-1">
                                  <button className="text-blue-400 hover:underline bg-transparent border-none cursor-pointer p-0 flex items-center gap-1" onClick={() => setPhoneLookupNumber(victim.phone2)}><Phone className="w-3 h-3" />{victim.phone2}</button>
                                  <button className="p-0.5 rounded hover:bg-slate-700 text-slate-400 hover:text-slate-200 shrink-0" title="Copy number" onClick={async (e) => { e.stopPropagation(); const ok = await copyPhoneNumber(victim.phone2); if (ok) toast.success("Phone number copied"); else toast.error("Couldn't copy"); }}><Copy className="h-3 w-3" /></button>
                                </div>
                              )}
                              {victim.address && <span className="col-span-2">{victim.address}</span>}
                            </div>
                            {victim.statement && <p className="text-sm mt-2 text-slate-300">{victim.statement}</p>}
                            {victim.property_stolen && <p className="text-sm text-amber-400">Property: {victim.property_stolen}</p>}
                            <div className="flex flex-wrap gap-2 mt-2">
                              {victim.has_injuries && <Badge variant="destructive" className="text-xs">INJURIES</Badge>}
                              {victim.on_police_report && <Badge variant="outline" className="text-xs">On Police Report</Badge>}
                              {victim.interviewed_on_scene && <Badge variant="outline" className="text-xs">Interviewed</Badge>}
                              {victim.can_recognize_perp && <Badge variant="outline" className="text-xs">Can ID Perp</Badge>}
                              {victim.is_minor_or_elderly && <Badge className="bg-amber-900/50 text-amber-200 text-xs">Minor/Elderly</Badge>}
                            </div>
                            {/* Show injuries description if has_injuries is true */}
                            {victim.has_injuries && victim.injuries_description && (
                              <div className="mt-2 p-2 bg-red-950/30 border border-red-900/50 rounded text-sm">
                                <p className="text-xs text-red-400 font-medium mb-1">Injuries Description:</p>
                                <p className="text-slate-300">{victim.injuries_description}</p>
                                {victim.medical_treatment && <p className="text-slate-400 mt-1">Treatment: {victim.medical_treatment}</p>}
                              </div>
                            )}
                            {victim.is_minor_or_elderly && victim.guardian_name && (
                              <div className="mt-2 p-2 bg-slate-900 rounded text-sm">
                                <p className="text-slate-400">Guardian: {victim.guardian_name}</p>
                                {victim.guardian_phone1 && <p className="text-slate-500">{victim.guardian_phone1}</p>}
                              </div>
                            )}
                            {/* Show direction of flight info if provided */}
                            {(victim.direction_of_flight_prior || victim.direction_of_flight_post) && (
                              <div className="mt-2 p-2 bg-slate-900 rounded text-sm">
                                {victim.direction_of_flight_prior && <p className="text-slate-400">Dir of Flight Prior: {victim.direction_of_flight_prior}</p>}
                                {victim.direction_of_flight_post && <p className="text-slate-400">Dir of Flight Post: {victim.direction_of_flight_post}</p>}
                              </div>
                            )}
                            {/* Show response/follow-up notes */}
                            {victim.response && (
                              <div className="mt-2 p-2 bg-blue-950/30 border border-blue-900/50 rounded text-sm">
                                <p className="text-xs text-blue-400 font-medium mb-1">Response / Follow-up:</p>
                                <p className="text-slate-300">{victim.response}</p>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                  <Dialog open={showVictimDialog} onOpenChange={setShowVictimDialog}>
                    <DialogTrigger asChild>
                      <Button className="mt-4 btn-secondary w-full" data-testid="add-victim-btn">
                        <Plus className="w-4 h-4 mr-1" />Add Victim
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Add Victim Information</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Name *</Label>
                            <Input value={newVictim.name || ""} onChange={(e) => setNewVictim({ ...newVictim, name: e.target.value })} className="input-dark" />
                          </div>
                          <div>
                            <Label>Email</Label>
                            <Input value={newVictim.email || ""} onChange={(e) => setNewVictim({ ...newVictim, email: e.target.value })} className="input-dark" />
                          </div>
                        </div>
                        <div>
                          <Label>Address</Label>
                          <Input value={newVictim.address || ""} onChange={(e) => setNewVictim({ ...newVictim, address: e.target.value })} className="input-dark" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Phone 1</Label>
                            <div className="flex gap-1">
                              <PhoneInput value={newVictim.phone1 || ""} onChange={(e) => setNewVictim({ ...newVictim, phone1: formatPhoneNumber(e.target.value) })} className="input-dark flex-1" />
                              <Popover open={showRecentCallsFor === "victim"} onOpenChange={(open) => { setShowRecentCallsFor(open ? "victim" : null); if (open) fetchRecentCalls(); }}>
                                <PopoverTrigger asChild><Button variant="outline" size="sm" className="h-9 px-2" title="Recent calls"><PhoneIncoming className="w-3.5 h-3.5" /></Button></PopoverTrigger>
                                <PopoverContent className="w-72 p-2 bg-slate-900 border-slate-700 z-[9999]">
                                  <p className="text-xs text-slate-400 mb-2">Recent Calls (30 min)</p>
                                  {loadingRecentCalls ? <div className="text-center py-2"><RefreshCw className="w-4 h-4 animate-spin mx-auto" /></div>
                                  : recentCalls.length === 0 ? <p className="text-xs text-slate-500 text-center py-2">No recent calls</p>
                                  : <div className="space-y-1 max-h-48 overflow-y-auto">{recentCalls.map((call, i) => (
                                    <Button key={i} variant="ghost" size="sm" className="w-full justify-start text-xs h-7 px-2" onClick={() => { setNewVictim({ ...newVictim, phone1: formatPhoneNumber(call.caller_number) }); setShowRecentCallsFor(null); }}><Phone className="w-3 h-3 mr-1.5 shrink-0" />{call.caller_number}</Button>
                                  ))}</div>}
                                </PopoverContent>
                              </Popover>
                            </div>
                          </div>
                          <div>
                            <Label>Phone 2</Label>
                            <PhoneInput value={newVictim.phone2 || ""} onChange={(e) => setNewVictim({ ...newVictim, phone2: formatPhoneNumber(e.target.value) })} className="input-dark" />
                          </div>
                        </div>
                        <div>
                          <Label>Victim Statement</Label>
                          <Textarea value={newVictim.statement || ""} onChange={(e) => setNewVictim({ ...newVictim, statement: e.target.value })} className="input-dark" />
                        </div>
                        <div>
                          <Label>Property Stolen</Label>
                          <Textarea value={newVictim.property_stolen || ""} onChange={(e) => setNewVictim({ ...newVictim, property_stolen: e.target.value })} className="input-dark" />
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox id="v_injuries" checked={newVictim.has_injuries || false} onCheckedChange={(c) => setNewVictim({ ...newVictim, has_injuries: c })} />
                          <Label htmlFor="v_injuries">Injuries?</Label>
                        </div>
                        {newVictim.has_injuries && (
                          <>
                            <div>
                              <Label>Injuries Description</Label>
                              <Textarea value={newVictim.injuries_description || ""} onChange={(e) => setNewVictim({ ...newVictim, injuries_description: e.target.value })} className="input-dark" />
                            </div>
                            <div>
                              <Label>Medical Treatment</Label>
                              <Textarea value={newVictim.medical_treatment || ""} onChange={(e) => setNewVictim({ ...newVictim, medical_treatment: e.target.value })} className="input-dark" />
                            </div>
                          </>
                        )}
                        <div className="grid grid-cols-2 gap-4">
                          <div className="flex items-center gap-2">
                            <Checkbox id="v_police" checked={newVictim.on_police_report || false} onCheckedChange={(c) => setNewVictim({ ...newVictim, on_police_report: c })} />
                            <Label htmlFor="v_police">On Police Report?</Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox id="v_interview" checked={newVictim.interviewed_on_scene || false} onCheckedChange={(c) => setNewVictim({ ...newVictim, interviewed_on_scene: c })} />
                            <Label htmlFor="v_interview">Interviewed on Scene?</Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox id="v_recognize" checked={newVictim.can_recognize_perp || false} onCheckedChange={(c) => setNewVictim({ ...newVictim, can_recognize_perp: c })} />
                            <Label htmlFor="v_recognize">Can Recognize Perp?</Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox id="v_minor" checked={newVictim.is_minor_or_elderly || false} onCheckedChange={(c) => setNewVictim({ ...newVictim, is_minor_or_elderly: c })} />
                            <Label htmlFor="v_minor">Minor or Elderly?</Label>
                          </div>
                        </div>
                        {newVictim.is_minor_or_elderly && (
                          <div className="p-3 bg-slate-950 rounded space-y-3">
                            <p className="text-sm font-medium text-amber-400">Guardian Information</p>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label>Guardian Name</Label>
                                <Input value={newVictim.guardian_name || ""} onChange={(e) => setNewVictim({ ...newVictim, guardian_name: e.target.value })} className="input-dark" />
                              </div>
                              <div>
                                <Label>Guardian Email</Label>
                                <Input value={newVictim.guardian_email || ""} onChange={(e) => setNewVictim({ ...newVictim, guardian_email: e.target.value })} className="input-dark" />
                              </div>
                            </div>
                            <div>
                              <Label>Guardian Address</Label>
                              <Input value={newVictim.guardian_address || ""} onChange={(e) => setNewVictim({ ...newVictim, guardian_address: e.target.value })} className="input-dark" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label>Guardian Phone 1</Label>
                                <PhoneInput value={newVictim.guardian_phone1 || ""} onChange={(e) => setNewVictim({ ...newVictim, guardian_phone1: formatPhoneNumber(e.target.value) })} className="input-dark" />
                              </div>
                              <div>
                                <Label>Guardian Phone 2</Label>
                                <PhoneInput value={newVictim.guardian_phone2 || ""} onChange={(e) => setNewVictim({ ...newVictim, guardian_phone2: formatPhoneNumber(e.target.value) })} className="input-dark" />
                              </div>
                            </div>
                            <div>
                              <Label>Guardian Statement</Label>
                              <Textarea value={newVictim.guardian_statement || ""} onChange={(e) => setNewVictim({ ...newVictim, guardian_statement: e.target.value })} className="input-dark" />
                            </div>
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Direction of Flight Prior</Label>
                            <Input value={newVictim.direction_of_flight_prior || ""} onChange={(e) => setNewVictim({ ...newVictim, direction_of_flight_prior: e.target.value })} className="input-dark" />
                          </div>
                          <div>
                            <Label>Direction of Flight Post</Label>
                            <Input value={newVictim.direction_of_flight_post || ""} onChange={(e) => setNewVictim({ ...newVictim, direction_of_flight_post: e.target.value })} className="input-dark" />
                          </div>
                        </div>
                        <Button onClick={addVictim} className="w-full btn-primary">Add Victim</Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </CardContent>
              </Card>
              )}

              {/* Witnesses */}
              {incident && (
              <Card className="form-section">
                <CardContent className="p-4">
                  <div className="max-h-[80vh] overflow-y-auto">
                    {incident.witnesses?.map((witness) => (
                      <div key={witness.witness_id} className="p-3 bg-slate-950 rounded-sm mb-2"
                        ref={editingWitnessId === witness.witness_id ? (el) => setTimeout(() => el?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 50) : undefined}
                      >
                        {editingWitnessId === witness.witness_id ? (
                          // Edit mode
                          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs">Name</Label>
                                <Input value={witness.name || ""} onChange={(e) => updateWitness(witness.witness_id, { name: e.target.value })} className="input-dark h-8" />
                              </div>
                              <div>
                                <Label className="text-xs">Contact Info</Label>
                                <PhoneInput value={witness.contact_info || ""} onChange={(e) => { const v = e.target.value; updateWitness(witness.witness_id, { contact_info: /^[\d\s\-\(\)\+]*$/.test(v) ? formatPhoneNumber(v) : v }); }} className="input-dark h-8" />
                              </div>
                            </div>
                            <div>
                              <Label className="text-xs">Location</Label>
                              <Input value={witness.location || ""} onChange={(e) => updateWitness(witness.witness_id, { location: e.target.value })} className="input-dark h-8" />
                            </div>
                            <div>
                              <Label className="text-xs">Statement</Label>
                              <Textarea value={witness.statement || ""} onChange={(e) => updateWitness(witness.witness_id, { statement: e.target.value })} className="input-dark" rows={2} />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <div className="flex items-center gap-2">
                                <Checkbox checked={witness.on_police_report || false} onCheckedChange={(c) => updateWitness(witness.witness_id, { on_police_report: c })} />
                                <Label className="text-xs">On Police Report?</Label>
                              </div>
                              <div className="flex items-center gap-2">
                                <Checkbox checked={witness.already_interviewed || false} onCheckedChange={(c) => updateWitness(witness.witness_id, { already_interviewed: c })} />
                                <Label className="text-xs">Already Interviewed?</Label>
                              </div>
                              <div className="flex items-center gap-2">
                                <Checkbox checked={witness.interviewed_on_scene || false} onCheckedChange={(c) => updateWitness(witness.witness_id, { interviewed_on_scene: c })} />
                                <Label className="text-xs">Interviewed on Scene?</Label>
                              </div>
                            </div>
                            <div>
                              <Label className="text-xs">Response / Follow-up</Label>
                              <Textarea value={witness.response || ""} onChange={(e) => updateWitness(witness.witness_id, { response: e.target.value })} className="input-dark" rows={2} placeholder="Notes from callback or follow-up..." />
                            </div>
                            <div className="flex gap-2 sticky bottom-0 bg-slate-950 pt-2 pb-1 z-10">
                              <Button size="sm" onClick={() => saveWitnessEdit(witness.witness_id)} className="btn-primary">
                                <Check className="w-3 h-3 mr-1" />Save
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setEditingWitnessId(null)}>Cancel</Button>
                            </div>
                          </div>
                        ) : (
                          // View mode
                          <>
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <p className="font-medium">{witness.name}</p>
                                {witness.contact_info && (
                                  /^\+?[\d\s\-\(\)]{7,}$/.test(witness.contact_info.trim()) ? (
                                    <div className="flex items-center gap-1">
                                      <button className="text-xs text-blue-400 hover:underline bg-transparent border-none cursor-pointer p-0" onClick={() => setPhoneLookupNumber(witness.contact_info)}>{witness.contact_info}</button>
                                      <button className="p-0.5 rounded hover:bg-slate-700 text-slate-400 hover:text-slate-200 shrink-0" title="Copy number" onClick={async (e) => { e.stopPropagation(); const ok = await copyPhoneNumber(witness.contact_info); if (ok) toast.success("Phone number copied"); else toast.error("Couldn't copy"); }}><Copy className="h-3 w-3" /></button>
                                    </div>
                                  ) : (
                                    <p className="text-xs text-slate-400">{witness.contact_info}</p>
                                  )
                                )}
                              </div>
                              <div className="flex gap-1">
                                <Button variant="ghost" size="sm" onClick={() => setEditingWitnessId(witness.witness_id)}>
                                  <Edit className="w-4 h-4 text-blue-400" />
                                </Button>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button variant="ghost" size="sm"><Trash2 className="w-4 h-4 text-red-500" /></Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent className="bg-slate-900 border-slate-700">
                                    <AlertDialogHeader>
                                      <AlertDialogTitle className="text-red-400">Remove Witness?</AlertDialogTitle>
                                      <AlertDialogDescription>Remove <span className="text-white font-medium">{witness.name || "this witness"}</span>?</AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel className="bg-slate-700 border-slate-600">Cancel</AlertDialogCancel>
                                      <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => removeWitness(witness.witness_id)}>Remove</AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            </div>
                            {witness.location && <p className="text-sm text-slate-400">Location: {witness.location}</p>}
                            {witness.statement && <p className="text-sm mt-2 text-slate-300">{witness.statement}</p>}
                            <div className="flex flex-wrap gap-2 mt-2">
                              {witness.on_police_report && <Badge variant="outline" className="text-xs">On Police Report</Badge>}
                              {witness.already_interviewed && <Badge variant="outline" className="text-xs">Already Interviewed</Badge>}
                              {witness.interviewed_on_scene && <Badge variant="outline" className="text-xs">Interviewed on Scene</Badge>}
                            </div>
                            {/* Show response/follow-up notes */}
                            {witness.response && (
                              <div className="mt-2 p-2 bg-blue-950/30 border border-blue-900/50 rounded text-sm">
                                <p className="text-xs text-blue-400 font-medium mb-1">Response / Follow-up:</p>
                                <p className="text-slate-300">{witness.response}</p>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                  <Dialog open={showWitnessDialog} onOpenChange={setShowWitnessDialog}>
                    <DialogTrigger asChild>
                      <Button className="mt-4 btn-secondary w-full" data-testid="add-witness-btn">
                        <Plus className="w-4 h-4 mr-1" />Add Witness
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
                      <DialogHeader>
                        <DialogTitle>Add Witness Information</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div>
                          <Label>Name *</Label>
                          <Input value={newWitness.name || ""} onChange={(e) => setNewWitness({ ...newWitness, name: e.target.value })} className="input-dark" />
                        </div>
                        <div>
                          <Label>Contact Info</Label>
                          <div className="flex gap-1">
                            <PhoneInput value={newWitness.contact_info || ""} onChange={(e) => { const v = e.target.value; setNewWitness({ ...newWitness, contact_info: /^[\d\s\-\(\)\+]*$/.test(v) ? formatPhoneNumber(v) : v }); }} className="input-dark flex-1" placeholder="Phone or email" />
                            <Popover open={showRecentCallsFor === "witness"} onOpenChange={(open) => { setShowRecentCallsFor(open ? "witness" : null); if (open) fetchRecentCalls(); }}>
                              <PopoverTrigger asChild><Button variant="outline" size="sm" className="h-9 px-2" title="Recent calls"><PhoneIncoming className="w-3.5 h-3.5" /></Button></PopoverTrigger>
                              <PopoverContent className="w-72 p-2 bg-slate-900 border-slate-700 z-[9999]">
                                <p className="text-xs text-slate-400 mb-2">Recent Calls (30 min)</p>
                                {loadingRecentCalls ? <div className="text-center py-2"><RefreshCw className="w-4 h-4 animate-spin mx-auto" /></div>
                                : recentCalls.length === 0 ? <p className="text-xs text-slate-500 text-center py-2">No recent calls</p>
                                : <div className="space-y-1 max-h-48 overflow-y-auto">{recentCalls.map((call, i) => (
                                  <Button key={i} variant="ghost" size="sm" className="w-full justify-start text-xs h-7 px-2" onClick={() => { setNewWitness({ ...newWitness, contact_info: formatPhoneNumber(call.caller_number) }); setShowRecentCallsFor(null); }}><Phone className="w-3 h-3 mr-1.5 shrink-0" />{call.caller_number}</Button>
                                ))}</div>}
                              </PopoverContent>
                            </Popover>
                          </div>
                        </div>
                        <div>
                          <Label>Location</Label>
                          <Input value={newWitness.location || ""} onChange={(e) => setNewWitness({ ...newWitness, location: e.target.value })} className="input-dark" placeholder="Where witness was located" />
                        </div>
                        <div>
                          <Label>Statement / Notes</Label>
                          <Textarea value={newWitness.statement || ""} onChange={(e) => setNewWitness({ ...newWitness, statement: e.target.value })} className="input-dark" />
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Checkbox id="w_police" checked={newWitness.on_police_report || false} onCheckedChange={(c) => setNewWitness({ ...newWitness, on_police_report: c })} />
                            <Label htmlFor="w_police">On Police Report?</Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox id="w_already" checked={newWitness.already_interviewed || false} onCheckedChange={(c) => setNewWitness({ ...newWitness, already_interviewed: c })} />
                            <Label htmlFor="w_already">Already Interviewed?</Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox id="w_scene" checked={newWitness.interviewed_on_scene || false} onCheckedChange={(c) => setNewWitness({ ...newWitness, interviewed_on_scene: c })} />
                            <Label htmlFor="w_scene">Interviewed on Scene?</Label>
                          </div>
                        </div>
                        <Button onClick={addWitness} className="w-full btn-primary">Add Witness</Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </CardContent>
              </Card>
              )}
              </div>
            </TabsContent>

            {/* Perp Descriptions Tab */}
            <TabsContent value="perps" className="mt-4">
              {incident && (
                <>
                  {/* Live Call Description - shows perp descriptions from live call */}
                  {incident.live_call_description && (
                    <Card className="form-section mb-4">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm uppercase tracking-wider text-amber-400 flex items-center gap-2">
                          <Radio className="w-4 h-4" />
                          Live Call Description
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-4 pt-0">
                        <Textarea
                          value={incident.live_call_description || ""}
                          onChange={(e) => updateField("live_call_description", e.target.value)}
                          placeholder="Perpetrator descriptions from live call..."
                          className="input-dark min-h-[120px] font-mono text-sm"
                        />
                        <p className="text-xs text-slate-500 mt-2">
                          This contains all perpetrator descriptions captured during the live call.
                        </p>
                      </CardContent>
                    </Card>
                  )}

                  <Card className="form-section">
                <CardContent className="p-4">
                  {/* Show count and all perps with scrollable container */}
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs text-slate-400">
                      {incident.perp_descriptions?.length || 0} perpetrator{(incident.perp_descriptions?.length || 0) !== 1 ? 's' : ''}
                    </span>
                    {(incident.perp_descriptions?.length || 0) > 3 && (
                      <span className="text-xs text-amber-400">Scroll to see all</span>
                    )}
                  </div>
                  <div className="max-h-[60vh] overflow-y-auto">
                    <div className="space-y-2 pr-2">
                    {incident.perp_descriptions?.map((perp, perpIndex) => (
                      <div key={perp.perp_id} className="p-3 bg-slate-950 rounded-sm border border-slate-800"
                        ref={editingPerpId === perp.perp_id ? (el) => el?.scrollIntoView({ behavior: "smooth", block: "start" }) : undefined}
                      >
                        <div className="text-xs text-slate-500 mb-1">Perp #{perpIndex + 1}</div>
                        {editingPerpId === perp.perp_id ? (
                          // Edit mode
                          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
                            {/* Name and Address */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              <div><Label className="text-xs">Name (if known)</Label><Input value={perp.name || ""} onChange={(e) => updatePerpDesc(perp.perp_id, { name: e.target.value })} className="input-dark h-8" placeholder="Perpetrator name..." /></div>
                              <div><Label className="text-xs">Address (if known)</Label><Input value={perp.address || ""} onChange={(e) => updatePerpDesc(perp.perp_id, { address: e.target.value })} className="input-dark h-8" placeholder="Perpetrator address..." /></div>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                              <div><Label className="text-xs">Sex</Label><select value={perp.sex || ""} onChange={(e) => updatePerpDesc(perp.perp_id, { sex: e.target.value })} className="w-full h-8 rounded-md border border-slate-700 bg-slate-800 px-2 text-sm text-slate-200"><option value="">—</option><option value="Male">Male</option><option value="Female">Female</option></select></div>
                              <div><Label className="text-xs">Race</Label><select value={perp.race || ""} onChange={(e) => updatePerpDesc(perp.perp_id, { race: e.target.value })} className="w-full h-8 rounded-md border border-slate-700 bg-slate-800 px-2 text-sm text-slate-200"><option value="">—</option><option value="Black">Black</option><option value="White">White</option><option value="Hispanic">Hispanic</option><option value="Asian">Asian</option></select></div>
                              <div><Label className="text-xs">Age</Label><Input value={perp.age || ""} onChange={(e) => updatePerpDesc(perp.perp_id, { age: e.target.value })} className="input-dark h-8" /></div>
                              <div><Label className="text-xs">Height</Label><Input value={perp.height || ""} onChange={(e) => updatePerpDesc(perp.perp_id, { height: e.target.value })} className="input-dark h-8" /></div>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                              <div><Label className="text-xs">Weight</Label><Input value={perp.weight || ""} onChange={(e) => updatePerpDesc(perp.perp_id, { weight: e.target.value })} className="input-dark h-8" /></div>
                              <div><Label className="text-xs">Build</Label><select value={perp.build || ""} onChange={(e) => updatePerpDesc(perp.perp_id, { build: e.target.value })} className="w-full h-8 rounded-md border border-slate-700 bg-slate-800 px-2 text-sm text-slate-200"><option value="">—</option><option value="Slim">Slim</option><option value="Medium">Medium</option><option value="Heavy">Heavy</option></select></div>
                              <div><Label className="text-xs">Complexion</Label><Input value={perp.complexion || ""} onChange={(e) => updatePerpDesc(perp.perp_id, { complexion: e.target.value })} className="input-dark h-8" /></div>
                              <div><Label className="text-xs">Hair</Label><Input value={perp.hair_color_cut || ""} onChange={(e) => updatePerpDesc(perp.perp_id, { hair_color_cut: e.target.value })} className="input-dark h-8" /></div>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                              <div><Label className="text-xs">Eyes</Label><Input value={perp.eye_color || ""} onChange={(e) => updatePerpDesc(perp.perp_id, { eye_color: e.target.value })} className="input-dark h-8" /></div>
                              <div><Label className="text-xs">Hat</Label><Input value={perp.hat_color_style || ""} onChange={(e) => updatePerpDesc(perp.perp_id, { hat_color_style: e.target.value })} className="input-dark h-8" /></div>
                              <div><Label className="text-xs">Shirt</Label><Input value={perp.shirt || ""} onChange={(e) => updatePerpDesc(perp.perp_id, { shirt: e.target.value })} className="input-dark h-8" /></div>
                              <div><Label className="text-xs">Pants</Label><Input value={perp.pants || ""} onChange={(e) => updatePerpDesc(perp.perp_id, { pants: e.target.value })} className="input-dark h-8" /></div>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                              <div><Label className="text-xs">Jacket</Label><Input value={perp.jacket_coat || ""} onChange={(e) => updatePerpDesc(perp.perp_id, { jacket_coat: e.target.value })} className="input-dark h-8" /></div>
                              <div><Label className="text-xs">Shoes</Label><Input value={perp.shoes || ""} onChange={(e) => updatePerpDesc(perp.perp_id, { shoes: e.target.value })} className="input-dark h-8" /></div>
                              <div><Label className="text-xs">Marks/Tattoos</Label><Input value={perp.visible_marks || ""} onChange={(e) => updatePerpDesc(perp.perp_id, { visible_marks: e.target.value })} className="input-dark h-8" /></div>
                              <div><Label className="text-xs">Other Clothing</Label><Input value={perp.clothing || ""} onChange={(e) => updatePerpDesc(perp.perp_id, { clothing: e.target.value })} className="input-dark h-8" /></div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              <div><Label className="text-xs">Vehicle</Label><Input value={perp.vehicle_description || ""} onChange={(e) => updatePerpDesc(perp.perp_id, { vehicle_description: e.target.value })} className="input-dark h-8" /></div>
                              <div><Label className="text-xs">License Plate</Label><Input value={perp.license_plate || ""} onChange={(e) => updatePerpDesc(perp.perp_id, { license_plate: e.target.value })} className="input-dark h-8" /></div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div><Label className="text-xs">Dir of Flight</Label><Input value={perp.direction_of_flight || ""} onChange={(e) => updatePerpDesc(perp.perp_id, { direction_of_flight: e.target.value })} className="input-dark h-8" /></div>
                              <div><Label className="text-xs">Dir of Approach</Label><Input value={perp.direction_of_approach || ""} onChange={(e) => updatePerpDesc(perp.perp_id, { direction_of_approach: e.target.value })} className="input-dark h-8" /></div>
                            </div>
                            <div><Label className="text-xs">Notes</Label><Textarea value={perp.notes || ""} onChange={(e) => updatePerpDesc(perp.perp_id, { notes: e.target.value })} className="input-dark" rows={2} /></div>
                            <div className="flex items-center gap-2"><Checkbox checked={perp.has_weapon || false} onCheckedChange={(c) => updatePerpDesc(perp.perp_id, { has_weapon: c })} /><Label className="text-xs text-red-400">Has Weapon?</Label></div>
                            {perp.has_weapon && (
                              <div><Label className="text-xs">Weapon Description</Label><Input value={perp.weapon_description || ""} onChange={(e) => updatePerpDesc(perp.perp_id, { weapon_description: e.target.value })} className="input-dark h-8" placeholder="Describe weapon..." /></div>
                            )}
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                              <div className="flex items-center gap-2"><Checkbox checked={perp.victim_can_recognize || false} onCheckedChange={(c) => updatePerpDesc(perp.perp_id, { victim_can_recognize: c })} /><Label className="text-xs">Victim Can ID?</Label></div>
                              <div className="flex items-center gap-2"><Checkbox checked={perp.identifiable_via_camera || false} onCheckedChange={(c) => updatePerpDesc(perp.perp_id, { identifiable_via_camera: c })} /><Label className="text-xs">On Camera?</Label></div>
                              <div className="flex items-center gap-2"><Checkbox checked={perp.has_warrant || false} onCheckedChange={(c) => updatePerpDesc(perp.perp_id, { has_warrant: c })} /><Label className="text-xs">Has Warrant?</Label></div>
                              <div className="flex items-center gap-2"><Checkbox checked={perp.ready_for_identification || false} onCheckedChange={async (c) => {
                                updatePerpDesc(perp.perp_id, { ready_for_identification: c });
                                if (c && perp.linked_profile_id) {
                                  try {
                                    await axios.post(`${API}/perpetrator-profiles/${perp.linked_profile_id}/ready-for-arrest`, {
                                      incident_id: incident.incident_id, incident_type: incident.incident_type,
                                      secondary_incident_type: incident.secondary_incident_type || "",
                                      headline: incident.headline, address: incident.address, date_time: incident.date_time,
                                      identification_methods: perp.identification_methods || [],
                                      report_filed: perp.report_filed || false,
                                      perp_description: `${perp.sex || ''} ${perp.race || ''} ${perp.age || ''}`.trim()
                                    }, { withCredentials: true });
                                    toast.success("Ready for arrest synced to profile");
                                  } catch (e) { console.error("Failed to sync ready to profile:", e); }
                                } else if (!c && perp.linked_profile_id) {
                                  try {
                                    await axios.delete(`${API}/perpetrator-profiles/${perp.linked_profile_id}/ready-for-arrest/${incident.incident_id}`, { withCredentials: true });
                                    toast.success("Ready for arrest removed from profile");
                                  } catch (e) { console.error("Failed to remove ready from profile:", e); }
                                }
                              }} /><Label className="text-xs">Ready for ID?</Label></div>
                            </div>
                            <div className="flex gap-2 sticky bottom-0 bg-slate-950 pt-2 pb-1 z-10">
                              <Button size="sm" onClick={() => savePerpEdit(perp.perp_id)} className="btn-primary"><Check className="w-3 h-3 mr-1" />Save</Button>
                              <Button size="sm" variant="outline" onClick={() => setEditingPerpId(null)}>Cancel</Button>
                            </div>
                          </div>
                        ) : (
                          // View mode - show ALL fields that have data
                          <>
                            {/* Header row with arrested badge and actions */}
                            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                              <div className="flex items-center gap-2">
                                {/* Arrested status badge */}
                                {perp.was_arrested ? (
                                  <Badge 
                                    className="bg-emerald-600 text-white cursor-pointer hover:bg-emerald-700"
                                    onClick={() => togglePerpArrest(perp.perp_id, false)}
                                  >
                                    ✓ ARRESTED
                                  </Badge>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-6 text-xs border-emerald-600 text-emerald-400 hover:bg-emerald-900/30"
                                    onClick={() => togglePerpArrest(perp.perp_id, true)}
                                  >
                                    Mark Arrested
                                  </Button>
                                )}
                                {perp.linked_profile_id && (
                                  <Badge variant="outline" className="text-purple-400 border-purple-500 text-[10px]">
                                    LINKED
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-1">
                                <Button variant="ghost" size="sm" onClick={() => setEditingPerpId(perp.perp_id)}>
                                  <Edit className="w-4 h-4 text-blue-400" />
                                </Button>
                                <Button 
                                  type="button"
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    removePerpDesc(perp.perp_id);
                                  }}
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </div>
                            </div>
                            
                            {/* Physical Description */}
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm mb-2">
                              {perp.sex && <span>Sex: {perp.sex}</span>}
                              {perp.race && <span>Race: {perp.race}</span>}
                              {perp.age && <span>Age: {perp.age}</span>}
                              {perp.height && <span>Height: {perp.height}</span>}
                              {perp.weight && <span>Weight: {perp.weight}</span>}
                              {perp.build && <span>Build: {perp.build}</span>}
                              {perp.complexion && <span>Complexion: {perp.complexion}</span>}
                              {perp.hair_color_cut && <span>Hair: {perp.hair_color_cut}</span>}
                              {perp.facial_hair && <span>Facial Hair: {perp.facial_hair}</span>}
                              {perp.eye_color && <span>Eyes: {perp.eye_color}</span>}
                              {perp.eyes_state && <span>Eyes State: {perp.eyes_state}</span>}
                              {perp.glasses && <span>Glasses: {perp.glasses}</span>}
                            </div>
                            
                            {/* Show Name and Address if known */}
                            {(perp.name || perp.address) && (
                              <div className="mt-2 p-2 bg-blue-950/30 border border-blue-900/50 rounded text-sm">
                                {perp.name && <p className="text-blue-300 font-medium">Name: {perp.name}</p>}
                                {perp.address && <p className="text-blue-300">Address: {perp.address}</p>}
                              </div>
                            )}
                            
                            {/* Show Linked Profile if exists */}
                            {perp.linked_profile_id && (
                              <div className="mt-2 p-2 bg-purple-950/30 border border-purple-800/50 rounded text-sm">
                                <div className="flex items-center justify-between gap-2">
                                  <div 
                                    className="flex items-center gap-2 cursor-pointer hover:opacity-80 flex-1"
                                    onClick={() => navigate(`/perpetrators/${perp.linked_profile_id}`)}
                                  >
                                    <LinkIcon className="w-4 h-4 text-purple-400" />
                                    <span className="text-purple-300 font-medium">
                                      Linked: {linkedProfileDetails[perp.linked_profile_id]?.name || perp.linked_profile_name || 'Unknown'}
                                    </span>
                                    <ExternalLink className="w-3 h-3 text-purple-400" />
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-2 text-red-400 hover:text-red-300 hover:bg-red-900/30"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      unlinkProfileFromPerp(perp.perp_id);
                                    }}
                                  >
                                    <X className="w-3 h-3 mr-1" />
                                    Unlink
                                  </Button>
                                </div>
                              </div>
                            )}
                            
                            {/* Link to Existing Profile button - only show if not already linked */}
                            {!perp.linked_profile_id && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="mt-2 text-purple-400 border-purple-700 hover:bg-purple-900/30"
                                onClick={() => {
                                  setLinkProfilePerp(perp);
                                  setShowLinkProfileDialog(true);
                                }}
                              >
                                <LinkIcon className="w-3 h-3 mr-1" />
                                Link to Existing Profile
                              </Button>
                            )}
                            
                            {/* Clothing */}
                            {(perp.hat_color_style || perp.shirt || perp.necktie || perp.pants || perp.jacket_coat || perp.shoes || perp.clothing || perp.visible_marks) && (
                              <div className="mt-2">
                                <p className="text-xs text-slate-500 font-medium mb-1">Clothing & Marks:</p>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs text-slate-400">
                                  {perp.hat_color_style && <span>Hat: {perp.hat_color_style}</span>}
                                  {perp.shirt && <span>Shirt: {perp.shirt}</span>}
                                  {perp.necktie && <span>Necktie: {perp.necktie}</span>}
                                  {perp.pants && <span>Pants: {perp.pants}</span>}
                                  {perp.jacket_coat && <span>Jacket: {perp.jacket_coat}</span>}
                                  {perp.shoes && <span>Shoes: {perp.shoes}</span>}
                                  {perp.clothing && <span>Clothing: {perp.clothing}</span>}
                                  {perp.visible_marks && <span className="col-span-2">Marks: {perp.visible_marks}</span>}
                                </div>
                              </div>
                            )}
                            
                            {/* Description / Current Location */}
                            {(perp.description || perp.current_location) && (
                              <div className="mt-2 text-sm text-slate-400">
                                {perp.current_location && <p>Current Location: {perp.current_location}</p>}
                                {perp.description && <p>Description: {perp.description}</p>}
                              </div>
                            )}

                            {/* Vehicle */}
                            {(perp.vehicle_description || perp.license_plate) && (
                              <div className="mt-2">
                                {perp.vehicle_description && <p className="text-sm text-slate-400">Vehicle: {perp.vehicle_description}</p>}
                                {perp.license_plate && <p className="text-sm text-amber-400">Plate: {perp.license_plate}</p>}
                              </div>
                            )}
                            
                            {/* Direction info */}
                            {(perp.direction_of_flight || perp.direction_of_approach) && (
                              <div className="mt-2">
                                <p className="text-xs text-slate-500 font-medium mb-1">Direction:</p>
                                <div className="text-xs text-slate-400">
                                  {perp.direction_of_flight && <p>Flight: {perp.direction_of_flight}</p>}
                                  {perp.direction_of_approach && <p>Approach: {perp.direction_of_approach}</p>}
                                </div>
                              </div>
                            )}
                            
                            {/* Show perp notes if exists */}
                            {perp.notes && (
                              <div className="mt-2 p-2 bg-slate-900 rounded text-sm">
                                <p className="text-xs text-slate-500 font-medium mb-1">Notes:</p>
                                <p className="text-slate-300">{perp.notes}</p>
                              </div>
                            )}
                            <div className="flex flex-wrap gap-2 mt-2">
                              {perp.was_arrested && <Badge className="bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 text-xs">ARRESTED</Badge>}
                              {perp.has_weapon && <Badge variant="destructive" className="text-xs">WEAPON{perp.weapon_description ? `: ${perp.weapon_description}` : ''}</Badge>}
                              {perp.victim_can_recognize && <Badge variant="outline" className="text-xs">Victim Can ID</Badge>}
                              {perp.identifiable_via_camera && <Badge variant="outline" className="text-xs">On Camera</Badge>}
                              {perp.has_warrant && <Badge className="bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 text-xs">WARRANT</Badge>}
                              {perp.ready_for_identification && <Badge className="bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 text-xs">READY FOR ID</Badge>}
                            </div>

                            {/* Show arrest via info if this incident's ready was linked to an arrest in another incident */}
                            {(() => {
                              if ((!perp.ready_for_identification && !perp.identified_for_arrest) || !perp.linked_profile_id) return null;
                              const profile = linkedProfileDetails[perp.linked_profile_id];
                              if (!profile?.ready_for_arrest) return null;
                              const rfaForThisIncident = profile.ready_for_arrest.find(r => r.incident_id === incident.incident_id);
                              if (!rfaForThisIncident?.arrest_linked) return null;
                              const al = rfaForThisIncident.arrest_linked;
                              return (
                                <div className="mt-2 p-2 bg-emerald-950/30 border border-emerald-700/50 rounded">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Badge className="bg-emerald-600 text-white text-xs">ARRESTED VIA</Badge>
                                  </div>
                                  <p className="text-sm text-emerald-300 font-medium">{al.headline || al.incident_type || 'Incident'}</p>
                                  {al.address && <p className="text-xs text-slate-400">{al.address}</p>}
                                  {al.arrest_date && (
                                    <p className="text-xs text-emerald-400 mt-0.5">
                                      Arrested: {new Date(al.arrest_date).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}
                                    </p>
                                  )}
                                  {al.incident_id && al.incident_id !== incident.incident_id && (
                                    <Button
                                      variant="link"
                                      size="sm"
                                      className="text-xs text-emerald-400 hover:text-emerald-300 p-0 h-auto mt-1"
                                      onClick={() => navigate(`/incidents/${al.incident_id}`)}
                                    >
                                      View Arrest Incident →
                                    </Button>
                                  )}
                                </div>
                              );
                            })()}

                            {/* Show linked arrest info if exists */}
                            {(() => {
                              const linkedArrest = incident.arrests?.find(a => a.linked_perp_id === perp.perp_id);
                              if (!linkedArrest) return null;
                              return (
                                <div className="mt-3 p-2 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900/50 rounded-sm">
                                  <p className="text-xs text-emerald-700 dark:text-emerald-400 font-medium mb-2">ARREST INFO</p>
                                  <div className="flex flex-wrap gap-2">
                                    {linkedArrest.was_live && <Badge className="bg-blue-900/50 text-blue-200 text-xs">LIVE ARREST</Badge>}
                                    {linkedArrest.perp_stopped_by_pd_only && <Badge variant="outline" className="text-xs">PD Only</Badge>}
                                    {linkedArrest.outstanding_perpetrators && <Badge variant="destructive" className="text-xs">OUTSTANDING</Badge>}
                                  </div>
                                  {linkedArrest.identification_by?.length > 0 && (
                                    <div className="mt-2">
                                      <p className="text-xs text-slate-500">ID by: {linkedArrest.identification_by.join(", ")}</p>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </>
                        )}
                      </div>
                    ))}
                    </div>
                  </div>
                  <Dialog open={showPerpDescDialog} onOpenChange={setShowPerpDescDialog}>
                    <DialogTrigger asChild>
                      <Button className="mt-4 btn-secondary w-full" data-testid="add-perp-btn">
                        <Plus className="w-4 h-4 mr-1" />Add Perpetrator Description
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-slate-900 border-slate-800 max-w-3xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Add Perpetrator Description</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        {/* Name and Address (if known) */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <Label>Name (if known)</Label>
                            <Input value={newPerpDesc.name || ""} onChange={(e) => setNewPerpDesc({ ...newPerpDesc, name: e.target.value })} className="input-dark" placeholder="Perpetrator name if known..." />
                          </div>
                          <div>
                            <Label>Address (if known)</Label>
                            <Input value={newPerpDesc.address || ""} onChange={(e) => setNewPerpDesc({ ...newPerpDesc, address: e.target.value })} className="input-dark" placeholder="Perpetrator address if known..." />
                          </div>
                        </div>
                        
                        {/* Physical Description Row 1 */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          <div>
                            <Label>Sex</Label>
                            <select value={newPerpDesc.sex || ""} onChange={(e) => setNewPerpDesc({ ...newPerpDesc, sex: e.target.value })} className="w-full h-9 rounded-md border border-slate-700 bg-slate-800 px-2 text-sm text-slate-200">
                              <option value="">—</option>
                              <option value="Male">Male</option>
                              <option value="Female">Female</option>
                            </select>
                          </div>
                          <div>
                            <Label>Race</Label>
                            <select value={newPerpDesc.race || ""} onChange={(e) => setNewPerpDesc({ ...newPerpDesc, race: e.target.value })} className="w-full h-9 rounded-md border border-slate-700 bg-slate-800 px-2 text-sm text-slate-200">
                              <option value="">—</option>
                              <option value="Black">Black</option>
                              <option value="White">White</option>
                              <option value="Hispanic">Hispanic</option>
                              <option value="Asian">Asian</option>
                            </select>
                          </div>
                          <div>
                            <Label>Age</Label>
                            <Input value={newPerpDesc.age || ""} onChange={(e) => setNewPerpDesc({ ...newPerpDesc, age: e.target.value })} className="input-dark" placeholder="e.g., 25-30" />
                          </div>
                          <div>
                            <Label>Height</Label>
                            <Input value={newPerpDesc.height || ""} onChange={(e) => setNewPerpDesc({ ...newPerpDesc, height: e.target.value })} className="input-dark" placeholder="e.g., 5'10" />
                          </div>
                        </div>

                        {/* Physical Description Row 2 */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          <div>
                            <Label>Weight</Label>
                            <Input value={newPerpDesc.weight || ""} onChange={(e) => setNewPerpDesc({ ...newPerpDesc, weight: e.target.value })} className="input-dark" placeholder="e.g., 180 lbs" />
                          </div>
                          <div>
                            <Label>Build</Label>
                            <select value={newPerpDesc.build || ""} onChange={(e) => setNewPerpDesc({ ...newPerpDesc, build: e.target.value })} className="w-full h-9 rounded-md border border-slate-700 bg-slate-800 px-2 text-sm text-slate-200">
                              <option value="">—</option>
                              <option value="Slim">Slim</option>
                              <option value="Medium">Medium</option>
                              <option value="Heavy">Heavy</option>
                            </select>
                          </div>
                          <div>
                            <Label>Complexion</Label>
                            <Input value={newPerpDesc.complexion || ""} onChange={(e) => setNewPerpDesc({ ...newPerpDesc, complexion: e.target.value })} className="input-dark" placeholder="e.g., Light, Dark" />
                          </div>
                          <div>
                            <Label>Hair</Label>
                            <Input value={newPerpDesc.hair_color_cut || ""} onChange={(e) => setNewPerpDesc({ ...newPerpDesc, hair_color_cut: e.target.value })} className="input-dark" placeholder="e.g., Black, short" />
                          </div>
                        </div>
                        
                        {/* Physical Description Row 3 */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          <div>
                            <Label>Eye Color</Label>
                            <Input value={newPerpDesc.eye_color || ""} onChange={(e) => setNewPerpDesc({ ...newPerpDesc, eye_color: e.target.value })} className="input-dark" />
                          </div>
                          <div>
                            <Label>Visible Marks/Tattoos</Label>
                            <Input value={newPerpDesc.visible_marks || ""} onChange={(e) => setNewPerpDesc({ ...newPerpDesc, visible_marks: e.target.value })} className="input-dark" />
                          </div>
                          <div>
                            <Label>License Plate</Label>
                            <Input value={newPerpDesc.license_plate || ""} onChange={(e) => setNewPerpDesc({ ...newPerpDesc, license_plate: e.target.value })} className="input-dark" />
                          </div>
                          <div>
                            <Label>Vehicle Description</Label>
                            <Input value={newPerpDesc.vehicle_description || ""} onChange={(e) => setNewPerpDesc({ ...newPerpDesc, vehicle_description: e.target.value })} className="input-dark" />
                          </div>
                        </div>
                        
                        {/* Clothing */}
                        <div className="p-3 bg-slate-800 rounded">
                          <Label className="text-xs text-slate-400 mb-2 block">CLOTHING</Label>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            <div>
                              <Label className="text-xs">Hat</Label>
                              <Input value={newPerpDesc.hat_color_style || ""} onChange={(e) => setNewPerpDesc({ ...newPerpDesc, hat_color_style: e.target.value })} className="input-dark h-8" placeholder="Color & style" />
                            </div>
                            <div>
                              <Label className="text-xs">Shirt</Label>
                              <Input value={newPerpDesc.shirt || ""} onChange={(e) => setNewPerpDesc({ ...newPerpDesc, shirt: e.target.value })} className="input-dark h-8" />
                            </div>
                            <div>
                              <Label className="text-xs">Pants</Label>
                              <Input value={newPerpDesc.pants || ""} onChange={(e) => setNewPerpDesc({ ...newPerpDesc, pants: e.target.value })} className="input-dark h-8" />
                            </div>
                            <div>
                              <Label className="text-xs">Jacket/Coat</Label>
                              <Input value={newPerpDesc.jacket_coat || ""} onChange={(e) => setNewPerpDesc({ ...newPerpDesc, jacket_coat: e.target.value })} className="input-dark h-8" />
                            </div>
                            <div>
                              <Label className="text-xs">Shoes</Label>
                              <Input value={newPerpDesc.shoes || ""} onChange={(e) => setNewPerpDesc({ ...newPerpDesc, shoes: e.target.value })} className="input-dark h-8" />
                            </div>
                            <div>
                              <Label className="text-xs">Other Clothing</Label>
                              <Input value={newPerpDesc.clothing || ""} onChange={(e) => setNewPerpDesc({ ...newPerpDesc, clothing: e.target.value })} className="input-dark h-8" />
                            </div>
                          </div>
                        </div>
                        
                        {/* Direction */}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label>Direction of Flight</Label>
                            <Input value={newPerpDesc.direction_of_flight || ""} onChange={(e) => setNewPerpDesc({ ...newPerpDesc, direction_of_flight: e.target.value })} className="input-dark" placeholder="e.g., Northbound on Main St" />
                          </div>
                          <div>
                            <Label>Direction of Approach</Label>
                            <Input value={newPerpDesc.direction_of_approach || ""} onChange={(e) => setNewPerpDesc({ ...newPerpDesc, direction_of_approach: e.target.value })} className="input-dark" placeholder="e.g., From the park" />
                          </div>
                        </div>
                        
                        {/* Notes */}
                        <div>
                          <Label>Notes</Label>
                          <Textarea value={newPerpDesc.notes || ""} onChange={(e) => setNewPerpDesc({ ...newPerpDesc, notes: e.target.value })} className="input-dark" rows={2} placeholder="Additional notes about the perpetrator..." />
                        </div>
                        
                        {/* Checkboxes */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          <div className="flex items-center gap-2">
                            <Checkbox id="p_arrested" checked={newPerpDesc.was_arrested || false} onCheckedChange={(c) => setNewPerpDesc({ ...newPerpDesc, was_arrested: c })} />
                            <Label htmlFor="p_arrested" className="text-emerald-400">Arrested?</Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox id="p_weapon" checked={newPerpDesc.has_weapon || false} onCheckedChange={(c) => setNewPerpDesc({ ...newPerpDesc, has_weapon: c })} />
                            <Label htmlFor="p_weapon" className="text-red-400">Weapon?</Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox id="p_warrant" checked={newPerpDesc.has_warrant || false} onCheckedChange={(c) => setNewPerpDesc({ ...newPerpDesc, has_warrant: c })} />
                            <Label htmlFor="p_warrant" className="text-amber-400">Has Warrant?</Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox id="p_ready_id" checked={newPerpDesc.ready_for_identification || false} onCheckedChange={(c) => setNewPerpDesc({ ...newPerpDesc, ready_for_identification: c })} />
                            <Label htmlFor="p_ready_id">Ready for ID?</Label>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          <div className="flex items-center gap-2">
                            <Checkbox id="p_victim_id" checked={newPerpDesc.victim_can_recognize || false} onCheckedChange={(c) => setNewPerpDesc({ ...newPerpDesc, victim_can_recognize: c })} />
                            <Label htmlFor="p_victim_id">Victim Can ID?</Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox id="p_camera" checked={newPerpDesc.identifiable_via_camera || false} onCheckedChange={(c) => setNewPerpDesc({ ...newPerpDesc, identifiable_via_camera: c })} />
                            <Label htmlFor="p_camera">On Camera?</Label>
                          </div>
                        </div>
                        
                        {/* Weapon Description (conditional) */}
                        {newPerpDesc.has_weapon && (
                          <div>
                            <Label className="text-red-400">Weapon Description</Label>
                            <Input value={newPerpDesc.weapon_description || ""} onChange={(e) => setNewPerpDesc({ ...newPerpDesc, weapon_description: e.target.value })} className="input-dark" placeholder="Describe weapon..." />
                          </div>
                        )}
                        
                        <Button onClick={addPerpDesc} className="w-full btn-primary">Add Perpetrator Description</Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </CardContent>
              </Card>
              </>
              )}
            </TabsContent>

            {/* Linked Profiles Tab */}
            <TabsContent value="profiles" className="mt-4">
              {incident && (
              <Card className="form-section">
                <CardContent className="p-4">
                  <ScrollArea className="max-h-64">
                    {/* Show profiles from linked_profiles array */}
                    {incident.linked_profiles?.map((profileId) => {
                      const profile = profiles.find(p => p.profile_id === profileId);
                      if (!profile) return null;
                      return (
                        <div 
                          key={profileId} 
                          className="p-3 bg-slate-950 rounded-sm mb-2 flex items-center gap-3"
                        >
                          <div
                            className="flex items-center gap-3 flex-1 cursor-pointer hover:opacity-80"
                            onClick={() => navigate(`/perpetrators?profile=${profileId}`)}
                          >
                            {profile.mugshot_url ? (
                              <img src={getMediaUrl(profile.mugshot_url)} alt={profile.name} className="w-12 h-12 object-cover rounded-sm" />
                            ) : (
                              <div className="w-12 h-12 bg-slate-800 rounded-sm flex items-center justify-center">
                                <User className="w-6 h-6 text-slate-600" />
                              </div>
                            )}
                            <div className="flex-1">
                              <p className="font-medium">{profile.name}</p>
                              <p className="text-xs text-slate-500">Linked to incident</p>
                              <div className="flex gap-1 mt-1">
                                {profile.status === "arrested" && <Badge className="bg-emerald-600 text-white text-xs">ARRESTED</Badge>}
                                {profile.status === "ready_for_arrest" && <Badge className="bg-amber-600 text-white text-xs">READY</Badge>}
                                {profile.status === "linked" && <Badge className="bg-blue-600 text-white text-xs">LINKED</Badge>}
                                {profile.status === "warrant" && <Badge variant="destructive" className="text-xs">WARRANT</Badge>}
                                {profile.has_arrest_warrant && profile.status !== "warrant" && <Badge variant="destructive" className="text-xs">WARRANT</Badge>}
                              </div>
                            </div>
                            <ExternalLink className="w-4 h-4 text-slate-500" />
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                            onClick={(e) => {
                              e.stopPropagation();
                              unlinkProfile(profileId);
                            }}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      );
                    })}
                    
                    {/* Show profiles linked via perp_descriptions */}
                    {incident.perp_descriptions?.filter(p => p.linked_profile_id && !incident.linked_profiles?.includes(p.linked_profile_id)).map((perp) => {
                      const profile = profiles.find(p => p.profile_id === perp.linked_profile_id);
                      return (
                        <div 
                          key={`perp-${perp.perp_id}`} 
                          className="p-3 bg-purple-950/30 border border-purple-800/50 rounded-sm mb-2 flex items-center gap-3"
                        >
                          <div
                            className="flex items-center gap-3 flex-1 cursor-pointer hover:opacity-80"
                            onClick={() => navigate(`/perpetrators?profile=${perp.linked_profile_id}`)}
                          >
                            {profile?.mugshot_url ? (
                              <img src={getMediaUrl(profile.mugshot_url)} alt={perp.linked_profile_name} className="w-12 h-12 object-cover rounded-sm" />
                            ) : (
                              <div className="w-12 h-12 bg-purple-900/50 rounded-sm flex items-center justify-center">
                                <User className="w-6 h-6 text-purple-400" />
                              </div>
                            )}
                            <div className="flex-1">
                              <p className="font-medium text-purple-300">{linkedProfileDetails[perp.linked_profile_id]?.name || perp.linked_profile_name || profile?.name || 'Unknown'}</p>
                              <p className="text-xs text-purple-400">Linked via perpetrator description</p>
                              <div className="flex gap-1 mt-1">
                                {profile?.status === "arrested" && <Badge className="bg-emerald-600 text-white text-xs">ARRESTED</Badge>}
                                {profile?.status === "ready_for_arrest" && <Badge className="bg-amber-600 text-white text-xs">READY</Badge>}
                                {profile?.status === "linked" && <Badge className="bg-blue-600 text-white text-xs">LINKED</Badge>}
                                {profile?.status === "warrant" && <Badge variant="destructive" className="text-xs">WARRANT</Badge>}
                                {profile?.has_arrest_warrant && profile?.status !== "warrant" && <Badge variant="destructive" className="text-xs">WARRANT</Badge>}
                              </div>
                            </div>
                            <ExternalLink className="w-4 h-4 text-purple-400" />
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                            onClick={(e) => {
                              e.stopPropagation();
                              unlinkProfileFromPerp(perp.perp_id);
                            }}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      );
                    })}
                    
                    {/* Show empty state if no linked profiles */}
                    {(!incident.linked_profiles?.length && !incident.perp_descriptions?.some(p => p.linked_profile_id)) && (
                      <p className="text-slate-500 text-sm text-center py-4">No linked profiles</p>
                    )}
                  </ScrollArea>
                  <Dialog open={showLinkProfile} onOpenChange={setShowLinkProfile}>
                    <DialogTrigger asChild>
                      <Button className="mt-4 btn-secondary w-full" data-testid="link-profile-btn">
                        <LinkIcon className="w-4 h-4 mr-1" />Link Profile
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-slate-900 border-slate-800">
                      <DialogHeader>
                        <DialogTitle>Link Perpetrator Profile</DialogTitle>
                      </DialogHeader>
                      <ScrollArea className="max-h-80 mt-4">
                        {profiles.filter(p => !incident.linked_profiles?.includes(p.profile_id)).map((profile) => (
                          <div
                            key={profile.profile_id}
                            onClick={() => linkProfile(profile.profile_id)}
                            className="p-3 bg-slate-950 rounded-sm mb-2 flex items-center gap-3 cursor-pointer hover:bg-slate-800"
                          >
                            {profile.mugshot_url ? (
                              <img src={getMediaUrl(profile.mugshot_url)} alt={profile.name} className="w-10 h-10 object-cover rounded-sm" />
                            ) : (
                              <div className="w-10 h-10 bg-slate-800 rounded-sm flex items-center justify-center">
                                <User className="w-5 h-5 text-slate-600" />
                              </div>
                            )}
                            <div className="flex-1">
                              <span className="font-medium">{profile.name}</span>
                              <div className="flex gap-1 mt-1">
                                {profile.status === "arrested" && <Badge className="bg-emerald-600 text-white text-xs">ARRESTED</Badge>}
                                {profile.status === "ready_for_arrest" && <Badge className="bg-amber-600 text-white text-xs">READY</Badge>}
                                {profile.status === "linked" && <Badge className="bg-blue-600 text-white text-xs">LINKED</Badge>}
                                {profile.status === "warrant" && <Badge variant="destructive" className="text-xs">WARRANT</Badge>}
                                {profile.has_arrest_warrant && profile.status !== "warrant" && <Badge variant="destructive" className="text-xs">WARRANT</Badge>}
                              </div>
                            </div>
                          </div>
                        ))}
                      </ScrollArea>
                    </DialogContent>
                  </Dialog>
                </CardContent>
              </Card>
              )}
            </TabsContent>

            {/* Arrests Tab */}
            <TabsContent value="arrests" className="mt-4">
              {incident && (
              <Card className="form-section">
                <CardContent className="p-4">
                  <div className="max-h-[70vh] overflow-y-auto">
                    {incident.arrests?.filter(a => !a.is_linked_arrest).map((arrest) => {
                      if (arrest.is_linked_arrest) {
                        return (
                          <div key={arrest.arrest_id} className="p-3 bg-emerald-950/20 rounded-sm mb-2 border border-emerald-700/50">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className="bg-emerald-600 text-white text-xs">LINKED ARREST</Badge>
                              <span className="text-xs text-slate-400">{arrest.perp_description_summary}</span>
                            </div>
                            {arrest.source_incident_id && (
                              <div className="mb-2">
                                <p className="text-xs text-slate-500">Arrested in: <span className="text-emerald-300 font-medium">{arrest.source_headline || arrest.source_incident_type || ''}</span></p>
                                {arrest.source_address && <p className="text-xs text-slate-400">{arrest.source_address}</p>}
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="text-xs text-emerald-400 hover:text-emerald-300 p-0 h-auto mt-1"
                                  onClick={() => navigate(`/incidents/${arrest.source_incident_id}`)}
                                >
                                  {arrest.source_incident_id} — View Arrest Incident →
                                </Button>
                              </div>
                            )}
                            {arrest.arrest_date && (
                              <p className="text-xs text-emerald-400">
                                <Calendar className="w-3 h-3 inline mr-1" />
                                Arrested: {new Date(arrest.arrest_date).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}
                              </p>
                            )}
                          </div>
                        );
                      }

                      // Find linked perp for more details
                      const linkedPerp = incident.perp_descriptions?.find(p => p.perp_id === arrest.linked_perp_id);
                      // Get profile ID from perp or arrest
                      const profileId = linkedPerp?.linked_profile_id || arrest.linked_profile_id;
                      // Get profile's ready_for_arrest records (use full profile details, not list data)
                      const linkedProfile = profileId ? (linkedProfileDetails[profileId] || profiles.find(p => p.profile_id === profileId)) : null;
                      const readyForArrestRecords = linkedProfile?.ready_for_arrest?.filter(r => !r.arrest_linked || r.arrest_linked?.incident_id === incident.incident_id) || [];

                      return (
                        <div key={arrest.arrest_id} className="p-3 bg-slate-950 rounded-sm mb-2 border border-slate-800"
                          ref={editingArrestId === arrest.arrest_id ? (el) => el?.scrollIntoView({ behavior: "smooth", block: "start" }) : undefined}
                        >
                          {editingArrestId === arrest.arrest_id ? (
                            // Edit mode
                            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
                              {linkedPerp && (
                                <div className="mb-2 p-2 bg-purple-950/30 rounded-sm">
                                  <p className="text-xs text-purple-400 font-medium mb-1">LINKED PERPETRATOR</p>
                                  <div className="text-sm text-slate-300">
                                    {linkedPerp.sex && <span className="mr-2">Sex: {linkedPerp.sex}</span>}
                                    {linkedPerp.race && <span className="mr-2">Race: {linkedPerp.race}</span>}
                                    {linkedPerp.age && <span className="mr-2">Age: {linkedPerp.age}</span>}
                                    {linkedPerp.height && <span className="mr-2">Height: {linkedPerp.height}</span>}
                                  </div>
                                  {linkedPerp.linked_profile_name && (
                                    <p className="text-xs text-purple-300 mt-1">Profile: {linkedPerp.linked_profile_name}</p>
                                  )}
                                </div>
                              )}
                              
                              {/* Show Ready for Arrest records from linked profile */}
                              {linkedProfile && readyForArrestRecords.length > 0 && (
                                <div className="p-3 bg-amber-900/20 border border-amber-700/50 rounded">
                                  <p className="text-xs text-amber-400 font-medium mb-2">
                                    Link to Ready for Arrest Records ({linkedProfile.name})
                                  </p>
                                  <div className="space-y-2">
                                    {readyForArrestRecords.map((rfa, idx) => {
                                      const rfaId = rfa.id || rfa.incident_id;
                                      const linkedIds = arrest.linked_ready_for_arrest_ids || (arrest.linked_ready_for_arrest_id ? [arrest.linked_ready_for_arrest_id] : []);
                                      const isLinked = linkedIds.includes(rfaId);
                                      
                                      return (
                                      <div
                                        key={rfaId || idx}
                                        onClick={async () => {
                                          // Toggle this ID in the array
                                          let newLinkedIds;
                                          if (isLinked) {
                                            newLinkedIds = linkedIds.filter(id => id !== rfaId);
                                          } else {
                                            newLinkedIds = [...linkedIds, rfaId];
                                          }
                                          
                                          // Update local state
                                          updateArrest(arrest.arrest_id, { 
                                            linked_ready_for_arrest_ids: newLinkedIds,
                                            linked_ready_for_arrest_id: newLinkedIds[0] || null // Keep backward compat
                                          });
                                          
                                          // If linking (not unlinking), sync to profile
                                          if (!isLinked && linkedProfile?.profile_id) {
                                            try {
                                              await axios.post(
                                                `${API}/perpetrator-profiles/${linkedProfile.profile_id}/arrest`,
                                                {
                                                  incident_id: incident.incident_id,
                                                  incident_type: incident.incident_type,
                                                  secondary_incident_type: incident.secondary_incident_type || "",
                                                  headline: incident.headline,
                                                  address: incident.address,
                                                  date_time: incident.date_time,
                                                  arrest_date: arrest.arrest_date || new Date().toISOString(),
                                                  linked_ready_for_arrest_ids: newLinkedIds
                                                },
                                                { withCredentials: true }
                                              );
                                              toast.success("Arrest linked to Ready for Arrest record");
                                            } catch (error) {
                                              console.error("Failed to sync arrest to profile:", error);
                                              toast.error("Failed to link arrest");
                                            }
                                          } else if (isLinked && linkedProfile?.profile_id) {
                                            // Unlinking - update profile to clear this specific link
                                            try {
                                              await axios.post(
                                                `${API}/perpetrator-profiles/${linkedProfile.profile_id}/arrest`,
                                                {
                                                  incident_id: incident.incident_id,
                                                  incident_type: incident.incident_type,
                                                  secondary_incident_type: incident.secondary_incident_type || "",
                                                  headline: incident.headline,
                                                  address: incident.address,
                                                  date_time: incident.date_time,
                                                  arrest_date: arrest.arrest_date || new Date().toISOString(),
                                                  linked_ready_for_arrest_ids: newLinkedIds,
                                                  unlink_ready_for_arrest_id: rfaId
                                                },
                                                { withCredentials: true }
                                              );
                                              toast.success("Unlinked from Ready for Arrest record");
                                            } catch (error) {
                                              console.error("Failed to unlink:", error);
                                            }
                                          }
                                        }}
                                        className={`p-2 rounded cursor-pointer border ${
                                          isLinked
                                            ? 'bg-amber-900/40 border-amber-500'
                                            : 'bg-slate-800 border-slate-700 hover:border-amber-700'
                                        }`}
                                      >
                                        <div className="flex items-center gap-2">
                                          <Checkbox checked={isLinked} readOnly className="pointer-events-none" />
                                          <div className="flex-1">
                                            <p className="text-sm font-medium">
                                              {rfa.headline || rfa.incident_type || 'Ready for Arrest'}
                                            </p>
                                            <p className="text-xs text-slate-400">
                                              {rfa.address} {rfa.date_time ? `• ${new Date(rfa.date_time).toLocaleDateString('en-US', { timeZone: 'America/New_York' })}` : ''}
                                            </p>
                                            {rfa.identification_methods?.length > 0 && (
                                              <p className="text-xs text-slate-500">ID: {rfa.identification_methods.join(", ")}</p>
                                            )}
                                            {rfa.report_filed && (
                                              <Badge className="bg-blue-600 text-white text-xs mt-1">REPORT FILED</Badge>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    );})}
                                  </div>
                                  {(arrest.linked_ready_for_arrest_ids?.length > 0 || arrest.linked_ready_for_arrest_id) && (
                                    <p className="text-xs text-emerald-400 mt-2">✓ Arrest linked to {arrest.linked_ready_for_arrest_ids?.length || 1} Ready for Arrest record(s)</p>
                                  )}
                                </div>
                              )}
                              
                              {linkedProfile && readyForArrestRecords.length === 0 && (
                                <div className="p-2 bg-slate-800 rounded text-xs text-slate-400">
                                  No Ready for Arrest records on profile "{linkedProfile.name}"
                                </div>
                              )}
                              
                              {/* Arrest Date/Time */}
                              <div>
                                <Label className="text-xs">Arrest Date & Time</Label>
                                <Input
                                  type="datetime-local"
                                  value={arrest.arrest_date ? (() => {
                                    const d = new Date(arrest.arrest_date);
                                    const parts = new Intl.DateTimeFormat('en-US', {
                                      timeZone: 'America/New_York',
                                      year: 'numeric', month: '2-digit', day: '2-digit',
                                      hour: '2-digit', minute: '2-digit', hour12: false
                                    }).formatToParts(d);
                                    const get = (type) => parts.find(p => p.type === type).value;
                                    return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
                                  })() : ""}
                                  onChange={(e) => {
                                    if (e.target.value) {
                                      const localDate = new Date(e.target.value);
                                      updateArrest(arrest.arrest_id, { arrest_date: localDate.toISOString() });
                                    }
                                  }}
                                  className="input-dark h-8 text-sm"
                                />
                              </div>

                              <div className="grid grid-cols-2 gap-3">
                                <div className="flex items-center gap-2">
                                  <Checkbox checked={arrest.was_live || false} onCheckedChange={(c) => updateArrest(arrest.arrest_id, { was_live: c })} />
                                  <Label className="text-xs">Live Arrest?</Label>
                                </div>
                                {arrest.was_live && (
                                  <div className="flex items-center gap-2">
                                    <Checkbox checked={arrest.perp_stopped_by_pd_only || false} onCheckedChange={(c) => updateArrest(arrest.arrest_id, { perp_stopped_by_pd_only: c })} />
                                    <Label className="text-xs">PD Only?</Label>
                                  </div>
                                )}
                                <div className="flex items-center gap-2">
                                  <Checkbox checked={arrest.outstanding_perpetrators || false} onCheckedChange={(c) => updateArrest(arrest.arrest_id, { outstanding_perpetrators: c })} />
                                  <Label className="text-xs">Outstanding Perps?</Label>
                                </div>
                              </div>
                              <div>
                                <Label className="text-xs">Identification Methods</Label>
                                <div className="flex flex-wrap gap-2 mt-1">
                                  {IDENTIFICATION_METHODS.map(method => (
                                    <label key={method} className="flex items-center gap-1 text-xs">
                                      <Checkbox 
                                        checked={(arrest.identification_by || []).includes(method)} 
                                        onCheckedChange={(c) => {
                                          const current = arrest.identification_by || [];
                                          const updated = c ? [...current, method] : current.filter(m => m !== method);
                                          updateArrest(arrest.arrest_id, { identification_by: updated });
                                        }}
                                      />
                                      {method}
                                    </label>
                                  ))}
                                </div>
                              </div>
                              <div className="flex gap-2 sticky bottom-0 bg-slate-950 pt-2 pb-1 z-10">
                                <Button size="sm" onClick={() => saveArrestEdit(arrest.arrest_id)} className="btn-primary"><Check className="w-3 h-3 mr-1" />Save</Button>
                                <Button size="sm" variant="outline" onClick={() => setEditingArrestId(null)}>Cancel</Button>
                              </div>
                            </div>
                          ) : (
                            // View mode
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                {/* Linked Perp Info */}
                                {linkedPerp && (
                                  <div className="mb-2 p-2 bg-purple-950/30 rounded-sm">
                                    <p className="text-xs text-purple-400 font-medium mb-1">LINKED PERPETRATOR</p>
                                    <div className="text-sm text-slate-300">
                                      {linkedPerp.sex && <span className="mr-2">Sex: {linkedPerp.sex}</span>}
                                      {linkedPerp.race && <span className="mr-2">Race: {linkedPerp.race}</span>}
                                      {linkedPerp.age && <span className="mr-2">Age: {linkedPerp.age}</span>}
                                      {linkedPerp.height && <span className="mr-2">Height: {linkedPerp.height}</span>}
                                    </div>
                                  </div>
                                )}
                                {!linkedPerp && arrest.linked_perp_id && (
                                  <Badge className="bg-purple-900/50 text-purple-200 mb-2">
                                    Perp: {arrest.perp_description_summary || 'Linked'}
                                  </Badge>
                                )}
                                
                                {/* Show linked profile name */}
                                {arrest.linked_profile_id && (
                                  <div className="mb-2 p-2 bg-purple-900/20 border border-purple-700/50 rounded-sm">
                                    <p className="text-xs text-purple-400 font-medium flex items-center gap-2">
                                      <UserCircle className="w-3 h-3" />
                                      LINKED PROFILE
                                    </p>
                                    <p className="text-sm text-purple-300 font-medium mt-1">
                                      {linkedProfileDetails[arrest.linked_profile_id]?.name || arrest.linked_profile_name || profiles.find(p => p.profile_id === arrest.linked_profile_id)?.name || arrest.linked_profile_id}
                                    </p>
                                    {(() => {
                                      const linkedIds = arrest.linked_ready_for_arrest_ids || (arrest.linked_ready_for_arrest_id ? [arrest.linked_ready_for_arrest_id] : []);
                                      if (linkedIds.length === 0) return null;
                                      const linkedRfaRecords = linkedProfile?.ready_for_arrest?.filter(r => linkedIds.includes(r.id || r.incident_id)) || [];
                                      if (linkedRfaRecords.length === 0) return <p className="text-xs text-amber-400 mt-1">→ Linked to Ready for Arrest record</p>;
                                      return (
                                        <div className="mt-2 space-y-1">
                                          {linkedRfaRecords.map((rfa, idx) => (
                                            <div key={rfa.id || rfa.incident_id || idx} className="p-2 bg-amber-900/20 border border-amber-700/40 rounded">
                                              <p className="text-xs text-amber-400 font-medium">
                                                {rfa.incident_type && <Badge className="bg-slate-600 mr-2 text-xs">{rfa.incident_type}</Badge>}
                                                {rfa.headline || 'Ready for Arrest'}
                                              </p>
                                              <p className="text-xs text-slate-400 mt-0.5">
                                                {rfa.address}{rfa.date_time ? ` • ${new Date(rfa.date_time).toLocaleDateString('en-US', { timeZone: 'America/New_York' })}` : ''}
                                              </p>
                                              {rfa.identification_methods?.length > 0 && (
                                                <p className="text-xs text-slate-500 mt-0.5">ID: {rfa.identification_methods.join(", ")}</p>
                                              )}
                                              {rfa.report_filed && (
                                                <Badge className="bg-blue-600 text-white text-xs mt-1">REPORT FILED</Badge>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      );
                                    })()}
                                  </div>
                                )}
                                
                                {/* Arrest Date */}
                                {arrest.arrest_date && (
                                  <p className="text-xs text-emerald-400 mb-2">
                                    <Calendar className="w-3 h-3 inline mr-1" />
                                    Arrested: {new Date(arrest.arrest_date).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}
                                  </p>
                                )}

                                {/* Arrest Status Badges */}
                                <div className="flex flex-wrap gap-2 mb-2">
                                  {arrest.was_live && <Badge className="bg-blue-900/50 text-blue-200">LIVE ARREST</Badge>}
                                  {arrest.was_live && arrest.perp_stopped_by_pd_only && <Badge variant="outline">PD Only</Badge>}
                                  {arrest.outstanding_perpetrators && <Badge variant="destructive">OUTSTANDING PERPS</Badge>}
                                </div>
                                
                                {/* Identification Methods */}
                                {arrest.identification_by?.length > 0 && (
                                  <div className="mt-2">
                                    <p className="text-xs text-slate-500 mb-1">Identified by:</p>
                                    <div className="flex flex-wrap gap-1">
                                      {arrest.identification_by.map((method) => (
                                        <Badge key={method} variant="outline" className="text-xs">{method}</Badge>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                              <div className="flex gap-1">
                                <Button variant="ghost" size="sm" onClick={() => setEditingArrestId(arrest.arrest_id)}>
                                  <Edit className="w-4 h-4 text-blue-400" />
                                </Button>
                                <Button 
                                  type="button"
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={(e) => {
                                    e.preventDefault();
                                    removeArrest(arrest.arrest_id);
                                  }}
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {/* Cross-incident arrests: show when this incident's ready was arrested via another incident */}
                    {(() => {
                      const crossArrests = [];
                      (incident.perp_descriptions || []).forEach(perp => {
                        if ((!perp.ready_for_identification && !perp.identified_for_arrest) || !perp.linked_profile_id) return;
                        const profile = linkedProfileDetails[perp.linked_profile_id];
                        if (!profile?.ready_for_arrest) return;
                        const rfa = profile.ready_for_arrest.find(r => r.incident_id === incident.incident_id);
                        if (rfa?.arrest_linked && rfa.arrest_linked.incident_id !== incident.incident_id) {
                          crossArrests.push({ perp, profile, arrest_linked: rfa.arrest_linked });
                        }
                      });
                      if (crossArrests.length === 0) return null;
                      return crossArrests.map(({ perp, profile, arrest_linked }, idx) => (
                        <div key={`cross-${idx}`} className="p-3 bg-emerald-950/20 rounded-sm mb-2 border border-emerald-700/50">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className="bg-emerald-600 text-white text-xs">ARREST VIA</Badge>
                            <span className="text-xs text-slate-400">
                              {profile.name} — arrested in another incident
                            </span>
                          </div>
                          <p className="text-sm text-emerald-300 font-medium">{arrest_linked.headline || arrest_linked.incident_type || 'Incident'}</p>
                          {arrest_linked.address && <p className="text-xs text-slate-400">{arrest_linked.address}</p>}
                          {arrest_linked.arrest_date && (
                            <p className="text-xs text-emerald-400 mt-0.5">
                              <Calendar className="w-3 h-3 inline mr-1" />
                              Arrested: {new Date(arrest_linked.arrest_date).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}
                            </p>
                          )}
                          <Button
                            variant="link"
                            size="sm"
                            className="text-xs text-emerald-400 hover:text-emerald-300 p-0 h-auto mt-1"
                            onClick={() => navigate(`/incidents/${arrest_linked.incident_id}`)}
                          >
                            {arrest_linked.incident_id} — View Arrest Incident →
                          </Button>
                        </div>
                      ));
                    })()}
                    {(!incident.arrests || incident.arrests.filter(a => !a.is_linked_arrest).length === 0) && (
                      <p className="text-center text-slate-500 py-4">No arrest records</p>
                    )}
                  </div>

                  {/* Show message if no perp descriptions exist */}
                  {(!incident.perp_descriptions || incident.perp_descriptions.length === 0) && (
                    <div className="mt-4 p-3 bg-amber-900/20 border border-amber-800 rounded-sm">
                      <p className="text-sm text-amber-300">To add an arrest record, first add a Perpetrator Description and mark them as &quot;Arrested&quot;.</p>
                    </div>
                  )}
                  
                  {/* Show unlinked perps that aren't arrested yet */}
                  {incident.perp_descriptions?.some(p => !p.was_arrested) && (
                    <div className="mt-4">
                      <p className="text-xs text-slate-400 mb-2">Mark a perpetrator as arrested to create an arrest record:</p>
                      <div className="flex flex-wrap gap-2">
                        {incident.perp_descriptions.filter(p => !p.was_arrested).map(perp => (
                          <Button 
                            key={perp.perp_id}
                            variant="outline" 
                            size="sm"
                            onClick={() => togglePerpArrest(perp.perp_id, true)}
                          >
                            <Plus className="w-3 h-3 mr-1" />
                            {perp.sex || ''} {perp.race || ''} {perp.age || 'Unknown'}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <Dialog open={showArrestDialog} onOpenChange={setShowArrestDialog}>
                    <DialogTrigger asChild>
                      <Button 
                        className="mt-4 btn-secondary w-full" 
                        data-testid="add-arrest-btn"
                      >
                        <Plus className="w-4 h-4 mr-1" />Add Arrest Info (Manual)
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
                      <DialogHeader>
                        <DialogTitle>Add Arrest Information</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 mt-4 max-h-[70vh] overflow-y-auto">
                        {/* Select Perpetrator OR Profile */}
                        <div>
                          <Label className="mb-2 block">Link to Perpetrator Description</Label>
                          <Select 
                            value={newArrest.linked_perp_id || "none"} 
                            onValueChange={(v) => {
                              const perpId = v === "none" ? null : v;
                              const perp = incident.perp_descriptions?.find(p => p.perp_id === perpId);
                              setNewArrest({ 
                                ...newArrest, 
                                linked_perp_id: perpId,
                                linked_profile_id: perp?.linked_profile_id || null,
                                linked_ready_for_arrest_id: null,
                                perp_description_summary: perp ? `${perp.sex || ''} ${perp.race || ''} ${perp.age || ''}`.trim() : ''
                              });
                            }}
                          >
                            <SelectTrigger className="input-dark">
                              <SelectValue placeholder="Select a perpetrator (optional)..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">No perpetrator</SelectItem>
                              {incident.perp_descriptions?.map(perp => (
                                <SelectItem key={perp.perp_id} value={perp.perp_id}>
                                  {perp.sex || ''} {perp.race || ''} {perp.age || 'Unknown'} {perp.was_arrested ? '(Already Arrested)' : ''} {perp.linked_profile_name ? `→ ${perp.linked_profile_name}` : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {/* Profile Search (if no perp selected or to override) */}
                        <div>
                          <Label className="mb-2 block">
                            {newArrest.linked_perp_id ? 'Or Search Different Profile' : 'Or Search & Link to Profile'}
                          </Label>
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                              placeholder="Search profiles by name..."
                              value={newArrest.profileSearchQuery || ""}
                              onChange={(e) => setNewArrest({ ...newArrest, profileSearchQuery: e.target.value })}
                              className="pl-10 input-dark"
                            />
                          </div>
                          
                          {/* Profile Search Results */}
                          {newArrest.profileSearchQuery && (
                            <div className="mt-2 max-h-48 overflow-y-auto border border-slate-700 rounded-lg">
                              {profiles
                                .filter(p => p.name?.toLowerCase().includes((newArrest.profileSearchQuery || "").toLowerCase()))
                                .slice(0, 10)
                                .map(profile => (
                                  <div
                                    key={profile.profile_id}
                                    onClick={() => setNewArrest({
                                      ...newArrest,
                                      linked_profile_id: profile.profile_id,
                                      linked_profile_name: profile.name,
                                      profileSearchQuery: "",
                                      linked_ready_for_arrest_id: null
                                    })}
                                    className={`p-2 cursor-pointer hover:bg-slate-700 border-b border-slate-800 last:border-0 flex items-center gap-2 ${
                                      newArrest.linked_profile_id === profile.profile_id ? 'bg-purple-900/30' : ''
                                    }`}
                                  >
                                    {profile.mugshot_url ? (
                                      <img
                                        src={getAuthenticatedMediaUrl(`${API.replace('/api', '')}${profile.mugshot_url}/thumb?size=96`)}
                                        alt=""
                                        width={40}
                                        height={40}
                                        loading="lazy"
                                        decoding="async"
                                        onClick={(e) => { e.stopPropagation(); setLightboxMedia(profile.mugshot_url); }}
                                        onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling?.classList.remove('hidden'); }}
                                        className="w-10 h-10 object-cover rounded shrink-0 cursor-zoom-in hover:ring-2 hover:ring-purple-400"
                                      />
                                    ) : null}
                                    <div className={`w-10 h-10 rounded bg-slate-700 flex items-center justify-center shrink-0 ${profile.mugshot_url ? 'hidden' : ''}`}>
                                      <UserCircle className="w-4 h-4 text-purple-400" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <p className="font-medium truncate">{profile.name}</p>
                                      <div className="flex gap-2 text-xs text-slate-400">
                                        {profile.ready_for_arrest_count > 0 && (
                                          <span className="text-amber-400">{profile.ready_for_arrest_count} ready for arrest</span>
                                        )}
                                        {profile.arrest_count > 0 && (
                                          <span className="text-emerald-400">{profile.arrest_count} arrests</span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))
                              }
                              {profiles.filter(p => p.name?.toLowerCase().includes((newArrest.profileSearchQuery || "").toLowerCase())).length === 0 && (
                                <p className="p-3 text-slate-500 text-center">No profiles found</p>
                              )}
                            </div>
                          )}
                          
                          {/* Selected Profile Display */}
                          {newArrest.linked_profile_id && !newArrest.profileSearchQuery && (
                            <div className="mt-2 p-3 bg-purple-900/20 border border-purple-700 rounded-lg">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-medium text-purple-300">
                                    {newArrest.linked_profile_name || profiles.find(p => p.profile_id === newArrest.linked_profile_id)?.name}
                                  </p>
                                  <p className="text-xs text-slate-400">Profile linked to this arrest</p>
                                </div>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => setNewArrest({ 
                                    ...newArrest, 
                                    linked_profile_id: null, 
                                    linked_profile_name: null,
                                    linked_ready_for_arrest_id: null 
                                  })}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                              
                              {/* Show Ready for Arrest records for this profile */}
                              {(() => {
                                const selectedProfile = linkedProfileDetails[newArrest.linked_profile_id] || profiles.find(p => p.profile_id === newArrest.linked_profile_id);
                                const readyForArrestRecords = Array.isArray(selectedProfile?.ready_for_arrest)
                                  ? selectedProfile.ready_for_arrest.filter(r => !r.arrest_linked)
                                  : [];
                                
                                if (readyForArrestRecords.length > 0) {
                                  return (
                                    <div className="mt-3 pt-3 border-t border-purple-800">
                                      <p className="text-xs text-amber-400 mb-2 font-medium">
                                        Link arrest to Ready for Arrest record:
                                      </p>
                                      <div className="space-y-2">
                                        {readyForArrestRecords.map((rfa, idx) => (
                                          <div
                                            key={rfa.id || rfa.incident_id || idx}
                                            onClick={() => {
                                              const rfaId = rfa.id || rfa.incident_id;
                                              const currentIds = newArrest.linked_ready_for_arrest_ids || (newArrest.linked_ready_for_arrest_id ? [newArrest.linked_ready_for_arrest_id] : []);
                                              const newIds = currentIds.includes(rfaId) ? currentIds.filter(id => id !== rfaId) : [...currentIds, rfaId];
                                              setNewArrest({ ...newArrest, linked_ready_for_arrest_ids: newIds, linked_ready_for_arrest_id: newIds[0] || null });
                                            }}
                                            className={`p-2 rounded cursor-pointer border ${
                                              (newArrest.linked_ready_for_arrest_ids || (newArrest.linked_ready_for_arrest_id ? [newArrest.linked_ready_for_arrest_id] : [])).includes(rfa.id || rfa.incident_id)
                                                ? 'bg-amber-900/30 border-amber-600'
                                                : 'bg-slate-800 border-slate-700 hover:border-amber-700'
                                            }`}
                                          >
                                            <div className="flex items-center gap-2">
                                              {(newArrest.linked_ready_for_arrest_ids || (newArrest.linked_ready_for_arrest_id ? [newArrest.linked_ready_for_arrest_id] : [])).includes(rfa.id || rfa.incident_id) && (
                                                <Check className="w-4 h-4 text-amber-400" />
                                              )}
                                              <div>
                                                <p className="text-sm font-medium">
                                                  {rfa.incident_type && <Badge className="bg-slate-600 mr-2 text-xs">{rfa.incident_type}</Badge>}
                                                  {rfa.headline || 'Identified'}
                                                </p>
                                                <p className="text-xs text-slate-400">
                                                  {rfa.address} • {rfa.date_time ? new Date(rfa.date_time).toLocaleDateString('en-US', { timeZone: 'America/New_York' }) : ''}
                                                </p>
                                                {rfa.identification_methods?.length > 0 && (
                                                  <p className="text-xs text-slate-500 mt-1">
                                                    ID: {rfa.identification_methods.join(", ")}
                                                  </p>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                      {(newArrest.linked_ready_for_arrest_ids?.length > 0 || newArrest.linked_ready_for_arrest_id) && (
                                        <div className="flex items-center gap-2 mt-2">
                                          <p className="text-xs text-amber-400">{newArrest.linked_ready_for_arrest_ids?.length || 1} record(s) selected</p>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="text-xs h-6 px-2"
                                            onClick={() => setNewArrest({ ...newArrest, linked_ready_for_arrest_id: null, linked_ready_for_arrest_ids: [] })}
                                          >
                                            Clear
                                          </Button>
                                        </div>
                                      )}
                                    </div>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Checkbox id="a_live" checked={newArrest.was_live || false} onCheckedChange={(c) => setNewArrest({ ...newArrest, was_live: c })} />
                          <Label htmlFor="a_live">Was Arrest Live?</Label>
                        </div>
                        {newArrest.was_live && (
                          <div className="flex items-center gap-2 pl-6">
                            <Checkbox id="a_pd" checked={newArrest.perp_stopped_by_pd_only || false} onCheckedChange={(c) => setNewArrest({ ...newArrest, perp_stopped_by_pd_only: c })} />
                            <Label htmlFor="a_pd">Perp Stopped by PD Only?</Label>
                          </div>
                        )}
                        <div>
                          <Label className="mb-2 block">Identification By (select all that apply)</Label>
                          <div className="space-y-2">
                            {IDENTIFICATION_BY_OPTIONS.map((method) => (
                              <div key={method} className="flex items-center gap-2">
                                <Checkbox
                                  id={`id_${method}`}
                                  checked={(newArrest.identification_by || []).includes(method)}
                                  onCheckedChange={() => toggleArrestIdentification(method)}
                                />
                                <Label htmlFor={`id_${method}`}>{method}</Label>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox id="a_outstanding" checked={newArrest.outstanding_perpetrators || false} onCheckedChange={(c) => setNewArrest({ ...newArrest, outstanding_perpetrators: c })} />
                          <Label htmlFor="a_outstanding">Any Outstanding Perpetrators?</Label>
                        </div>
                        <Button onClick={addArrest} className="w-full btn-primary">Add Arrest</Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </CardContent>
              </Card>
              )}
            </TabsContent>

            {/* Ready for Arrests Tab */}
            <TabsContent value="ready" className="mt-4">
              {incident && (
              <Card className="form-section">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-emerald-400">Identified for Arrest</h3>
                    <Badge variant="outline" className="text-emerald-400 border-emerald-600">
                      {incident.perp_descriptions?.filter(p => p.identified_for_arrest).length || 0} identified
                    </Badge>
                  </div>
                  
                  {(!incident.perp_descriptions || incident.perp_descriptions.length === 0) ? (
                    <p className="text-slate-500 text-sm text-center py-8">No perpetrators added. Add perps in the Perps tab first.</p>
                  ) : (
                    <div className="space-y-3">
                      {incident.perp_descriptions.map((perp) => (
                        <div key={perp.perp_id} className={`p-3 rounded border ${perp.identified_for_arrest ? 'bg-emerald-900/20 border-emerald-700/50' : 'bg-slate-950 border-slate-800'}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="font-medium">{perp.name || `${perp.sex || ''} ${perp.race || ''} ${perp.age || 'Unknown'}`}</span>
                                {(perp.linked_profile_id && (linkedProfileDetails[perp.linked_profile_id]?.name || perp.linked_profile_name)) ? (
                                  <Badge className="bg-purple-600 text-white text-xs">{linkedProfileDetails[perp.linked_profile_id]?.name || perp.linked_profile_name}</Badge>
                                ) : (
                                  <Badge variant="outline" className="text-slate-500 text-xs">No Profile</Badge>
                                )}
                                {perp.was_arrested && (
                                  <Badge className="bg-red-600 text-white text-xs">ARRESTED</Badge>
                                )}
                                {perp.report_filed && (
                                  <Badge className="bg-blue-600 text-white text-xs">REPORT FILED</Badge>
                                )}
                              </div>
                              
                              {/* Show message if no profile linked */}
                              {!perp.linked_profile_id && (
                                <div className="p-2 bg-amber-900/20 border border-amber-700/50 rounded mb-2">
                                  <p className="text-xs text-amber-400">
                                    Link a perpetrator profile to sync Ready for Arrest status
                                  </p>
                                </div>
                              )}
                              
                              {/* Identified for Arrest Toggle - only enabled if profile linked */}
                              <div className="flex items-center gap-2 mb-2">
                                <Checkbox
                                  id={`identified_${perp.perp_id}`}
                                  checked={perp.identified_for_arrest || false}
                                  disabled={!perp.linked_profile_id}
                                  onCheckedChange={(checked) => {
                                    if (checked) {
                                      // Open dialog to select methods
                                      setShowIdentifiedDialog(perp.perp_id);
                                    } else {
                                      toggleIdentifiedForArrest(perp.perp_id, false, []);
                                    }
                                  }}
                                />
                                <Label 
                                  htmlFor={`identified_${perp.perp_id}`} 
                                  className={perp.identified_for_arrest ? 'text-emerald-400 font-medium' : perp.linked_profile_id ? 'text-slate-400' : 'text-slate-600'}
                                >
                                  Identified for Arrest
                                </Label>
                              </div>
                              
                              {/* Show identification methods if identified */}
                              {perp.identified_for_arrest && (
                                <div className="mt-2 pl-6">
                                  <p className="text-xs text-slate-500 mb-1">Select how this perpetrator was identified:</p>
                                  <div className="flex flex-wrap gap-2">
                                    {IDENTIFICATION_METHODS.map(method => (
                                      <div key={method} className="flex items-center gap-1">
                                        <Checkbox
                                          id={`method_${perp.perp_id}_${method}`}
                                          checked={(perp.identification_methods || []).includes(method)}
                                          onCheckedChange={(checked) => {
                                            const currentMethods = perp.identification_methods || [];
                                            const newMethods = checked 
                                              ? [...currentMethods, method]
                                              : currentMethods.filter(m => m !== method);
                                            updatePerpIdentificationMethods(perp.perp_id, newMethods);
                                          }}
                                        />
                                        <Label htmlFor={`method_${perp.perp_id}_${method}`} className="text-xs">
                                          {method}
                                        </Label>
                                      </div>
                                    ))}
                                    {/* Report Filed - now inline with identification methods */}
                                    <div className="flex items-center gap-1">
                                      <Checkbox
                                        id={`report_filed_${perp.perp_id}`}
                                        checked={perp.report_filed || false}
                                        onCheckedChange={(checked) => {
                                          updatePerpReportFiled(perp.perp_id, checked);
                                        }}
                                      />
                                      <Label htmlFor={`report_filed_${perp.perp_id}`} className="text-xs text-blue-400 font-medium">
                                        Report Filed
                                      </Label>
                                    </div>
                                  </div>
                                  
                                  <Button 
                                    size="sm" 
                                    className="mt-3 btn-primary"
                                    onClick={() => {
                                      toggleIdentifiedForArrest(perp.perp_id, true, perp.identification_methods || [], perp.report_filed || false);
                                    }}
                                  >
                                    Save & Sync to Profile
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Dialog for selecting identification methods */}
                  <Dialog open={!!showIdentifiedDialog} onOpenChange={() => setShowIdentifiedDialog(null)}>
                    <DialogContent className="bg-slate-900 border-slate-800 max-w-sm">
                      <DialogHeader>
                        <DialogTitle>Identification Method</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-3 mt-4">
                        <p className="text-sm text-slate-400">Select how this perpetrator was identified:</p>
                        {IDENTIFICATION_METHODS.map(method => {
                          const perp = incident.perp_descriptions?.find(p => p.perp_id === showIdentifiedDialog);
                          return (
                            <div key={method} className="flex items-center gap-2">
                              <Checkbox
                                id={`dialog_method_${method}`}
                                checked={(perp?.identification_methods || []).includes(method)}
                                onCheckedChange={(checked) => {
                                  const currentMethods = perp?.identification_methods || [];
                                  const newMethods = checked 
                                    ? [...currentMethods, method]
                                    : currentMethods.filter(m => m !== method);
                                  updatePerpIdentificationMethods(showIdentifiedDialog, newMethods);
                                }}
                              />
                              <Label htmlFor={`dialog_method_${method}`}>{method}</Label>
                            </div>
                          );
                        })}
                        {/* Report Filed option */}
                        {(() => {
                          const perp = incident.perp_descriptions?.find(p => p.perp_id === showIdentifiedDialog);
                          return (
                            <div className="flex items-center gap-2">
                              <Checkbox
                                id="dialog_report_filed"
                                checked={perp?.report_filed || false}
                                onCheckedChange={(checked) => {
                                  const updatedPerps = (incident.perp_descriptions || []).map(p => 
                                    p.perp_id === showIdentifiedDialog ? { ...p, report_filed: checked } : p
                                  );
                                  setIncident({ ...incident, perp_descriptions: updatedPerps });
                                }}
                              />
                              <Label htmlFor="dialog_report_filed" className="text-blue-400 font-medium">Report Filed</Label>
                            </div>
                          );
                        })()}
                        <Button 
                          className="w-full btn-primary mt-4"
                          onClick={() => {
                            const perp = incident.perp_descriptions?.find(p => p.perp_id === showIdentifiedDialog);
                            toggleIdentifiedForArrest(showIdentifiedDialog, true, perp?.identification_methods || [], perp?.report_filed || false);
                            setShowIdentifiedDialog(null);
                          }}
                        >
                          Confirm Identification
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </CardContent>
              </Card>
              )}
            </TabsContent>

            {/* Notes Tab */}
            <TabsContent value="notes" className="mt-4">
              {incident && (
                <Card className="form-section">
                  <CardContent className="p-4">
                    <div className="max-h-[60vh] overflow-y-auto">
                      {(!incident?.notes || !Array.isArray(incident.notes) || incident.notes.length === 0) && (
                        <p className="text-slate-500 text-sm text-center py-8">No notes yet. Add a note below.</p>
                      )}
                      {incident?.notes && Array.isArray(incident.notes) && incident.notes.map((note, index) => {
                        // Handle both string notes and object notes
                        const noteText = typeof note === 'string' ? note : (note?.text || note?.note || String(note));

                        return (
                          <div key={index} className="p-3 bg-slate-950 rounded-sm mb-2 text-sm">
                            {editingNoteIndex === index ? (
                              <div className="space-y-2">
                                <Textarea
                                  value={editNoteValue}
                                  onChange={(e) => {
                                    setEditNoteValue(e.target.value);
                                    e.target.style.height = "auto";
                                    e.target.style.height = e.target.scrollHeight + "px";
                                  }}
                                  ref={(el) => {
                                    if (el) {
                                      el.style.height = "auto";
                                      el.style.height = el.scrollHeight + "px";
                                    }
                                  }}
                                  className="input-dark resize-none overflow-hidden"
                                />
                                <div className="flex gap-2">
                                  <Button size="sm" onClick={saveNoteEdit} className="btn-primary">
                                    <Check className="w-3 h-3 mr-1" />Save
                                  </Button>
                                  <Button size="sm" variant="outline" onClick={() => setEditingNoteIndex(null)}>Cancel</Button>
                                </div>
                              </div>
                            ) : (
                              <div>
                                {/* Metadata line */}
                                {(note?.created_at || note?.created_by || note?.edited_at) && (
                                  <div className="flex items-center gap-2 flex-wrap mb-1.5 text-[10px] text-slate-500">
                                    {note.created_by && <span className="text-slate-400 font-medium">{note.created_by}</span>}
                                    {note.created_at && (
                                      <span className="font-mono">{new Date(note.created_at).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}</span>
                                    )}
                                    {note.edited_at && (
                                      <span className="text-amber-500 italic">
                                        (edited {new Date(note.edited_at).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}
                                        {note.edited_by ? ` by ${note.edited_by}` : ""})
                                      </span>
                                    )}
                                  </div>
                                )}
                                <div className="flex items-start justify-between gap-2">
                                  <span className="flex-1 whitespace-pre-wrap">{noteText}</span>
                                  <div className="flex gap-1 flex-shrink-0">
                                    <Button variant="ghost" size="sm" onClick={() => startEditNote(index)}>
                                      <Edit className="w-4 h-4 text-blue-400" />
                                    </Button>
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <Button variant="ghost" size="sm"><Trash2 className="w-4 h-4 text-red-500" /></Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent className="bg-slate-900 border-slate-700">
                                      <AlertDialogHeader>
                                        <AlertDialogTitle className="text-red-400">Delete Note?</AlertDialogTitle>
                                        <AlertDialogDescription>Are you sure you want to delete this note?</AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel className="bg-slate-700 border-slate-600">Cancel</AlertDialogCancel>
                                        <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => removeNote(index)}>Delete</AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  <div className="mt-4 pt-4 border-t border-slate-800 flex gap-2">
                    <Input
                      placeholder="Add a note..."
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      className="input-dark flex-1"
                      data-testid="note-input"
                    />
                    <Button onClick={addNote} className="btn-secondary" data-testid="add-note-btn">
                      <Plus className="w-4 h-4 mr-1" />Add
                    </Button>
                  </div>
                </CardContent>
              </Card>
              )}
            </TabsContent>

        {/* Units Tab */}
        <TabsContent value="units" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Units Responded - Multi-select with search */}
          <Card className="form-section">
            <CardHeader className="pb-2">
              <CardTitle className="form-section-title flex items-center gap-2">
                <Users className="w-4 h-4" />Units Responded ({(incident.units_responded || []).length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative mb-2">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  ref={unitRespondedInputRef}
                  value={unitRespondedSearch}
                  onChange={(e) => setUnitRespondedSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      const q = unitRespondedSearch.toLowerCase().trim();
                      if (!q) return;
                      const current = incident.units_responded || [];
                      const match = units.filter(u => u.name.toLowerCase().includes(q)).find(u => !current.includes(u.name));
                      if (match) toggleUnit(match.name, "units_responded", unitRespondedInputRef);
                    } else if (e.key === "Escape") {
                      e.preventDefault();
                      setUnitRespondedSearch("");
                    }
                  }}
                  placeholder="Search units..."
                  className="input-dark pl-8 h-8 text-sm"
                />
              </div>
              <ScrollArea className="h-32">
                <div className="space-y-1">
                  {units
                    .filter(u => u.name.toLowerCase().includes(unitRespondedSearch.toLowerCase()))
                    .map((unit) => (
                    <div
                      key={unit.unit_id}
                      className="flex items-center gap-2 px-1 py-0.5 rounded cursor-pointer hover:bg-slate-800"
                      onMouseDown={(e) => e.preventDefault()}
                      onTouchStart={(e) => e.preventDefault()}
                      onClick={() => toggleUnit(unit.name, "units_responded", unitRespondedInputRef)}
                    >
                      <Checkbox
                        checked={(incident.units_responded || []).includes(unit.name)}
                        tabIndex={-1}
                        className="pointer-events-none"
                      />
                      <Label className="text-sm cursor-pointer pointer-events-none">{unit.name}</Label>
                    </div>
                  ))}
                </div>
              </ScrollArea>
              <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-slate-800">
                {[...(incident.units_responded || [])].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })).map((unit) => (
                  <Badge key={unit} variant="outline" className="text-xs">
                    {unit}
                    <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => toggleUnit(unit, "units_responded")} />
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Investigating Units - Multi-select with search */}
          <Card className="form-section">
            <CardHeader className="pb-2">
              <CardTitle className="form-section-title flex items-center gap-2">
                <Shield className="w-4 h-4" />Units Investigating ({(incident.units_investigating || []).length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative mb-2">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  ref={unitInvestigatingInputRef}
                  value={unitInvestigatingSearch}
                  onChange={(e) => setUnitInvestigatingSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      const q = unitInvestigatingSearch.toLowerCase().trim();
                      if (!q) return;
                      const current = incident.units_investigating || [];
                      const match = investigators.filter(u => u.name.toLowerCase().includes(q)).find(u => !current.includes(u.name));
                      if (match) toggleUnit(match.name, "units_investigating", unitInvestigatingInputRef);
                    } else if (e.key === "Escape") {
                      e.preventDefault();
                      setUnitInvestigatingSearch("");
                    }
                  }}
                  placeholder="Search units..."
                  className="input-dark pl-8 h-8 text-sm"
                />
              </div>
              <ScrollArea className="h-28">
                <div className="space-y-1">
                  {investigators
                    .filter(u => u.name.toLowerCase().includes(unitInvestigatingSearch.toLowerCase()))
                    .map((unit) => (
                    <div
                      key={unit.unit_id}
                      className="flex items-center gap-2 px-1 py-0.5 rounded cursor-pointer hover:bg-slate-800"
                      onMouseDown={(e) => e.preventDefault()}
                      onTouchStart={(e) => e.preventDefault()}
                      onClick={() => toggleUnit(unit.name, "units_investigating", unitInvestigatingInputRef)}
                    >
                      <Checkbox
                        checked={(incident.units_investigating || []).includes(unit.name)}
                        tabIndex={-1}
                        className="pointer-events-none"
                      />
                      <Label className="text-sm cursor-pointer pointer-events-none">{unit.name}</Label>
                    </div>
                  ))}
                </div>
              </ScrollArea>
              <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-slate-800">
                {[...(incident.units_investigating || [])].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })).map((unit) => (
                  <Badge key={unit} variant="secondary" className="text-xs">
                    {unit}
                    <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => toggleUnit(unit, "units_investigating")} />
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Dispatcher - Multi-select with search */}
          <Card className="form-section">
            <CardHeader className="pb-2">
              <CardTitle className="form-section-title flex items-center gap-2">
                <Phone className="w-4 h-4" />Dispatcher ({(incident.dispatcher_units || []).length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="hotline"
                  checked={incident.is_hotline_call}
                  onCheckedChange={(checked) => updateField("is_hotline_call", checked)}
                />
                <Label htmlFor="hotline">Hotline Call</Label>
              </div>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  ref={dispatcherInputRef}
                  value={dispatcherSearch}
                  onChange={(e) => setDispatcherSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      const q = dispatcherSearch.toLowerCase().trim();
                      if (!q) return;
                      const current = incident.dispatcher_units || [];
                      const match = dispatchers.filter(u => u.name.toLowerCase().includes(q)).find(u => !current.includes(u.name));
                      if (match) toggleUnit(match.name, "dispatcher_units", dispatcherInputRef);
                    } else if (e.key === "Escape") {
                      e.preventDefault();
                      setDispatcherSearch("");
                    }
                  }}
                  placeholder="Search dispatchers..."
                  className="input-dark pl-8 h-8 text-sm"
                />
              </div>
              <ScrollArea className="h-24">
                <div className="space-y-1">
                  {dispatchers
                    .filter(u => u.name.toLowerCase().includes(dispatcherSearch.toLowerCase()))
                    .map((unit) => (
                    <div
                      key={unit.unit_id}
                      className="flex items-center gap-2 px-1 py-0.5 rounded cursor-pointer hover:bg-slate-800"
                      onMouseDown={(e) => e.preventDefault()}
                      onTouchStart={(e) => e.preventDefault()}
                      onClick={() => toggleUnit(unit.name, "dispatcher_units", dispatcherInputRef)}
                    >
                      <Checkbox
                        checked={(incident.dispatcher_units || []).includes(unit.name)}
                        tabIndex={-1}
                        className="pointer-events-none"
                      />
                      <Label className="text-sm cursor-pointer pointer-events-none">{unit.name}</Label>
                    </div>
                  ))}
                </div>
              </ScrollArea>
              <div className="flex flex-wrap gap-1 pt-2 border-t border-slate-800">
                {(incident.dispatcher_units || []).map((unit) => (
                  <Badge key={unit} className="bg-amber-900/50 text-amber-200 text-xs">
                    {unit}
                    <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => toggleUnit(unit, "dispatcher_units")} />
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          </div>
        </TabsContent>

        {/* Updates Tab */}
        <TabsContent value="updates" className="mt-4">
          <div className="space-y-4">
          {/* Flags */}
          <Card className="form-section">
            <CardHeader className="pb-2">
              <CardTitle className="form-section-title">Flags</CardTitle>
            </CardHeader>
            <CardContent className="flex items-center gap-6 flex-wrap">
              <div className="flex items-center gap-2">
                <Checkbox id="is_pr" checked={incident.is_pr} onCheckedChange={(checked) => updateField("is_pr", checked)} />
                <Label htmlFor="is_pr">PR Flag</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="is_arrest" checked={incident.is_arrest} onCheckedChange={(checked) => updateField("is_arrest", checked)} />
                <Label htmlFor="is_arrest">Arrest?</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="show_in_call_history" checked={incident.show_in_call_history || false} onCheckedChange={(checked) => updateField("show_in_call_history", checked)} />
                <Label htmlFor="show_in_call_history" className={incident.show_in_call_history ? "text-blue-400" : "text-slate-500"}>
                  {incident.show_in_call_history ? "✓ Visible in Call History" : "Hidden from Call History"}
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="hide_from_media" checked={incident.hide_from_media || false} onCheckedChange={(checked) => updateField("hide_from_media", checked)} />
                <Label htmlFor="hide_from_media" className={incident.hide_from_media ? "text-red-400" : "text-slate-500"}>
                  {incident.hide_from_media ? "✓ Hidden from Media Page" : "Visible on Media Page"}
                </Label>
              </div>
            </CardContent>
          </Card>

          {/* Radio Transmissions */}
          <Card className="form-section">
            <CardHeader className="pb-2">
              <CardTitle className="form-section-title">Radio Transmissions</CardTitle>
            </CardHeader>
            <CardContent className="flex items-center gap-8">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="10_85"
                  checked={incident.transmitted_10_85}
                  onCheckedChange={(checked) => updateField("transmitted_10_85", checked)}
                />
                <Label htmlFor="10_85">10-85 Transmitted</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="10_13"
                  checked={incident.transmitted_10_13}
                  onCheckedChange={(checked) => updateField("transmitted_10_13", checked)}
                />
                <Label htmlFor="10_13">10-13 Transmitted</Label>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Hate Crime */}
          <Card className="form-section">
            <CardHeader className="pb-2">
              <CardTitle className="form-section-title flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />Hate Crime
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="hate_crime"
                    checked={incident.is_hate_crime}
                    onCheckedChange={(checked) => updateField("is_hate_crime", checked)}
                  />
                  <Label htmlFor="hate_crime">Hate Crime</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="hctf"
                    checked={incident.hctf_notified}
                    onCheckedChange={(checked) => updateField("hctf_notified", checked)}
                  />
                  <Label htmlFor="hctf">HCTF Notified</Label>
                </div>
              </div>
              {incident.is_hate_crime && (
                <Textarea
                  value={incident.hate_crime_nature || ""}
                  onChange={(e) => updateField("hate_crime_nature", e.target.value)}
                  placeholder="Nature of crime..."
                  className="input-dark"
                />
              )}
            </CardContent>
          </Card>

          {/* Weapon */}
          <Card className="form-section">
            <CardHeader className="pb-2">
              <CardTitle className="form-section-title">Weapon</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="weapon"
                  checked={incident.weapon_displayed}
                  onCheckedChange={(checked) => updateField("weapon_displayed", checked)}
                />
                <Label htmlFor="weapon">Weapon Displayed/Used?</Label>
              </div>
              {incident.weapon_displayed && (
                <Textarea
                  value={incident.weapon_notes || ""}
                  onChange={(e) => updateField("weapon_notes", e.target.value)}
                  placeholder="Weapon details..."
                  className="input-dark"
                />
              )}
            </CardContent>
          </Card>

          {/* EMS / Injuries */}
          <Card className="form-section">
            <CardHeader className="pb-2">
              <CardTitle className="form-section-title">EMS / Injuries</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="ems"
                    checked={incident.ems_required}
                    onCheckedChange={(checked) => updateField("ems_required", checked)}
                  />
                  <Label htmlFor="ems">EMS Required?</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="injuries"
                    checked={incident.injuries_reported}
                    onCheckedChange={(checked) => updateField("injuries_reported", checked)}
                  />
                  <Label htmlFor="injuries">Injuries Reported?</Label>
                </div>
              </div>
              {incident.ems_required && (
                <div>
                  <Label className="text-xs">EMS Notes</Label>
                  <Textarea
                    value={incident.ems_notes || ""}
                    onChange={(e) => updateField("ems_notes", e.target.value)}
                    placeholder="EMS details, response, hospital..."
                    className="input-dark min-h-[60px]"
                    rows={2}
                  />
                </div>
              )}
              {incident.injuries_reported && (
                <div>
                  <Label className="text-xs">Injuries Notes</Label>
                  <Textarea
                    value={incident.injuries_notes || ""}
                    onChange={(e) => updateField("injuries_notes", e.target.value)}
                    placeholder="Injury details, severity, treatment..."
                    className="input-dark min-h-[60px]"
                    rows={2}
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Police */}
          <Card className="form-section">
            <CardHeader className="pb-2">
              <CardTitle className="form-section-title">Police</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs text-slate-400">Assigned Detective/Officer</Label>
                <Input
                  value={incident.assigned_detective || ""}
                  onChange={(e) => updateField("assigned_detective", e.target.value)}
                  placeholder="Enter name..."
                  className="input-dark"
                />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="police_followup"
                  checked={incident.police_followup_required}
                  onCheckedChange={(checked) => updateField("police_followup_required", checked)}
                />
                <Label htmlFor="police_followup">Police Follow-up Required?</Label>
              </div>
              {incident.police_followup_required && (
                <Textarea
                  value={incident.police_followup_notes || ""}
                  onChange={(e) => updateField("police_followup_notes", e.target.value)}
                  placeholder="Follow-up notes..."
                  className="input-dark"
                />
              )}
              <div className="flex items-center gap-2 mb-3">
                <Checkbox
                  id="pd_activated"
                  checked={incident.pd_activated}
                  onCheckedChange={(checked) => updateField("pd_activated", checked)}
                />
                <Label htmlFor="pd_activated" className={incident.pd_activated ? "text-blue-400 font-semibold" : ""}>PD Activated</Label>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <Checkbox
                  id="police_report_filed"
                  checked={incident.police_report_filed}
                  onCheckedChange={(checked) => updateField("police_report_filed", checked)}
                />
                <Label htmlFor="police_report_filed" className={incident.police_report_filed ? "text-green-400 font-semibold" : ""}>Police Report Filed</Label>
              </div>
              {incident.police_report_filed && (
                <div className="mb-3">
                  <Label>Report Notes</Label>
                  <Textarea
                    value={incident.police_report_notes || ""}
                    onChange={(e) => updateField("police_report_notes", e.target.value)}
                    placeholder="Report number, officer name, notes..."
                    className="input-dark"
                  />
                </div>
              )}
              <div>
                <Label>Police Response/Action</Label>
                <Textarea
                  value={incident.police_action_notes || ""}
                  onChange={(e) => updateField("police_action_notes", e.target.value)}
                  placeholder="Police action notes..."
                  className="input-dark"
                />
              </div>
            </CardContent>
          </Card>

          {/* Follow-up */}
          <Card className="form-section">
            <CardHeader className="pb-2">
              <CardTitle className="form-section-title">Follow-up</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="followup"
                  checked={incident.needs_followup}
                  onCheckedChange={(checked) => updateField("needs_followup", checked)}
                />
                <Label htmlFor="followup">Needs Follow-up</Label>
              </div>
              {incident.needs_followup && (
                <Textarea
                  value={incident.followup_notes || ""}
                  onChange={(e) => updateField("followup_notes", e.target.value)}
                  placeholder="Follow-up notes..."
                  className="input-dark"
                />
              )}
            </CardContent>
          </Card>

          {/* Security Footage */}
          <Card className="form-section">
            <CardHeader className="pb-2">
              <CardTitle className="form-section-title flex items-center gap-2">
                <Camera className="w-4 h-4" />Security Footage
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="footage_requested"
                    checked={incident.footage_requested}
                    onCheckedChange={(checked) => updateField("footage_requested", checked)}
                  />
                  <Label htmlFor="footage_requested">Footage Requested</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="footage"
                    checked={incident.footage_obtained}
                    onCheckedChange={(checked) => updateField("footage_obtained", checked)}
                  />
                  <Label htmlFor="footage">Footage Obtained</Label>
                </div>
              </div>
              {(incident.footage_requested || incident.footage_obtained) && (
                <Input
                  value={incident.footage_locations || ""}
                  onChange={(e) => updateField("footage_locations", e.target.value)}
                  placeholder="Locations..."
                  className="input-dark"
                />
              )}
            </CardContent>
          </Card>

          {/* Call Final */}
          <Card className="form-section">
            <CardHeader className="pb-2">
              <CardTitle className="form-section-title">Call Final</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={incident.call_final || ""}
                onChange={(e) => updateField("call_final", e.target.value)}
                placeholder="Final notes..."
                className="input-dark"
              />
            </CardContent>
          </Card>
          </div>
          </div>
        </TabsContent>

        {/* Media Tab */}
        <TabsContent value="media" className="mt-4">
      <Card className="form-section">
        <CardHeader className="pb-2">
          <CardTitle className="form-section-title flex items-center gap-2">
            <Camera className="w-4 h-4" />
            Media Files ({(incident.media_files || []).length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Upload + Drive Buttons */}
          <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label className="cursor-pointer">
              <input
                type="file"
                multiple
                accept="image/*,video/*"
                onChange={handleMediaUpload}
                className="hidden"
                disabled={uploading}
              />
              <div className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-700 rounded-sm hover:border-blue-500 hover:bg-slate-800/50 transition-colors h-full">
                {uploading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500" />
                ) : (
                  <Upload className="w-5 h-5 text-slate-400" />
                )}
                <span className="text-sm text-slate-400">
                  {uploading ? "Uploading..." : "Click to upload"}
                </span>
              </div>
            </label>
            <button
              type="button"
              onClick={handleAttachFromDrive}
              disabled={uploading || !isDrivePickerConfigured()}
              className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-700 rounded-sm hover:border-amber-500 hover:bg-slate-800/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={isDrivePickerConfigured() ? "Pick files from your Google Drive" : "Drive picker not configured"}
            >
              {/* Inline SVG so we don't need a new icon import */}
              <svg width="20" height="20" viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da"/>
                <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z" fill="#00ac47"/>
                <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z" fill="#ea4335"/>
                <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d"/>
                <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc"/>
                <path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00"/>
              </svg>
              <span className="text-sm text-slate-300">Attach from Drive</span>
            </button>
          </div>

          {/* Media Grid */}
          {(incident.media_files || []).length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {(incident.media_files || []).map((item, index) => {
                // Drive attachments are dicts with type "google_drive";
                // legacy entries are URL/path strings.
                if (item && typeof item === "object" && item.type === "google_drive") {
                  return (
                    <div
                      key={`drive-${item.file_id}-${index}`}
                      className="relative group bg-slate-950 rounded-sm overflow-hidden border border-slate-800 cursor-pointer"
                      onClick={() => window.open(item.web_view_url, "_blank", "noopener")}
                      title={`${item.name} — opens in Google Drive`}
                    >
                      {item.thumbnail_url ? (
                        <img
                          src={item.thumbnail_url}
                          alt={item.name}
                          className="w-full h-32 object-cover hover:opacity-80 transition-opacity pointer-events-none"
                          draggable="false"
                          referrerPolicy="no-referrer"
                          onError={(e) => {
                            // Thumbnail failed (likely the viewer doesn't have
                            // access to the file) — swap to a Drive icon.
                            e.target.style.display = "none";
                            const fallback = e.target.parentElement?.querySelector('[data-drive-fallback]');
                            if (fallback) fallback.style.display = "flex";
                          }}
                        />
                      ) : null}
                      <div
                        data-drive-fallback
                        className="w-full h-32 flex items-center justify-center bg-slate-900 text-slate-400"
                        style={{ display: item.thumbnail_url ? "none" : "flex" }}
                      >
                        <svg width="40" height="40" viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                          <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da"/>
                          <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z" fill="#00ac47"/>
                          <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z" fill="#ea4335"/>
                          <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d"/>
                          <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc"/>
                          <path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00"/>
                        </svg>
                      </div>
                      {/* Drive / folder badge + remove button */}
                      <div className="absolute bottom-1 left-1 flex gap-1">
                        <Badge className="text-xs bg-amber-900/80 text-amber-200">
                          {item.is_folder ? "Folder" : "Drive"}
                        </Badge>
                      </div>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button
                            type="button"
                            onClick={(e) => e.stopPropagation()}
                            className="absolute top-1 right-1 p-1 bg-black/60 hover:bg-red-600 rounded text-white opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Remove from incident"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent
                          className="bg-slate-900 border-slate-700"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <AlertDialogHeader>
                            <AlertDialogTitle className="text-red-400">
                              Remove from incident?
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                              Remove <span className="text-white font-medium">{item.name}</span> from this incident's Media tab? The file stays in your Google Drive — only the link to this incident is removed.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="bg-slate-700 border-slate-600">Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              className="bg-red-600 hover:bg-red-700"
                              onClick={() => handleDetachDriveFile(item.file_id)}
                            >
                              Remove
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                      {/* Filename overlay */}
                      <div className="absolute bottom-0 left-0 right-0 px-1 pb-1 pt-4 bg-gradient-to-t from-black/80 to-transparent pointer-events-none">
                        <p className="text-[10px] text-white truncate">{item.name}</p>
                      </div>
                    </div>
                  );
                }

                const url = item;
                const fileId = getFileIdFromUrl(url);
                const isHidden = hiddenMediaIds.includes(fileId);
                return (
                <div
                  key={index}
                  className="relative group bg-slate-950 rounded-sm overflow-hidden border border-slate-800 cursor-pointer"
                  onClick={() => openLightbox(url, index)}
                >
                  {isImage(url) ? (
                    <>
                      <img
                        src={getMediaUrl(url)}
                        alt={`Media ${index + 1}`}
                        className="w-full h-32 object-cover hover:opacity-80 transition-opacity pointer-events-none"
                        draggable="false"
                      />
                      {/* Server-side watermarking handles non-admin users */}
                    </>
                  ) : isVideo(url) ? (
                    <div className="w-full h-32 bg-slate-900 hover:bg-slate-800 transition-colors relative overflow-hidden">
                      <video
                        src={getMediaUrl(url)}
                        className="w-full h-full object-cover pointer-events-none"
                        muted
                        playsInline
                        preload="metadata"
                        onLoadedMetadata={(e) => {
                          e.target.currentTime = 1;
                        }}
                      />
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none bg-black/20">
                        <div className="bg-black/50 rounded-full p-1.5">
                          <Play className="w-5 h-5 text-white" />
                        </div>
                      </div>
                      {/* Server-side watermarking handles non-admin users */}
                    </div>
                  ) : (
                    <div className="w-full h-32 flex items-center justify-center bg-slate-900 hover:bg-slate-800 transition-colors">
                      <ImageIcon className="w-8 h-8 text-slate-500" />
                    </div>
                  )}

                  {/* File type / hidden indicator */}
                  <div className="absolute bottom-1 left-1 flex gap-1">
                    {isVideo(url) && (
                      <Badge className="text-xs bg-purple-900/80 text-purple-200">Video</Badge>
                    )}
                    {isHidden && (
                      <Badge className="text-xs bg-red-900/80 text-red-200">
                        <EyeOff className="w-3 h-3" />
                      </Badge>
                    )}
                  </div>
                </div>
              )})}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No media files uploaded</p>
            </div>
          )}

          {/* Media hint */}
          {(incident.media_files || []).length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-800">
              <p className="text-xs text-slate-500">
                Tap media to view full size. Use the lightbox to download or hide from Media Page.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        {/* Live Call Tab */}
        <TabsContent value="livecall" className="mt-4">
          <div className="space-y-4">
      {/* Source Live Call History */}
      {sourceLiveCall && (
        <Card className="card-dark border-blue-900/50">
          <CardHeader className="pb-2">
            <CardTitle className="section-header flex items-center gap-2 text-blue-400">
              <Phone className="w-4 h-4" />
              Source Live Call History
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Original Call Info */}
            <div className="bg-slate-900 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-blue-300 uppercase tracking-wider mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Original Call Information
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 text-sm">
                <div>
                  <span className="text-slate-500 text-xs uppercase">Call Type</span>
                  <p className="text-white">{sourceLiveCall.call_type || "-"}</p>
                </div>
                {sourceLiveCall.secondary_call_type && (
                  <div>
                    <span className="text-slate-500 text-xs uppercase">Secondary Type</span>
                    <p className="text-white">{sourceLiveCall.secondary_call_type}</p>
                  </div>
                )}
                <div>
                  <span className="text-slate-500 text-xs uppercase">Location{sourceLiveCall.location_title ? ` (${sourceLiveCall.location_title})` : ""}</span>
                  <p className="text-white">{sourceLiveCall.location || "-"}</p>
                </div>
                {sourceLiveCall.cross_streets && (
                  <div>
                    <span className="text-slate-500 text-xs uppercase">Cross Streets</span>
                    <p className="text-white">{sourceLiveCall.cross_streets}</p>
                  </div>
                )}
                <div>
                  <span className="text-slate-500 text-xs uppercase">Priority</span>
                  <Badge className={sourceLiveCall.priority === "priority" ? "bg-red-600" : "bg-slate-600"}>
                    {sourceLiveCall.priority?.toUpperCase() || "NORMAL"}
                  </Badge>
                </div>
                <div>
                  <span className="text-slate-500 text-xs uppercase">Created By</span>
                  <p className="text-white">{sourceLiveCall.created_by_name || "-"}</p>
                </div>
                <div>
                  <span className="text-slate-500 text-xs uppercase">Created At</span>
                  <p className="text-white">
                    {sourceLiveCall.created_at ? new Date(sourceLiveCall.created_at).toLocaleString('en-US', {
                      timeZone: 'America/New_York',
                      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true
                    }) : "-"}
                  </p>
                </div>
                {sourceLiveCall.transmitted_10_85 && (
                  <div>
                    <Badge className="bg-red-600 text-white">10-85 TRANSMITTED</Badge>
                  </div>
                )}
                {sourceLiveCall.transmitted_10_13 && (
                  <div>
                    <Badge className="bg-orange-600 text-white">10-13 TRANSMITTED</Badge>
                  </div>
                )}
              </div>
              {sourceLiveCall.description && (
                <div className="mt-3 pt-3 border-t border-slate-800">
                  <span className="text-slate-500 text-xs uppercase">Description</span>
                  <p className="text-white mt-1 whitespace-pre-wrap">{sourceLiveCall.description}</p>
                </div>
              )}
              {sourceLiveCall.callback_numbers?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-800">
                  <span className="text-slate-500 text-xs uppercase">Callback Numbers</span>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {sourceLiveCall.callback_numbers.map((cb, idx) => (
                      <Badge key={idx} variant="outline" className="text-blue-400 border-blue-600">
                        {cb.number}{cb.caller_type ? ` (${cb.caller_type === 'cv' ? 'CV' : '3rd Party'})` : ''}
                        {cb.has_visual && <span className="ml-1 text-green-400">VISUAL</span>}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Additional Locations from Live Call */}
            {sourceLiveCall.additional_locations?.length > 0 && (
              <div className="bg-slate-900 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-blue-300 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Additional Locations ({sourceLiveCall.additional_locations.length})
                </h4>
                <div className="space-y-2">
                  {sourceLiveCall.additional_locations.map((loc, idx) => (
                    <div key={idx} className="p-2.5 bg-blue-950/30 border border-blue-800/30 rounded">
                      <Badge variant="outline" className="text-[10px] text-blue-400 border-blue-600 mb-1">
                        {loc.title || `Additional #${idx + 1}`}
                      </Badge>
                      <div className="flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                        <span className="text-sm text-slate-200 font-medium">{loc.location}</span>
                      </div>
                      {loc.cross_streets && <p className="text-xs text-emerald-400 ml-5 mt-0.5">✕ {loc.cross_streets}</p>}
                      {loc.location_notes && <p className="text-xs text-slate-400 ml-5 mt-0.5 italic">{loc.location_notes}</p>}
                      {loc.direction_of_flight && <p className="text-xs text-amber-400 ml-5 mt-0.5">→ {loc.direction_of_flight}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Perp Latest Locations from Live Call */}
            {(() => {
              const perpsWithLocation = (sourceLiveCall.perp_descriptions || [])
                .map((perp, idx) => ({ perp, perpNum: idx + 1 }))
                .filter(({ perp }) => perp.current_location || perp.direction_of_flight);
              if (perpsWithLocation.length === 0) return null;
              return (
                <div className="bg-slate-900 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-purple-300 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Perp Latest Locations ({perpsWithLocation.length})
                  </h4>
                  <div className="space-y-2">
                    {perpsWithLocation.map(({ perp, perpNum }) => (
                      <div key={`perpLoc_${perp.perp_id || perpNum}`} className="p-2.5 bg-purple-950/20 border border-purple-800/40 rounded">
                        <div className="flex items-center gap-1.5 flex-wrap mb-1">
                          <Badge variant="outline" className="text-[10px] text-purple-400 border-purple-600">
                            Perp #{perpNum}{perp.linked_profile_name ? ` — ${perp.linked_profile_name}` : ''}
                          </Badge>
                          {(perp.was_arrested || perp.is_arrested) && <Badge className="text-[10px] bg-emerald-700 text-white border-0">ARRESTED</Badge>}
                          {perp.is_holding && <Badge className="text-[10px] bg-amber-700 text-white border-0">HOLDING</Badge>}
                          {perp.is_detained && <Badge className="text-[10px] bg-orange-700 text-white border-0">DETAINED</Badge>}
                          {perp.is_visual && <Badge className="text-[10px] bg-cyan-600 text-white border-0">VISUAL</Badge>}
                          {perp.is_positive_id && <Badge className="text-[10px] bg-emerald-600 text-white border-0">POSITIVE ID</Badge>}
                          {perp.has_weapon && <Badge variant="destructive" className="text-[10px]">ARMED</Badge>}
                        </div>
                        {perp.current_location && (
                          <div className="flex items-center gap-1.5">
                            <MapPin className="w-3.5 h-3.5 text-purple-400 shrink-0" />
                            <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(perp.current_location + ", Brooklyn, NY")}`} target="_blank" rel="noopener noreferrer" className="text-sm text-slate-200 font-medium underline-offset-2 hover:underline break-words">{perp.current_location}</a>
                          </div>
                        )}
                        {perp.direction_of_flight && <p className="text-xs text-amber-400 ml-5 mt-0.5">→ {perp.direction_of_flight}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Job Narrative */}
            {sourceLiveCall.call_note && (
              <div className="bg-slate-900 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-cyan-300 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Job Narrative
                </h4>
                <div className="whitespace-pre-wrap text-sm text-slate-300 bg-slate-800/50 rounded p-3 max-h-64 overflow-y-auto">
                  {sourceLiveCall.call_note}
                </div>
              </div>
            )}

            {/* Unit Assignments */}
            {sourceLiveCall.assigned_units?.length > 0 && (
              <div className="bg-slate-900 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-emerald-300 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Assigned Units ({sourceLiveCall.assigned_units.length})
                </h4>
                <div className="flex flex-wrap gap-2">
                  {sourceLiveCall.assigned_units.map((unit) => {
                    const status = sourceLiveCall.unit_statuses?.[unit];
                    return (
                      <Badge key={unit} className={`
                        ${status?.status === '10-84' ? 'bg-emerald-600' :
                          status?.status === '10-97' ? 'bg-amber-600' :
                          status?.status === '10-98' ? 'bg-blue-600' :
                          'bg-slate-600'}
                      `}>
                        {unit}
                        {status?.status && <span className="ml-1 opacity-75">({status.status})</span>}
                      </Badge>
                    );
                  })}
                </div>

                {/* Unit Assignment History */}
                {sourceLiveCall.unit_assignment_history?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-800">
                    <p className="text-xs text-slate-500 uppercase mb-2">
                      Assignment History ({sourceLiveCall.unit_assignment_history.length})
                    </p>
                    <div className="max-h-32 overflow-y-auto space-y-1 text-xs">
                      {[...sourceLiveCall.unit_assignment_history].reverse().map((h, i) => {
                        const isPatrolCar = h.action?.includes('patrol car');
                        const is84 = h.action?.includes('84');
                        const is98 = h.action?.includes('98');
                        const actionColor = h.action === 'assigned' || h.action?.includes('assigned') ? 'text-green-400' : is98 ? 'text-green-400' : is84 ? 'text-blue-400' : h.action?.includes('status') ? 'text-blue-400' : h.action?.includes('unassigned') || h.action?.includes('removed') || h.action?.includes('cleared') ? 'text-red-400' : 'text-slate-400';
                        return (
                          <div key={i} className="text-slate-400 text-[11px]">
                            <span className="font-mono text-slate-500 text-[10px]">
                              {h.timestamp ? new Date(h.timestamp).toLocaleString('en-US', {
                                timeZone: 'America/New_York',
                                hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true
                              }) : ""}
                            </span>
                            {' '}
                            <span className={isPatrolCar ? 'text-purple-400' : 'text-slate-300'}>{isPatrolCar ? '🚓' : ''}{h.unit}</span>
                            {' '}
                            <span className={actionColor}>{isPatrolCar ? (h.action?.includes('unassigned') ? '−' : '+') : h.action}</span>
                            {h.by && <span className="text-amber-400/70 ml-1">({h.by})</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Updates History */}
            {sourceLiveCall.updates?.length > 0 && (
              <div className="bg-slate-900 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-amber-300 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Update History ({sourceLiveCall.updates.length})
                </h4>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {sourceLiveCall.updates.slice().reverse().map((update, idx) => {
                    const perpInfo = update.perp_id ? sourceLiveCall.perp_descriptions?.find(p => p.perp_id === update.perp_id) : null;
                    const perpIndex = perpInfo ? sourceLiveCall.perp_descriptions.indexOf(perpInfo) + 1 : null;
                    const isLocationUpdate = !!update.updated_location;
                    const isPerpUpdate = !!update.updated_perp_description || update.action === 'perp_added' || (update.general_notes?.startsWith('PERP') || update.general_notes?.includes('Perp #'));

                    return (
                      <div key={update.update_id || idx} className="p-2 bg-slate-800 rounded text-sm border-l-2 border-blue-500/50">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-slate-400 text-xs font-mono">
                              {update.timestamp ? new Date(update.timestamp).toLocaleString('en-US', {
                                timeZone: 'America/New_York',
                                month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true
                              }) : ""}
                            </span>
                            {isLocationUpdate && perpIndex && (
                              <Badge className="bg-amber-900/50 text-amber-200 text-[10px]">Location · Perp #{perpIndex}</Badge>
                            )}
                            {isLocationUpdate && !perpIndex && update.location_label && (
                              <Badge className="bg-amber-900/50 text-amber-200 text-[10px]">Location · {update.location_label}</Badge>
                            )}
                            {isLocationUpdate && !perpIndex && !update.location_label && (
                              <Badge className="bg-amber-900/50 text-amber-200 text-[10px]">Location · Original</Badge>
                            )}
                            {isPerpUpdate && perpIndex && (
                              <Badge className="bg-purple-900/50 text-purple-200 text-[10px]">Perp #{perpIndex}</Badge>
                            )}
                            {update.transmitted_10_85 && <Badge variant="destructive" className="text-[10px]">10-85</Badge>}
                            {update.transmitted_10_13 && <Badge variant="destructive" className="text-[10px]">10-13</Badge>}
                            {update.no_further && <Badge className="bg-amber-900/50 text-amber-200 text-[10px]">NO FURTHER</Badge>}
                          </div>
                          <span className="text-xs text-slate-500">{update.updated_by || update.user_name || ""}</span>
                        </div>
                        {update.updated_location && (
                          <p className="text-sm mb-1 text-amber-300">
                            <span className="text-amber-500">📍 {perpIndex ? `Perp #${perpIndex} Location:` : 'Location:'}</span> {update.updated_location}
                          </p>
                        )}
                        {update.updated_direction && (
                          <p className="text-sm mb-1 text-amber-400">
                            <span className="text-amber-500">→ {perpIndex ? `Perp #${perpIndex} Direction:` : 'Direction:'}</span> {update.updated_direction}
                          </p>
                        )}
                        {update.updated_perp_description && (
                          <p className="text-sm mb-1 text-purple-400">{update.updated_perp_description}</p>
                        )}
                        {update.general_notes && (
                          <p className={`text-sm ${isPerpUpdate ? 'text-purple-300' : 'text-slate-300'}`}>{update.general_notes}</p>
                        )}
                        {update.update_text && (
                          <p className="text-sm text-blue-300"><span className="text-blue-500 text-xs">Update:</span> {update.update_text}</p>
                        )}
                        {/* Fallback for old format */}
                        {update.field && !update.general_notes && !update.updated_location && (
                          <p className="text-white">
                            <span className="text-slate-400">{update.field}:</span>{" "}
                            {update.new_value !== undefined ? String(update.new_value) : update.value || ""}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Read Receipts from Live Call */}
            {sourceLiveCall.read_receipts?.length > 0 && (
              <div className="bg-slate-900 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-purple-300 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  Live Call Read Receipts ({sourceLiveCall.read_receipts.length})
                </h4>
                <div className="flex flex-wrap gap-2">
                  {sourceLiveCall.read_receipts.map((receipt, idx) => (
                    <div key={idx} className="bg-slate-800 rounded px-3 py-1.5 text-sm flex items-center gap-2">
                      <Eye className="w-3 h-3 text-purple-400" />
                      <span className="text-slate-300">{receipt.user_name}</span>
                      {receipt.source && (
                        <span className={`text-[10px] font-bold ${receipt.source === "response" ? "text-blue-400" : "text-amber-400"}`} title={receipt.source === "response" ? "Response page" : "Dispatch page"}>
                          {receipt.source === "response" ? "R" : receipt.source === "live_calls" ? "L" : "?"}
                        </span>
                      )}
                      <span className="text-slate-500 text-xs">
                        {receipt.read_at ? new Date(receipt.read_at).toLocaleString('en-US', {
                          timeZone: 'America/New_York',
                          month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true
                        }) : ""}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Delivered Receipts from Live Call */}
            {sourceLiveCall.delivered_receipts?.length > 0 && (
              <div className="bg-slate-900 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  Delivered Receipts ({sourceLiveCall.delivered_receipts.length})
                </h4>
                <div className="flex flex-wrap gap-2">
                  {sourceLiveCall.delivered_receipts.map((receipt, idx) => (
                    <div key={idx} className="bg-slate-800 rounded px-3 py-1.5 text-sm flex items-center gap-2">
                      <Check className="w-3 h-3 text-slate-500" />
                      <span className="text-slate-400">{receipt.user_name}</span>
                      <span className="text-slate-600 text-xs">
                        {receipt.delivered_at ? new Date(receipt.delivered_at).toLocaleString('en-US', {
                          timeZone: 'America/New_York',
                          month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true
                        }) : ""}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Linked Radio Recordings - Only for users with recording access */}
      {(user?.recording_admin_access || user?.role === 'admin') && sourceLiveCall && (
        <Card className="bg-slate-800 border-slate-800 mb-4 mt-4">
          <CardHeader
            className="py-2 pb-1 cursor-pointer"
            onClick={() => setShowLinkedRecordings(!showLinkedRecordings)}
          >
            <CardTitle className="text-xs uppercase text-slate-400 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Headphones className="w-4 h-4" />
                <span>Linked Radio Recordings ({linkedRecordings.length})</span>
              </div>
              <div className="flex items-center gap-1">
                {linkedRecordings.some(r => !r.transcription) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`h-5 px-1 ${transcribingLinkedFile === "all" ? "text-red-400" : ""}`}
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (transcribingLinkedFile === "all") {
                        cancelTranscribeAllRef.current = true;
                        return;
                      }
                      cancelTranscribeAllRef.current = false;
                      setTranscribingLinkedFile("all");
                      let count = 0;
                      for (const rec of linkedRecordings) {
                        if (cancelTranscribeAllRef.current) break;
                        if (rec.transcription) continue;
                        try {
                          const fn = decodeURIComponent(rec.filename);
                          const res = await axios.post(`${API}/radio/recordings/transcribe`, null, { params: { filename: fn }, withCredentials: true });
                          setLinkedRecordings(prev => prev.map(r => r.filename === rec.filename ? { ...r, transcription: res.data.transcription, transcription_status: "completed" } : r));
                          count++;
                        } catch {}
                      }
                      const stopped = cancelTranscribeAllRef.current;
                      cancelTranscribeAllRef.current = false;
                      setTranscribingLinkedFile(null);
                      if (count > 0) toast.success(`Transcribed ${count} recording${count !== 1 ? "s" : ""}${stopped ? " (stopped)" : ""}`);
                    }}
                    title={transcribingLinkedFile === "all" ? "Stop Transcribing" : "Transcribe All"}
                  >
                    {transcribingLinkedFile === "all" ? <X className="w-3 h-3" /> : <MessageSquare className="w-3.5 h-3.5" />}
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 px-1"
                  onClick={(e) => { e.stopPropagation(); fetchLinkedRecordings(sourceLiveCall); }}
                  disabled={loadingRecordings}
                >
                  <RefreshCw className={`w-3 h-3 ${loadingRecordings ? 'animate-spin' : ''}`} />
                </Button>
                {showLinkedRecordings ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
            </CardTitle>
          </CardHeader>
          {showLinkedRecordings && (
            <CardContent className="p-3 pt-0">
              {loadingRecordings ? (
                <div className="flex items-center gap-2 text-slate-400 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading...
                </div>
              ) : linkedRecordings.length === 0 ? (
                <p className="text-slate-500 text-sm">No recordings in time range</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {linkedRecordings.map((rec) => (
                    <div key={rec.filename} className="bg-slate-900 rounded px-2 py-1.5">
                      <div className="flex items-center gap-2 text-sm">
                        <Badge variant="outline" className="text-xs">{rec.user_id}</Badge>
                        <span className="text-slate-300 flex-1">{rec.time}</span>
                        {!rec.transcription && (
                          <button
                            onClick={() => transcribeLinkedRecording(rec.filename)}
                            disabled={transcribingLinkedFile === rec.filename}
                            className="p-1 rounded text-slate-500 hover:text-slate-300 hover:bg-slate-800"
                            title="Transcribe"
                          >
                            {transcribingLinkedFile === rec.filename ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageSquare className="w-3.5 h-3.5" />}
                          </button>
                        )}
                        <button
                          onClick={() => playLinkedRecording(rec.filename)}
                          className={`p-1 rounded ${playingRecording === rec.filename ? "text-green-400 bg-green-900/30" : "text-blue-400 hover:text-blue-300 hover:bg-slate-800"}`}
                        >
                          {playingRecording === rec.filename ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                        </button>
                      </div>
                      {rec.transcription && (
                        <p className="text-[11px] text-slate-400 italic mt-1 pl-1">
                          <MessageSquare className="w-3 h-3 inline mr-1 text-slate-500" />{rec.transcription}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          )}
        </Card>
      )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Read Receipts */}
      <Card className="card-dark">
        <CardHeader className="pb-2">
          <CardTitle className="section-header flex items-center gap-2">
            <Eye className="w-4 h-4" />
            Read Receipts ({incident.read_receipts?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {incident.read_receipts?.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {incident.read_receipts.map((receipt, idx) => (
                <div key={idx} className="bg-slate-800 rounded px-3 py-1.5 text-sm flex items-center gap-2">
                  <Eye className="w-3 h-3 text-green-500" />
                  <span className="text-slate-300">{receipt.user_name}</span>
                  <span className="text-slate-500 text-xs">
                    {new Date(receipt.read_at).toLocaleString('en-US', {
                      timeZone: 'America/New_York',
                      month: 'numeric',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true
                    })}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500 italic">No read receipts yet</p>
          )}
        </CardContent>
      </Card>

      {/* Incident Audit Log */}
      <Card className="card-dark mt-4 border-purple-900/50">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle
              className="section-header flex items-center gap-2 text-purple-400 cursor-pointer"
              onClick={() => {
                if (!auditExpanded) {
                  fetchAuditLog();
                }
                setAuditExpanded(!auditExpanded);
              }}
            >
              <History className="w-4 h-4" />
              Incident Audit Log
              {auditExpanded ? (
                <ChevronUp className="w-4 h-4 ml-2" />
              ) : (
                <ChevronDown className="w-4 h-4 ml-2" />
              )}
            </CardTitle>
            {auditExpanded && auditLog.length > 0 && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportAuditCSV}
                  className="text-xs h-7"
                >
                  <FileSpreadsheet className="w-3 h-3 mr-1" />
                  CSV
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportAuditPDF}
                  className="text-xs h-7"
                >
                  <FileText className="w-3 h-3 mr-1" />
                  PDF
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        {auditExpanded && (
          <CardContent>
            {loadingAudit ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
              </div>
            ) : auditLog.length > 0 ? (
              <ScrollArea className="h-[400px] overflow-y-auto">
                <div className="space-y-2 pr-4">
                  {auditLog.map((entry, idx) => (
                    <div
                      key={entry.audit_id || idx}
                      className="bg-slate-900 rounded-lg p-3 border border-slate-800"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge
                              className={
                                entry.action === "created"
                                  ? "bg-green-900/50 text-green-300"
                                  : entry.action === "drive_attached"
                                  ? "bg-amber-900/50 text-amber-300"
                                  : entry.action === "drive_detached"
                                  ? "bg-red-900/50 text-red-300"
                                  : "bg-blue-900/50 text-blue-300"
                              }
                            >
                              {(() => {
                                const a = entry.action || "";
                                if (a === "drive_attached") return "Drive Attached";
                                if (a === "drive_detached") return "Drive Removed";
                                // Generic snake_case → Title Case
                                return a.split("_").map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(" ");
                              })()}
                            </Badge>
                            <span className="text-sm text-white font-medium">
                              {entry.user_name}
                            </span>
                          </div>
                          {/* Show detailed changes */}
                          {entry.changes && Object.keys(entry.changes).length > 0 ? (
                            <div className="mt-2 space-y-1">
                              {Object.entries(entry.changes).map(([field, change]) => {
                                const fieldLabel = field.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
                                const oldVal = change.old;
                                const newVal = change.new;

                                // Format display values
                                const formatVal = (val) => {
                                  if (val === null || val === undefined) return "(empty)";
                                  if (typeof val === "object" && val.number !== undefined) {
                                    let str = val.number;
                                    if (val.label) str += ` (${val.label})`;
                                    if (val.caller_type) str += ` [${val.caller_type === 'cv' ? 'CV' : '3rd Party'}]`;
                                    return str;
                                  }
                                  if (Array.isArray(val)) {
                                    if (val.length === 0) return "(none)";
                                    const formatted = val.slice(0, 3).map(item => formatVal(item));
                                    return formatted.join(", ") + (val.length > 3 ? ` (+${val.length - 3} more)` : "");
                                  }
                                  if (typeof val === "object") {
                                    const pairs = Object.entries(val)
                                      .filter(([k, v]) => v !== null && v !== undefined && v !== "")
                                      .map(([k, v]) => `${k}: ${v}`);
                                    return pairs.length > 0 ? pairs.join(", ") : "(empty)";
                                  }
                                  if (typeof val === "boolean") return val ? "Yes" : "No";
                                  return String(val);
                                };

                                // Word-level diff for long strings — highlights only what changed
                                const renderWordDiff = (oldStr, newStr) => {
                                  const oldWords = oldStr.split(/(\s+)/);
                                  const newWords = newStr.split(/(\s+)/);
                                  // Simple LCS-based diff
                                  const max = oldWords.length + newWords.length;
                                  if (max > 500) {
                                    // Too long, fall back to simple display
                                    return (
                                      <span className="text-slate-400">
                                        {" "}<span className="text-red-400/70 line-through">{oldStr.length > 200 ? oldStr.slice(0, 200) + "..." : oldStr}</span>
                                        {" → "}<span className="text-green-400">{newStr.length > 200 ? newStr.slice(0, 200) + "..." : newStr}</span>
                                      </span>
                                    );
                                  }
                                  // Build diff using longest common subsequence
                                  const dp = Array.from({ length: oldWords.length + 1 }, () => new Array(newWords.length + 1).fill(0));
                                  for (let i = 1; i <= oldWords.length; i++)
                                    for (let j = 1; j <= newWords.length; j++)
                                      dp[i][j] = oldWords[i-1] === newWords[j-1] ? dp[i-1][j-1] + 1 : Math.max(dp[i-1][j], dp[i][j-1]);
                                  // Backtrack to get diff
                                  const parts = [];
                                  let i = oldWords.length, j = newWords.length;
                                  const stack = [];
                                  while (i > 0 || j > 0) {
                                    if (i > 0 && j > 0 && oldWords[i-1] === newWords[j-1]) {
                                      stack.push({ type: "same", text: oldWords[i-1] });
                                      i--; j--;
                                    } else if (j > 0 && (i === 0 || dp[i][j-1] >= dp[i-1][j])) {
                                      stack.push({ type: "add", text: newWords[j-1] });
                                      j--;
                                    } else {
                                      stack.push({ type: "del", text: oldWords[i-1] });
                                      i--;
                                    }
                                  }
                                  stack.reverse().forEach(s => parts.push(s));
                                  return (
                                    <span className="text-slate-300">
                                      {" "}{parts.map((p, k) =>
                                        p.type === "same" ? <span key={k}>{p.text}</span>
                                        : p.type === "del" ? <span key={k} className="bg-red-900/50 text-red-300 line-through px-0.5 rounded-sm">{p.text}</span>
                                        : <span key={k} className="bg-green-900/50 text-green-300 px-0.5 rounded-sm">{p.text}</span>
                                      )}
                                    </span>
                                  );
                                };

                                // Use word diff for long text changes — works on formatted strings regardless of original type
                                const oldStr = formatVal(oldVal);
                                const newStr = formatVal(newVal);
                                const isLongText = oldStr.length > 40 || newStr.length > 40;

                                return (
                                  <div key={field} className="text-xs bg-slate-800/50 rounded px-2 py-1">
                                    <span className="text-purple-400 font-medium">{fieldLabel}:</span>
                                    {oldVal !== null && oldVal !== undefined && oldVal !== "" ? (
                                      isLongText ? renderWordDiff(oldStr, newStr) : (
                                        <span className="text-slate-400">
                                          {" "}<span className="text-red-400/70 line-through">{oldStr}</span>
                                          {" → "}
                                          <span className="text-green-400">{newStr}</span>
                                        </span>
                                      )
                                    ) : (
                                      <span className="text-green-400"> {newStr}</span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ) : entry.details ? (
                            <p className="text-xs text-slate-400 mt-1">
                              {entry.details}
                            </p>
                          ) : null}
                        </div>
                        <span className="text-xs text-slate-500 whitespace-nowrap">
                          {entry.timestamp
                            ? new Date(entry.timestamp).toLocaleString("en-US", {
                                timeZone: "America/New_York",
                                month: "numeric",
                                day: "numeric",
                                year: "2-digit",
                                hour: "numeric",
                                minute: "2-digit",
                                hour12: true,
                              })
                            : ""}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            ) : (
              <p className="text-sm text-slate-500 italic py-4 text-center">
                No audit entries found for this incident
              </p>
            )}
          </CardContent>
        )}
      </Card>

      {/* Link Profile Dialog - Top Level */}
      <Dialog open={showLinkProfileDialog} onOpenChange={(open) => {
        setShowLinkProfileDialog(open);
        if (!open) {
          setLinkProfilePerp(null);
          setProfileSearchQuery("");
        }
      }}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle>Link to Perpetrator Profile</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {linkProfilePerp && (
              <div className="p-2 bg-slate-800 rounded text-xs text-slate-400">
                Linking perp: {[linkProfilePerp.sex, linkProfilePerp.race, linkProfilePerp.age].filter(Boolean).join(' ') || linkProfilePerp.name || 'Unknown description'}
              </div>
            )}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                placeholder="Search profiles..."
                value={profileSearchQuery}
                onChange={(e) => setProfileSearchQuery(e.target.value)}
                className="input-dark pl-10"
                autoFocus
              />
            </div>
            <div className="max-h-[400px] overflow-y-auto pr-2">
              <div className="space-y-2">
                {profiles.length === 0 && (
                  <p className="text-center text-slate-500 py-4">No profiles available. Create profiles in Profiles Admin first.</p>
                )}
                {profiles
                  .filter(p => !profileSearchQuery || p.name?.toLowerCase().includes(profileSearchQuery.toLowerCase()))
                  .map(profile => (
                    <div
                      key={profile.profile_id}
                      className="p-3 bg-slate-800 rounded cursor-pointer hover:bg-slate-700 hover:ring-1 hover:ring-purple-500 flex items-start gap-3"
                      onClick={() => {
                        console.log("Profile clicked:", profile.profile_id, "Perp:", linkProfilePerp?.perp_id);
                        if (linkProfilePerp?.perp_id) {
                          linkProfileToPerp(linkProfilePerp.perp_id, profile);
                        } else {
                          toast.error("No perp selected - please try again");
                          setShowLinkProfileDialog(false);
                        }
                      }}
                    >
                      {profile.mugshot_url ? (
                        <img
                          src={getAuthenticatedMediaUrl(`${API.replace('/api', '')}${profile.mugshot_url}/thumb?size=96`)}
                          alt=""
                          width={48}
                          height={48}
                          loading="lazy"
                          decoding="async"
                          onClick={(e) => { e.stopPropagation(); setLightboxMedia(profile.mugshot_url); }}
                          onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling?.classList.remove('hidden'); }}
                          className="w-12 h-12 object-cover rounded shrink-0 cursor-zoom-in hover:ring-2 hover:ring-purple-400"
                        />
                      ) : null}
                      <div className={`w-12 h-12 rounded bg-slate-700 flex items-center justify-center shrink-0 ${profile.mugshot_url ? 'hidden' : ''}`}>
                        <UserCircle className="w-5 h-5 text-purple-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-purple-300 truncate">{profile.name}</p>
                        <div className="flex gap-1 mt-1 flex-wrap">
                          {/* Status badge based on profile.status */}
                          {profile.status === "arrested" && <Badge className="bg-emerald-600 text-white text-xs">ARRESTED</Badge>}
                          {profile.status === "ready_for_arrest" && <Badge className="bg-amber-600 text-white text-xs">READY</Badge>}
                          {profile.status === "linked" && <Badge className="bg-blue-600 text-white text-xs">LINKED</Badge>}
                          {profile.status === "warrant" && <Badge variant="destructive" className="text-xs">WARRANT</Badge>}
                          {profile.has_arrest_warrant && profile.status !== "warrant" && <Badge variant="destructive" className="text-xs">WARRANT</Badge>}
                        </div>
                        {profile.known_address && <p className="text-xs text-slate-400 mt-1 truncate">{profile.known_address}</p>}
                        {profile.notes && <p className="text-xs text-slate-500 truncate mt-1">{profile.notes}</p>}
                      </div>
                    </div>
                  ))}
                {profiles.length > 0 && profiles.filter(p => !profileSearchQuery || p.name?.toLowerCase().includes(profileSearchQuery.toLowerCase())).length === 0 && (
                  <p className="text-center text-slate-500 py-4">No profiles match your search</p>
                )}
              </div>
            </div>
            <Button variant="outline" onClick={() => setShowLinkProfileDialog(false)} className="w-full">Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Media Lightbox — wrap in DismissableLayerBranch so clicks inside
          don't dismiss any enclosing Radix Dialog. */}
      {lightboxMedia && (
        <DismissableLayerBranch
          className="fixed inset-0 bg-black/90 z-[9999] flex items-center justify-center p-4"
          onClick={() => setLightboxMedia(null)}
        >
          {/* Close button */}
          <button
            onClick={() => setLightboxMedia(null)}
            className="absolute top-4 right-4 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white z-[10000]"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Media content */}
          <div
            className="relative max-w-4xl max-h-[80vh] w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {isImage(lightboxMedia) ? (
              <div className="relative">
                <img
                  src={getMediaUrl(lightboxMedia)}
                  alt="Media"
                  className="w-full max-h-[70vh] object-contain rounded-lg"
                />
                {/* Server-side watermarking handles non-admin users */}
              </div>
            ) : isVideo(lightboxMedia) ? (
              <div className="relative">
                <video
                  src={getMediaUrl(lightboxMedia)}
                  controls
                  controlsList={user?.role !== "admin" ? "nodownload" : undefined}
                  onContextMenu={(e) => user?.role !== "admin" && e.preventDefault()}
                  playsInline
                  webkit-playsinline="true"
                  preload="auto"
                  className="w-full max-h-[70vh] rounded-lg"
                />
                {/* Server-side watermarking handles non-admin users */}
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 bg-slate-800 rounded-lg">
                <ImageIcon className="w-16 h-16 text-slate-500" />
              </div>
            )}

            {/* Controls */}
            <div className="mt-4 p-4 bg-slate-900 rounded-lg space-y-3">
              {/* Hide from Media checkbox */}
              <div className="flex items-center gap-3">
                <Checkbox
                  id="hide-media-checkbox"
                  checked={hiddenMediaIds.includes(getFileIdFromUrl(lightboxMedia))}
                  onCheckedChange={(checked) => {
                    const fileId = getFileIdFromUrl(lightboxMedia);
                    if (fileId) {
                      toggleHideMedia(fileId, checked);
                    }
                  }}
                />
                <Label
                  htmlFor="hide-media-checkbox"
                  className={hiddenMediaIds.includes(getFileIdFromUrl(lightboxMedia)) ? "text-red-400" : "text-slate-300"}
                >
                  {hiddenMediaIds.includes(getFileIdFromUrl(lightboxMedia))
                    ? "Hidden from Media Page"
                    : "Hide from Media Page"}
                </Label>
              </div>

              {/* Action buttons */}
              <div className="flex gap-2">
                {/* Download - hide for videos if non-admin */}
                {!(isVideo(lightboxMedia) && user?.role !== "admin") && (
                  <Button
                    onClick={() => handleMediaDownload(lightboxMedia)}
                    disabled={isDownloading}
                    className="flex-1"
                  >
                    {isDownloading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    {isDownloading ? 'Downloading...' : 'Download'}
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => setLightboxMedia(null)}
                  className="flex-1"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </DismissableLayerBranch>
      )}

      {/* Phone Lookup Popup */}
      <PhoneLookupPopup
        phone={phoneLookupNumber}
        open={!!phoneLookupNumber}
        onOpenChange={(open) => { if (!open) setPhoneLookupNumber(null); }}
      />
    </div>
  );
}
